import "./styles.css";
import { isShippingRequestStatus } from "../shared/shipping-request-status";
import { cleaningPickupFeedback, type CleaningPickupStatus } from "./cleaning-pickup-feedback";
import { openCleaningPickupAlreadySentDialog } from "./cleaning-pickup-notice";
import "./inventory-transfer.css";
import {
  filterRepairOrderListSkuItems,
  orderListItemSize,
  orderListItemsForWorkspace,
  shouldDisplayDailyOrderListOrder,
} from "./order-list";
import {
  groupOrderLineItems,
  orderCancellationLabel,
  orderCancellationType,
  summarizeOrderCancellation,
  type GroupedOrderLineItems,
} from "./order-line-items";
import { matchesOrderSearch } from "./order-search";
import { blocksOrderDialog } from "./order-row-interaction";
import { repairAtelierResetButton, resetRepairAtelierSelection } from "./repair-atelier-selection";
import { canRecordRepairInstockDate, repairInstockDateCell } from "./repair-instock";
import { isUnpaidRepairSelection, pruneUnpaidRepairSelections, repairCaseBadge } from "./repair-order-selection";
import { orderDialogImportedFields } from "./order-dialog-imported-fields";
import {
  inventoryTransferSharedMaxQuantity,
  shouldShowInventoryTransferRequestStatus,
  type InventoryTransferView,
} from "./inventory-transfer";
import {
  inventoryTransferRequestDialogHtml,
  inventoryTransferWorkspaceHtml,
  inventoryRelocationConfirmationHtml,
  inventoryRelocationRegroupConfirmationHtml,
  inventoryRelocationReturnDialogHtml,
  inventoryRelocationSelection,
  selectedInventoryRelocationRecords,
  type InventoryTransferData,
} from "./inventory-transfer-view";
import { inventoryReplenishmentNavigationBadge } from "./inventory-replenishment-navigation";
import { bindCleaningSelection, cleaningTabHtml, cleaningWorkspaceHtml, type CleaningManagementData } from "./cleaning-workspace";
import { isCleaningView, type CleaningView } from "../shared/cleaning-views";
import { openCleaningMemoDialog } from "./cleaning-memo-dialog";
import { openRelocationDetail, confirmRelocationAdminReturn } from "./inventory-relocation-detail-dialog";
import type { InventorySourceLocation } from "./inventory-source-location";
import type { InventoryRelocationLocationResult, InventoryRelocationRegroupPlan } from "./inventory-relocation-group";
import { inventoryRegistrationDialogHtml, inventoryRegistrationLoadingDialogHtml, inventoryRegistrationSyncSummary, inventoryRegistrationAllSucceeded, inventoryRegistrationResultMessage, inventoryRegistrationSuccessDialogHtml, type InventoryRegistrationPlan, type InventoryRegistrationSyncResult, type InventoryRegistrationResult } from "./inventory-relocation-registration";
import { inventoryReplenishmentPrintPages } from "./inventory-replenishment-print";
import { inventoryRelocationListPrintPages } from "./inventory-relocation-list-print";
import { bindInventoryReplenishmentStickyHeader } from "./inventory-replenishment-sticky-header";
import {
  customerManagementViewCounts,
  customerManagementViewRecords,
  type CustomerManagementView,
} from "./customer-management";
import {
  defaultOrderSortModeForFilter,
  defaultOrderSortSettingsForList,
  sortDashboardOrders,
  type OrderSortMode,
} from "./order-sort";
import { paymentStatusLabel } from "./payment-status";
import {
  htmlPrintPageRule,
  htmlPrintPaperDefinition,
  type HtmlPrintPaper,
} from "./html-print-preview";
import {
  orderFilterView,
  orderWorkspaceEntryPlan,
  shouldShowShippingRequestAction,
  shouldShowSizeOrderReprintAction,
} from "./workspace-navigation";
import {
  inventoryConsumptionDisplay,
  inventoryRemainingDisplay,
  inventoryStockColumns,
  requiresInventoryReturn,
  type InventoryConsumptionSnapshot,
  type InventoryLocationSnapshot,
} from "./inventory-snapshot";
import {
  DAILY_ORDER_PRINT_PDF_HEIGHT_MM,
  DAILY_ORDER_PRINT_PDF_WIDTH_MM,
  DAILY_ORDER_PRINT_RENDER_HEIGHT_PX,
  DAILY_ORDER_PRINT_RENDER_WIDTH_PX,
  DAILY_ORDER_PRINT_ROWS_PER_PAGE,
  DAILY_ORDER_PRINT_SALES_SOURCES,
  dailyOrderPrintConditionLabel,
  dailyOrderPrintHistoryOrderIds,
  dailyOrderPrintInventoryMismatchBadgeHtml,
  filterDailyOrderPrintOrdersBySalesSource,
  formatDailyOrderPrintPdfFilename,
  formatRepairPrintPdfFilename,
  isDailyOrderPrintEligible,
  resolveDailyOrderPrintMode,
  formatReceivePdfFilename,
  formatReceivePdfShippedSummary,
  groupReceivePdfProperties,
  hasReceivePdfNoteContent,
  RECEIVE_PDF_ADDITIONAL_OPTIONS,
  RECEIVE_PDF_ATELIER_TASKS,
  RECEIVE_PDF_HEIGHT_MM,
  RECEIVE_PDF_NOTE_CHARS,
  RECEIVE_PDF_RENDER_HEIGHT_PX,
  RECEIVE_PDF_RENDER_WIDTH_PX,
  RECEIVE_PDF_WIDTH_MM,
  partitionReceivePdfProperties,
  partitionReceivePdfItemSegments,
  receivePdfInspectionFooterFlags,
  receivePdfLayoutOptions,
  allowsDetachedSizeOrderPdfInspectionFooter,
  splitReceivePdfText,
  type ReceivePdfLayout,
  type DailyOrderPrintMode,
  type DailyOrderPrintSalesSource,
} from "./receive-pdf";

interface LineItem {
  id: string;
  sku: string;
  title: string;
  variantTitle: string | null;
  quantity: number;
  currentQuantity: number;
  unfulfilledQuantity: number;
  unitPrice: string;
  currencyCode: string;
  properties: Array<{ name: string; value: string }>;
  inventoryLocations?: InventoryLocationSnapshot[];
  inventorySnapshotCapturedAt?: string;
  consumedInventoryLocations?: InventoryConsumptionSnapshot[];
  inventoryConsumptionCapturedAt?: string;
  inventoryConsumptionSource?: string;
}

type CsOrderStatus = "" | "受付済み" | "依頼済み" | "輸入中" | "手続き済" | "入荷済み" | "入荷完了" | "印刷完了" | "出荷依頼" | "出荷済み" | "非表示済";
type RecordCorrectionStatus = Exclude<CsOrderStatus, "">;
interface RecordCorrectionResetFields {
  itemArrivalDate: boolean;
  shippingRequestDate: boolean;
  repairAtelier: boolean;
  repairRequestDate: boolean;
  status: boolean;
}
type ListFilter = "all" | "received" | "shipped" | "hidden" | "unnecessary" | "ended";
type DashboardView = "active" | "history";
type DashboardWorkspace = "all" | "size" | "repair";
type EtcWorkspace = "inventory-transfer" | "return-exchange" | "inventory-search" | "cleaning";
type BulkConfirmAction = "receive" | "reprint" | "ship" | "hide" | "reset" | "bulk-delete" | "repair-request" | "repair-print" | "confirm-unnecessary";

interface DashboardOrder {
  sizeUnitId?: string;
  recordType: "order" | "ended" | "after-alteration";
  endedAt: string | null;
  id: string;
  legacyId: string;
  name: string;
  orderNote: string;
  customerRequest: string;
  includedItems: string;
  sourceName: string;
  createdAt: string;
  shopifyUpdatedAt: string;
  shopifyFulfillmentStatus: string;
  financialStatus: string;
  currencyCode: string;
  totalAmount: string;
  matchedLineCount: number;
  matchedQuantity: number;
  importOrigin: string;
  customerName: string;
  shippingProvince: string;
  csOrderStatus: CsOrderStatus | string;
  itemArrivalDate: string;
  shippingRequest: string;
  shippingRequestDate: string;
  isRepairOrder: boolean;
  repairFinish: string;
  caseNumberRepairA: string;
  repairAtelier: string;
  repairRequestDate: string;
  repairInstockDate: string;
  retailLocationName: string;
  hasInventoryTransferRequest?: boolean;
  salesSource: string;
  deliveryMethod: string;
  arrivalContact: string;
  requestedDeliveryDate: string;
  wearingDate: string;
  isExpress: boolean;
  memo: string;
  memoSyncStatus: string;
  memoSyncError: string | null;
  summarySyncStatus: string;
  summarySyncError: string | null;
  importedAt: string;
  updatedAt: string;
  manualOverride: boolean;
  manualUpdatedAt: string | null;
  items: LineItem[];
}

interface BootstrapData {
  shop: string;
  view: DashboardView;
  workspace: DashboardWorkspace;
  repairAtelierOptions: string[];
  limit: number;
  rule: { sourceName: string; skuSuffixes: string[] };
  stats: {
    orderCount: number;
    matchedQuantity: number;
    memoCount: number;
    syncErrorCount: number;
    receivedCount: number;
    shippedCount: number;
    hiddenCount: number;
    unnecessaryCount: number;
    privacyRequestCount: number;
    inventoryReplenishmentCount: number;
  };
  orders: DashboardOrder[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

type CustomerManagementStatusField = "warehouseArrival" | "problemOccurred" | "productCondition";

interface CustomerManagementDraft {
  warehouseArrival: boolean;
  problemOccurred: boolean;
  productCondition: boolean;
}

interface CustomerManagementRecord {
  id: string;
  classificationCode: string;
  sourceOrderGid: string;
  orderNumber: string;
  customerName: string;
  sku: string;
  supportType: string;
  specialNotes: string;
  registeredByName: string;
  productCondition: boolean;
  warehouseArrival: boolean;
  warehouseArrivalDate: string;
  problemOccurred: boolean;
  processingStatus: string;
  customerCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  editCount: number;
  lastEditedByName: string;
  lastEditedAt: string;
}

interface CustomerManagementData {
  classificationCode: string;
  records: CustomerManagementRecord[];
}

interface ForcedShopifyOrder {
  orderGid: string;
  orderLegacyId: string;
  orderName: string;
  orderNote: string;
  sourceName: string;
  shopifyFulfillmentStatus: string;
  createdAt: string;
  currencyCode: string;
  totalAmount: string;
  customerName: string;
  shippingProvince: string;
  csOrderStatus: string;
  retailLocationName: string;
  deliveryMethod: string;
  arrivalContact: string;
  requestedDeliveryDate: string;
  wearingDate: string;
  isExpress: boolean;
  matchedItems: Array<{
    lineItemGid: string;
    sku: string;
    title: string;
    variantTitle: string | null;
    quantity: number;
    currentQuantity: number;
    unfulfilledQuantity: number;
    unitPrice: string;
    currencyCode: string;
    properties: Array<{ name: string; value: string }>;
  }>;
}

interface MaintenanceCandidate {
  shopifyOrder: ForcedShopifyOrder;
  existing: DashboardOrder | null;
  cancelled: boolean;
  manuallyDeleted: boolean;
  lineItemsTruncated: boolean;
}

interface ComplianceRequestSummary {
  id: string;
  topic: string;
  requestReference: string;
  status: string;
  requestedOrderCount: number;
  receivedAt: string;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

interface InventoryPopoverData {
  productId: string;
  productTitle: string;
  productImage: { url: string; altText: string | null } | null;
  selectedVariantId: string;
  requestedSku: string;
  duplicateSkuMatches: number;
  variantsTruncated: boolean;
  variants: Array<{
    id: string;
    title: string;
    sku: string;
    image: { url: string; altText: string | null } | null;
    tracked: boolean;
    totalAvailable: number | null;
    locationsTruncated: boolean;
    locations: Array<{
      id: string;
      name: string;
      available: number;
    }>;
  }>;
}

const state: {
  data: BootstrapData | null;
  view: DashboardView;
  workspace: DashboardWorkspace;
  etcWorkspace: EtcWorkspace | null;
  cleaningData: CleaningManagementData | null;
  cleaningView: CleaningView;
  cleaningLoading: boolean;
  cleaningError: string;
  cleaningSelectedIds: Set<string>;
  cleaningSavingIds: Set<string>;
  cleaningRequestSaving: boolean;
  inventoryTransferData: InventoryTransferData | null;
  inventoryTransferView: InventoryTransferView;
  inventoryTransferLoading: boolean;
  inventoryTransferError: string;
  inventoryTransferSubmitting: boolean;
  inventoryReplenishmentSelectedIds: Set<string>;
  inventoryRelocationSelectedGroups: Set<string>;
  inventoryRelocationSavingIds: Set<string>;
  inventoryReplenishmentCount: number;
  customerManagementData: CustomerManagementData | null;
  customerManagementView: CustomerManagementView;
  customerManagementLoading: boolean;
  customerManagementError: string;
  customerManagementUpdatingIds: Set<string>;
  customerManagementSelectedIds: Set<string>;
  customerManagementUnlockedIds: Set<string>;
  customerManagementEditToken: string;
  customerManagementEditExpiresAt: string;
  customerManagementUnlockBusy: boolean;
  customerManagementSaveBusy: boolean;
  customerManagementDrafts: Map<string, CustomerManagementDraft>;
  repairAtelierSelections: Map<string, string>;
  repairInstockSavingOrderId: string | null;
  query: string;
  filter: ListFilter;
  sortMode: OrderSortMode;
  expressFirst: boolean;
  historyPage: number;
  checkedOrderIds: Set<string>;
  selectedLegacyId: string | null;
  isBulkUpdating: boolean;
  isWorkspaceSwitching: boolean;
  isFilterSwitching: boolean;
  isPageReloading: boolean;
  isOrderDetailsRefreshing: boolean;
  isImporting: boolean;
  importError: string;
  importMessage: string;
  maintenanceOpen: boolean;
  maintenanceToken: string;
  maintenanceExpiresAt: string;
  maintenanceQuery: string;
  maintenanceResults: MaintenanceCandidate[];
  complianceRequests: ComplianceRequestSummary[];
  maintenanceSelectedGid: string | null;
  maintenanceBusy: boolean;
  maintenanceMessage: string;
  resetToken: string;
  resetExpiresAt: string;
} = {
  data: null,
  view: "active",
  workspace: "all",
  etcWorkspace: null,
  cleaningData: null,
  cleaningView: "received",
  cleaningLoading: false,
  cleaningError: "",
  cleaningSelectedIds: new Set(),
  cleaningSavingIds: new Set(),
  cleaningRequestSaving: false,
  inventoryTransferData: null,
  inventoryTransferView: "active",
  inventoryTransferLoading: false,
  inventoryTransferError: "",
  inventoryTransferSubmitting: false,
  inventoryReplenishmentSelectedIds: new Set(),
  inventoryRelocationSelectedGroups: new Set(),
  inventoryRelocationSavingIds: new Set(),
  inventoryReplenishmentCount: 0,
  customerManagementData: null,
  customerManagementView: "active",
  customerManagementLoading: false,
  customerManagementError: "",
  customerManagementUpdatingIds: new Set(),
  customerManagementSelectedIds: new Set(),
  customerManagementUnlockedIds: new Set(),
  customerManagementEditToken: "",
  customerManagementEditExpiresAt: "",
  customerManagementUnlockBusy: false,
  customerManagementSaveBusy: false,
  customerManagementDrafts: new Map(),
  repairAtelierSelections: new Map(),
  repairInstockSavingOrderId: null,
  query: "",
  filter: "all",
  sortMode: "default",
  expressFirst: false,
  historyPage: 1,
  checkedOrderIds: new Set(),
  selectedLegacyId: null,
  isBulkUpdating: false,
  isWorkspaceSwitching: false,
  isFilterSwitching: false,
  isPageReloading: false,
  isOrderDetailsRefreshing: false,
  isImporting: false,
  importError: "",
  importMessage: "",
  maintenanceOpen: false,
  maintenanceToken: "",
  maintenanceExpiresAt: "",
  maintenanceQuery: "",
  maintenanceResults: [],
  complianceRequests: [],
  maintenanceSelectedGid: null,
  maintenanceBusy: false,
  maintenanceMessage: "",
  resetToken: "",
  resetExpiresAt: "",
};

let lockedScrollY: number | null = null;
let orderSearchComposing = false;
let operationSuccessTimer: number | null = null;

function clearOperationSuccessTimer(): void {
  if (operationSuccessTimer === null) return;
  window.clearTimeout(operationSuccessTimer);
  operationSuccessTimer = null;
}

function showOperationSuccess(message: string): void {
  clearOperationSuccessTimer();
  state.importMessage = message;
  render();
  operationSuccessTimer = window.setTimeout(() => {
    operationSuccessTimer = null;
    if (state.importMessage !== message) return;
    state.importMessage = "";
    render();
  }, 5_000);
}

function lockBackgroundScroll(): void {
  if (lockedScrollY !== null) return;
  lockedScrollY = window.scrollY;
  document.documentElement.classList.add("modal-open");
  document.body.classList.add("modal-open");
  document.body.style.top = `-${lockedScrollY}px`;
}

function unlockBackgroundScroll(): void {
  if (document.querySelector("dialog[open]")) return;
  if (lockedScrollY === null) return;
  const scrollY = lockedScrollY;
  lockedScrollY = null;
  document.documentElement.classList.remove("modal-open");
  document.body.classList.remove("modal-open");
  document.body.style.top = "";
  window.scrollTo({ top: scrollY, left: 0, behavior: "auto" });
}

function showLockedModal(dialog: HTMLDialogElement): void {
  if (dialog.open) return;
  lockBackgroundScroll();
  dialog.showModal();
}

const dialogScrollObserver = new MutationObserver(() => {
  if (document.querySelector("dialog[open]")) {
    lockBackgroundScroll();
  } else {
    unlockBackgroundScroll();
  }
});
dialogScrollObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["open"],
  childList: true,
  subtree: true,
});

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const errorBody = isRecord(body) ? (body as ApiErrorBody) : {};
    throw new Error(errorBody.error?.message ?? `API error (${response.status})`);
  }
  return body as T;
}

function maintenanceHeaders(): HeadersInit {
  return { "x-maintenance-token": state.maintenanceToken };
}

function maintenanceUnlocked(): boolean {
  return Boolean(
    state.maintenanceToken
    && state.maintenanceExpiresAt
    && new Date(state.maintenanceExpiresAt).getTime() > Date.now(),
  );
}

function resetHeaders(): HeadersInit {
  return { "x-reset-token": state.resetToken };
}

function resetUnlocked(): boolean {
  return Boolean(
    state.resetToken
    && state.resetExpiresAt
    && new Date(state.resetExpiresAt).getTime() > Date.now(),
  );
}

function formatMoney(amount: string, currencyCode: string): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: currencyCode === "JPY" ? 0 : 2,
  }).format(Number(amount));
}

function formatDate(value: string, withTime = true): string {
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(new Date(value));
}

function formatCalendarDate(value: string): string {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!matched) return value;
  return `${matched[1]}/${Number(matched[2])}/${Number(matched[3])}`;
}

function relativeTime(value: string): string {
  const minutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000);
  const formatter = new Intl.RelativeTimeFormat("ja", { numeric: "auto" });
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

function notify(message: string, isError = false): void {
  if (window.shopify?.toast) {
    window.shopify.toast.show(message, { duration: 3500, isError });
    return;
  }
  const toast = document.createElement("div");
  toast.className = `fallback-toast${isError ? " is-error" : ""}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  window.setTimeout(() => toast.remove(), 3500);
}

function syncBadge(order: DashboardOrder): string {
  if (order.memoSyncStatus === "error" || order.summarySyncStatus === "error") {
    return '<span class="status-badge error"><span></span>同期エラー</span>';
  }
  if (order.memoSyncStatus === "pending" || order.summarySyncStatus === "pending") {
    return '<span class="status-badge pending"><span></span>同期中</span>';
  }
  return '<span class="status-badge success"><span></span>同期済み</span>';
}

function workflowBadge(status: string): string {
  if (status === "終了データ") return '<span class="workflow-badge ended">終了データ</span>';
  if (status === "非表示済") return '<span class="workflow-badge hidden">非表示済</span>';
  if (isShippingRequestStatus(status)) return '<span class="workflow-badge shipped">出荷依頼</span>';
  if (status === "受付済み") return '<span class="workflow-badge requested">受付済み</span>';
  if (status === "依頼済み") return '<span class="workflow-badge requested">依頼済み</span>';
  if (status === "輸入中") return '<span class="workflow-badge importing">輸入中</span>';
  if (status === "手続き済" || status === "入荷済み") return '<span class="workflow-badge received">入荷済み</span>';
  if (status === "印刷完了") return '<span class="workflow-badge received">印刷完了</span>';
  if (status === "入荷完了") return '<span class="workflow-badge received">入荷完了</span>';
  return '<span class="workflow-badge waiting">未処理</span>';
}

function repairFinishBadge(value: string): string {
  const label = value.trim();
  if (!label) return "—";
  const tone = label === "特急仕上げ"
    ? "express"
    : label === "通常仕上げ"
    ? "standard"
    : "other";
  return `<span class="repair-finish-badge ${tone}">${escapeHtml(label)}</span>`;
}

function statusClass(status: string): string {
  if (status === "終了データ") return " status-ended";
  if (status === "非表示済") return " status-hidden";
  if (isShippingRequestStatus(status)) return " status-shipped";
  if (status === "受付済み") return " status-requested";
  if (status === "依頼済み") return " status-requested";
  if (status === "手続き済" || status === "入荷済み") return " status-received";
  if (status === "印刷完了") return " status-received";
  if (status === "入荷完了") return " status-received";
  return "";
}

function salesSourceBadge(salesSource: string): string {
  const label = salesSource || "—";
  const tone = salesSource === "大阪"
    ? "osaka"
    : salesSource === "東京"
    ? "tokyo"
    : salesSource === "ネット"
    ? "web"
    : "other";
  return `<span class="sales-source-badge ${tone}">${escapeHtml(label)}</span>`;
}

function shopifyStatusBadge(status: string): string {
  const normalized = status.trim().toUpperCase();
  const statuses: Record<string, { label: string; tone: string }> = {
    FULFILLED: { label: "発送済み", tone: "fulfilled" },
    CANCELLED: { label: "キャンセル", tone: "hold" },
    CANCELED: { label: "キャンセル", tone: "hold" },
    PARTIALLY_FULFILLED: { label: "一部発送済み", tone: "partial" },
    IN_PROGRESS: { label: "処理中", tone: "progress" },
    PENDING_FULFILLMENT: { label: "処理待ち", tone: "progress" },
    SCHEDULED: { label: "発送予定", tone: "scheduled" },
    ON_HOLD: { label: "保留", tone: "hold" },
    REQUEST_DECLINED: { label: "依頼拒否", tone: "hold" },
    RESTOCKED: { label: "再入庫済み", tone: "neutral" },
    OPEN: { label: "未発送", tone: "unfulfilled" },
    UNFULFILLED: { label: "未発送", tone: "unfulfilled" },
    FULFILLMENT_NOT_REQUIRED: { label: "発送不要", tone: "neutral" },
  };
  const display = statuses[normalized]
    ?? { label: status.trim() || "未取得", tone: "neutral" };
  return `<span class="shopify-status-badge ${display.tone}">${escapeHtml(display.label)}</span>`;
}

function paymentStatusBadge(financialStatus: string): string {
  const label = paymentStatusLabel(financialStatus);
  const tone = label === "支払済" ? "paid" : "unpaid";
  return `<span class="financial-status-badge ${tone}">${label}</span>`;
}

function isPaymentUnpaid(financialStatus: string): boolean {
  return paymentStatusLabel(financialStatus) === "未払い";
}

function paymentHoldBadge(financialStatus: string): string {
  return isPaymentUnpaid(financialStatus)
    ? '<b class="financial-status-badge unpaid">未払い</b>'
    : "";
}

function isShopifyFulfilled(status: string): boolean {
  return status.trim().toUpperCase() === "FULFILLED";
}

function filterOrders(orders: DashboardOrder[]): DashboardOrder[] {
  const byStatus = orders.filter((order) => {
    const dailyHiddenOnly = state.workspace === "all"
      && order.recordType === "order"
      && !shouldDisplayDailyOrderListOrder(order.items);
    const shopifyFulfilled = isShopifyFulfilled(order.shopifyFulfillmentStatus);
    if (state.filter === "ended") return order.recordType === "ended";
    if (state.workspace === "all" && shopifyFulfilled) return false;
    if (state.filter === "unnecessary") {
      return state.workspace === "all"
        && (order.csOrderStatus === "印刷完了" || order.csOrderStatus === "入荷完了")
        && orderCancellationType(order.items, order.shopifyFulfillmentStatus) !== "";
    }
    if (state.filter === "hidden") {
      return dailyHiddenOnly || (order.csOrderStatus === "非表示済" && !shopifyFulfilled);
    }
    if (dailyHiddenOnly) return false;
    if (state.filter === "received") {
      const received = state.workspace === "all"
        ? order.csOrderStatus === "印刷完了" || order.csOrderStatus === "入荷完了"
        : order.csOrderStatus === "入荷完了";
      return received && !shopifyFulfilled;
    }
    if (state.filter === "shipped") {
      return isShippingRequestStatus(order.csOrderStatus) || shopifyFulfilled;
    }
    return true;
  });
  return byStatus.filter((order) => matchesOrderSearch(order, state.query));
}

function orderRows(
  orders: DashboardOrder[],
  selectable = true,
  allOrdersLayout = false,
  showInventoryTransferStatus = false,
): string {
  if (orders.length === 0) {
    return `<div class="empty-state">
      <div class="empty-icon">98</div>
      <h3>該当する注文はありません</h3>
      <p>検索条件または表示中の処理状態を変更してください。</p>
    </div>`;
  }

  return orders.map((order) => {
    if (order.recordType === "ended" && allOrdersLayout) {
      return `<div class="order-row all-order-row status-ended ended-record" aria-label="${escapeHtml(order.name)}の終了データ">
        <span class="select-cell"><input type="checkbox" disabled aria-label="終了データは選択できません"></span>
        <span class="order-identity"><strong>${escapeHtml(order.name)}</strong><span class="customer-name">終了データ</span><small>Order ID: ${escapeHtml(order.legacyId)}</small></span>
        <span class="ended-identifiers"><small>認識番号</small><code>${escapeHtml(order.id)}</code></span>
        <span></span><span></span><span></span><span></span><span></span><span></span>
        <span></span><span></span><span></span><span></span>
      </div>`;
    }
    if (order.recordType === "ended") {
      return `<div class="order-row status-ended ended-record" aria-label="${escapeHtml(order.name)}の終了データ">
        <span class="select-cell"><input type="checkbox" disabled aria-label="終了データは選択できません"></span>
        <span class="order-identity">
          <strong>${escapeHtml(order.name)}</strong>
          <span class="customer-name">終了データ</span>
          <small>Order ID: ${escapeHtml(order.legacyId)}</small>
        </span>
        <span class="ended-identifiers"><small>認識番号</small><code>${escapeHtml(order.id)}</code></span>
        <span></span><span></span><span></span><span></span>
        <span class="province-cell"></span>
        <span class="memo-cell">識別情報のみ保持</span>
        <span class="item-arrival-date-cell"></span>
        <span class="shipping-request-date-cell"></span>
        <span class="workflow-cell">${workflowBadge(order.csOrderStatus)}</span>
        <span class="shopify-status-cell"></span>
        <span class="chevron" aria-hidden="true"></span>
      </div>`;
    }
    const listSkuItems = orderListItemsForWorkspace(
      allOrdersLayout ? "all" : "size",
      order.sourceName,
      order.items,
    );
    const previewItems = allOrdersLayout ? listSkuItems : listSkuItems.slice(0, 2);
    const cancellation = allOrdersLayout
      ? summarizeOrderCancellation(order.items, order.shopifyFulfillmentStatus)
      : null;
    const partialCancelledQuantityByItemId = new Map(
      cancellation?.partiallyCancelled
        ? cancellation.cancelled.map((segment) => [segment.item.id, segment.quantity] as const)
        : [],
    );
    const dailyCancellation = Boolean(cancellation?.fullyCancelled || cancellation?.partiallyCancelled);
    const dailyCancellationText = cancellation ? orderCancellationLabel(cancellation) : "";
    const skuPreview = previewItems.map((item) => {
      const sku = item.sku.trim();
      if (!sku) return "<code>SKUなし</code>";
      const cancelledQuantity = partialCancelledQuantityByItemId.get(item.id) ?? 0;
      const returnRequired = cancelledQuantity <= 0 && allOrdersLayout && requiresInventoryReturn(
        order.salesSource,
        item.consumedInventoryLocations ?? [],
      );
      const cancellationTape = cancelledQuantity > 0
        ? `<b class="partial-cancelled-sku-badge">品取消${cancelledQuantity > 1 ? ` ×${cancelledQuantity}` : ""}</b>`
        : "";
      const returnRequiredBadge = returnRequired
        ? '<b class="inventory-return-required-badge"><span>在庫</span><span>相違</span></b>'
        : "";
      return `<button class="sku-inventory-trigger${cancelledQuantity > 0 ? " partial-cancelled-sku-trigger" : ""}${returnRequired ? " return-required-sku-trigger" : ""}" type="button"
        data-inventory-sku="${escapeHtml(sku)}"
        data-inventory-title="${escapeHtml(item.title)}"
        aria-label="SKU ${escapeHtml(sku)}の在庫を表示${cancelledQuantity > 0 ? `、${cancelledQuantity}点品取消` : ""}${returnRequired ? "、在庫相違" : ""}">
        <code>${escapeHtml(sku)}</code>${returnRequiredBadge}${cancellationTape}
      </button>`;
    }).join("");
    const overflow = !allOrdersLayout && listSkuItems.length > previewItems.length
      ? `<em>+${listSkuItems.length - previewItems.length}</em>`
      : "";
    const specificNamePreview = previewItems
      .map((item) => `<span>${escapeHtml(item.title.trim() || "—")}</span>`)
      .join("");
    const sizePreview = previewItems
      .map((item) => `<span>${escapeHtml(orderListItemSize(item) || "—")}</span>`)
      .join("");
    const consumptionPreview = previewItems
      .map((item) => {
        const value = inventoryConsumptionDisplay(item);
        const cancelledQuantity = partialCancelledQuantityByItemId.get(item.id) ?? 0;
        const returnRequired = cancelledQuantity <= 0 && allOrdersLayout && requiresInventoryReturn(
          order.salesSource,
          item.consumedInventoryLocations ?? [],
        );
        return `<span class="${returnRequired ? "inventory-return-required-consumption" : ""}">${escapeHtml(value)}</span>`;
      })
      .join("");
    const inventoryPreview = previewItems
      .map((item) => `<span>${escapeHtml(
        inventoryRemainingDisplay(item),
      )}</span>`)
      .join("");
    const checked = state.checkedOrderIds.has(order.id);
    const disabled = !selectable || (state.view !== "history"
      && (isShippingRequestStatus(order.csOrderStatus) || order.csOrderStatus === "非表示済"));
    const highlightedProvince = order.shippingProvince === "北海道" || order.shippingProvince === "沖縄県";
    const paymentUnpaid = isPaymentUnpaid(order.financialStatus);
    const inventoryTransferStatus = showInventoryTransferStatus
      ? '<span class="all-order-transfer-cell">'
        + (order.hasInventoryTransferRequest
          ? '<span class="inventory-transfer-requested-badge">依頼済</span>'
          : "—")
        + "</span>"
      : "";
    return `<div class="order-row${allOrdersLayout ? " all-order-row" : ""}${statusClass(order.csOrderStatus)}${highlightedProvince ? " remote-province" : ""}${paymentUnpaid ? " payment-unpaid" : ""}${dailyCancellation ? " daily-cancelled-order" : ""}${showInventoryTransferStatus ? " has-transfer-status" : ""}${showInventoryTransferStatus && order.hasInventoryTransferRequest ? " inventory-transfer-requested-order" : ""}" data-order-id="${escapeHtml(order.sizeUnitId || order.legacyId)}" tabindex="0" role="button" aria-label="${escapeHtml(order.name)}の詳細を開く">
      <span class="select-cell">
        <input type="checkbox" data-select-order="${escapeHtml(order.id)}" aria-label="${escapeHtml(order.name)}を選択" ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}>
      </span>
      <span class="order-identity${highlightedProvince ? " remote-province-identity" : ""}">
        <span class="order-number-line"><strong>${escapeHtml(order.name)}</strong>${dailyCancellationText
          ? `<b class="daily-cancellation-label">${dailyCancellationText}</b>`
          : `${paymentHoldBadge(order.financialStatus)}${order.isExpress ? '<b class="express-badge">特急</b>' : ""}`}</span>
        <span class="customer-name">${escapeHtml(order.customerName || "ゲスト注文")} 様</span>
        <small>${formatDate(order.createdAt)} · ${relativeTime(order.createdAt)}${order.manualOverride ? " · 手動登録" : ""}</small>
      </span>
      <span class="route-cell sales-source-cell">${salesSourceBadge(order.salesSource)}</span>
      <span class="route-cell delivery-cell">${escapeHtml(order.deliveryMethod || "—")}</span>
      ${allOrdersLayout ? "" : `<span class="route-cell arrival-cell">${escapeHtml(order.arrivalContact || "—")}</span>`}
      <span class="order-schedule-cell">
        <span class="requested-delivery-date${order.requestedDeliveryDate ? " has-value" : ""}"><small>${order.requestedDeliveryDate ? "配送希望日" : "配送"}</small><strong>${escapeHtml(order.requestedDeliveryDate || "—")}</strong></span>
        <span><small>着用</small><strong>${escapeHtml(order.wearingDate || "—")}</strong></span>
      </span>
      ${allOrdersLayout ? `<span class="repair-finish-cell all-order-finish-cell">${repairFinishBadge(order.repairFinish)}</span>` : ""}
      <span class="sku-stack">${skuPreview || '<span class="sku-empty">—</span>'}${overflow}</span>
      ${allOrdersLayout ? `<span class="all-order-item-stack all-order-size-cell">${sizePreview || "—"}${overflow}</span><span class="all-order-item-stack all-order-name-cell">${specificNamePreview || "—"}${overflow}</span><span class="all-order-item-stack all-order-consumption-cell">${consumptionPreview || "—"}${overflow}</span><span class="all-order-item-stack all-order-inventory-cell">${inventoryPreview || "—"}${overflow}</span>` : ""}
      <span class="province-cell">${highlightedProvince
        ? `<span class="province-badge">${escapeHtml(order.shippingProvince)}</span>`
        : escapeHtml(order.shippingProvince || "—")}</span>
      ${inventoryTransferStatus}
      ${allOrdersLayout ? "" : `<span class="memo-cell ${order.memo ? "has-memo" : ""}">${order.memo ? escapeHtml(order.memo) : "メモなし"}</span><span class="item-arrival-date-cell">${order.itemArrivalDate ? escapeHtml(formatCalendarDate(order.itemArrivalDate)) : "—"}</span><span class="shipping-request-date-cell">${order.shippingRequestDate ? escapeHtml(formatCalendarDate(order.shippingRequestDate)) : "—"}</span>`}
      ${allOrdersLayout ? "" : `<span class="workflow-cell">${workflowBadge(order.csOrderStatus)}</span>`}
      ${allOrdersLayout ? "" : `<span class="shopify-status-cell">${shopifyStatusBadge(order.shopifyFulfillmentStatus)}</span>`}
      <span class="chevron" aria-hidden="true">›</span>
    </div>`;
  }).join("");
}

function repairOrderRows(orders: DashboardOrder[], atelierOptions: string[]): string {
  if (orders.length === 0) {
    return `<div class="empty-state">
      <div class="empty-icon">✂</div>
      <h3>該当するお直し注文はありません</h3>
      <p>検索条件または表示中の処理状態を変更してください。</p>
    </div>`;
  }

  return orders.map((order) => {
    if (order.recordType === "ended") {
      return `<div class="order-row repair-order-row status-ended ended-record" aria-label="${escapeHtml(order.name)}の終了データ">
        <span class="select-cell"><input type="checkbox" disabled aria-label="終了データは選択できません"></span>
        <span class="order-identity"><span class="order-number-line"><strong>${escapeHtml(order.name)}</strong>${repairCaseBadge(order.name)}</span><span class="customer-name">終了データ</span><small>Order ID: ${escapeHtml(order.legacyId)}</small></span>
        <span class="ended-identifiers"><small>認識番号</small><code>${escapeHtml(order.id)}</code></span>
        <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
        <span class="memo-cell">識別情報のみ保持</span><span></span>
        <span class="workflow-cell">${workflowBadge(order.csOrderStatus)}</span><span></span><span></span><span></span>
      </div>`;
    }
    if (order.recordType === "after-alteration") {
      const checked = state.checkedOrderIds.has(order.id);
      const disabled = state.view !== "history"
        && (isShippingRequestStatus(order.csOrderStatus) || order.csOrderStatus === "非表示済");
      const showAtelierRadios = state.view !== "history"
        && state.filter !== "received"
        && !disabled;
      const selectedAtelier = state.repairAtelierSelections.get(order.id) ?? order.repairAtelier;
      const atelierSelectionPending = state.repairAtelierSelections.has(order.id);
      const atelierCell = showAtelierRadios
        ? atelierOptions.length > 0
          ? `<fieldset class="repair-atelier-radio-list${atelierSelectionPending ? " is-pending" : ""}">
            <legend>${escapeHtml(order.name)}の依頼工房</legend>
            ${atelierOptions.map((option) => `<label class="repair-atelier-option"><input type="radio"
              name="repairAtelier-${escapeHtml(order.id)}"
              data-repair-atelier-order="${escapeHtml(order.id)}"
              value="${escapeHtml(option)}"
              ${selectedAtelier === option ? "checked" : ""}
              ${state.isBulkUpdating ? "disabled" : ""}> <span>${escapeHtml(option)}</span></label>`).join("")}
            ${repairAtelierResetButton(order.id, order.name, atelierSelectionPending, state.isBulkUpdating)}
          </fieldset>`
          : '<span class="repair-atelier-empty">選択肢なし</span>'
        : escapeHtml(order.repairAtelier || "-");
      return `<div class="order-row repair-order-row after-alteration-record${statusClass(order.csOrderStatus)}"
        data-order-id="${escapeHtml(order.id)}" tabindex="0" role="button"
        aria-label="${escapeHtml(order.name)}の後直し管理データを開く">
        <span class="select-cell"><input type="checkbox" data-select-order="${escapeHtml(order.id)}"
          aria-label="${escapeHtml(order.name)}を選択" ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}></span>
        <span class="order-identity">
          <span class="order-number-line"><strong>${escapeHtml(order.name)}</strong>${repairCaseBadge(order.name)}</span>
          <span class="customer-name">${escapeHtml(order.customerName)}</span>
          <small>${escapeHtml(formatCalendarDate(order.createdAt))}</small>
        </span>
        <span class="route-cell sales-source-cell">${salesSourceBadge("ネット")}</span>
        <span class="route-cell delivery-cell">-</span>
        <span class="repair-finish-cell">-</span>
        ${repairInstockDateCell(order, order.repairInstockDate ? formatCalendarDate(order.repairInstockDate) : "-", state.isBulkUpdating, state.view === "history", state.repairInstockSavingOrderId === order.id)}
        <span class="repair-atelier-cell">${atelierCell}</span>
        <span class="repair-request-date-cell">${order.repairRequestDate ? escapeHtml(formatCalendarDate(order.repairRequestDate)) : "-"}</span>
        <span class="order-schedule-cell">-</span>
        <span class="sku-stack"><span class="sku-empty">-</span></span>
        <span class="province-cell">-</span>
        <span class="memo-cell ${order.memo ? "has-memo" : ""}">${order.memo ? escapeHtml(order.memo) : "-"}</span>
        <span class="shipping-request-date-cell">${order.shippingRequestDate ? escapeHtml(formatCalendarDate(order.shippingRequestDate)) : "-"}</span>
        <span class="workflow-cell">${order.csOrderStatus ? workflowBadge(order.csOrderStatus) : "-"}</span>
        <span class="shopify-status-cell">-</span>
        <span class="financial-status-cell">-</span>
        <span class="chevron" aria-hidden="true">›</span>
      </div>`;
    }
    const listSkuItems = filterRepairOrderListSkuItems(order.items);
    const skuPreview = listSkuItems.slice(0, 2).map((item) => {
      const sku = item.sku.trim();
      if (!sku) return "<code>SKUなし</code>";
      return `<button class="sku-inventory-trigger" type="button"
        data-inventory-sku="${escapeHtml(sku)}"
        data-inventory-title="${escapeHtml(item.title)}"
        aria-label="SKU ${escapeHtml(sku)}の在庫を表示"><code>${escapeHtml(sku)}</code></button>`;
    }).join("");
    const highlightedProvince = order.shippingProvince === "北海道" || order.shippingProvince === "沖縄県";
    const checked = state.checkedOrderIds.has(order.id);
    const disabled = state.view !== "history"
      && (isShippingRequestStatus(order.csOrderStatus) || order.csOrderStatus === "非表示済");
    const showAtelierRadios = state.view !== "history" && state.filter !== "received" && !disabled;
    const selectedAtelier = state.repairAtelierSelections.get(order.id) ?? order.repairAtelier;
    const atelierSelectionPending = state.repairAtelierSelections.has(order.id);
    const paymentUnpaid = isPaymentUnpaid(order.financialStatus);
    const selectionDisabled = disabled || isUnpaidRepairSelection("repair", order);
    const atelierCell = showAtelierRadios
      ? atelierOptions.length > 0
        ? `<fieldset class="repair-atelier-radio-list${atelierSelectionPending ? " is-pending" : ""}">
          <legend>${escapeHtml(order.name)}の依頼工房</legend>
          ${atelierOptions.map((option) => `<label class="repair-atelier-option"><input type="radio"
            name="repairAtelier-${escapeHtml(order.legacyId)}"
            data-repair-atelier-order="${escapeHtml(order.id)}"
            value="${escapeHtml(option)}"
            ${selectedAtelier === option ? "checked" : ""}
            ${state.isBulkUpdating ? "disabled" : ""}> <span>${escapeHtml(option)}</span></label>`).join("")}
          ${repairAtelierResetButton(order.id, order.name, atelierSelectionPending, state.isBulkUpdating)}
        </fieldset>`
        : '<span class="repair-atelier-empty">選択肢なし</span>'
      : escapeHtml(order.repairAtelier || "—");
    return `<div class="order-row repair-order-row${statusClass(order.csOrderStatus)}${highlightedProvince ? " remote-province" : ""}${paymentUnpaid ? " payment-unpaid" : ""}" data-order-id="${escapeHtml(order.sizeUnitId || order.legacyId)}" tabindex="0" role="button" aria-label="${escapeHtml(order.name)}の詳細を開く">
      <span class="select-cell"${paymentUnpaid ? ' title="未払いの注文は選択できません"' : ""}><input type="checkbox" data-select-order="${escapeHtml(order.id)}" aria-label="${escapeHtml(order.name)}を選択" ${checked && !selectionDisabled ? "checked" : ""} ${selectionDisabled ? "disabled" : ""}></span>
      <span class="order-identity${highlightedProvince ? " remote-province-identity" : ""}">
        <span class="order-number-line"><strong>${escapeHtml(order.name)}</strong>${repairCaseBadge(order.name)}${paymentHoldBadge(order.financialStatus)}${order.isExpress ? '<b class="express-badge">特急</b>' : ""}</span>
        <span class="customer-name">${escapeHtml(order.customerName || "ゲスト注文")} 様</span>
        <small>${formatDate(order.createdAt)} · ${relativeTime(order.createdAt)}${order.manualOverride ? " · 手動登録" : ""}</small>
      </span>
      <span class="route-cell sales-source-cell">${salesSourceBadge(order.salesSource)}</span>
      <span class="route-cell delivery-cell">${escapeHtml(order.deliveryMethod || "—")}</span>
      <span class="repair-finish-cell">${repairFinishBadge(order.repairFinish)}</span>
      ${repairInstockDateCell(order, order.repairInstockDate ? formatCalendarDate(order.repairInstockDate) : "—", state.isBulkUpdating, state.view === "history", state.repairInstockSavingOrderId === order.id)}
      <span class="repair-atelier-cell">${atelierCell}</span>
      <span class="repair-request-date-cell">${order.repairRequestDate ? escapeHtml(formatCalendarDate(order.repairRequestDate)) : "—"}</span>
      <span class="order-schedule-cell">
        <span class="requested-delivery-date${order.requestedDeliveryDate ? " has-value" : ""}"><small>${order.requestedDeliveryDate ? "配送希望日" : "配送"}</small><strong>${escapeHtml(order.requestedDeliveryDate || "—")}</strong></span>
        <span><small>着用</small><strong>${escapeHtml(order.wearingDate || "—")}</strong></span>
      </span>
      <span class="sku-stack">${skuPreview || '<span class="sku-empty">—</span>'}${listSkuItems.length > 2 ? `<em>+${listSkuItems.length - 2}</em>` : ""}</span>
      <span class="province-cell">${highlightedProvince
        ? `<span class="province-badge">${escapeHtml(order.shippingProvince)}</span>`
        : escapeHtml(order.shippingProvince || "—")}</span>
      <span class="memo-cell ${order.memo ? "has-memo" : ""}">${order.memo ? escapeHtml(order.memo) : "メモなし"}</span>
      <span class="shipping-request-date-cell">${order.shippingRequestDate ? escapeHtml(formatCalendarDate(order.shippingRequestDate)) : "—"}</span>
      <span class="workflow-cell">${workflowBadge(order.csOrderStatus)}</span>
      <span class="shopify-status-cell">${shopifyStatusBadge(order.shopifyFulfillmentStatus)}</span>
      <span class="financial-status-cell">${paymentStatusBadge(order.financialStatus)}</span>
      <span class="chevron" aria-hidden="true">›</span>
    </div>`;
  }).join("");
}

async function updateSelectedRepairRequest(): Promise<void> {
  const selections = [...state.repairAtelierSelections].map(([orderId, atelier]) => ({
    orderId,
    atelier,
  }));
  if (
    state.workspace !== "repair"
    || selections.length === 0
    || state.isBulkUpdating
  ) return;
  state.isBulkUpdating = true;
  render();
  try {
    const result = await api<{ updated: number; requestDate: string }>(
      "/api/orders/repair-request",
      {
        method: "POST",
        body: JSON.stringify({ selections }),
      },
    );
    state.repairAtelierSelections.clear();
    notify(`${result.updated}件の依頼工房・依頼日を保存し、処理状況を「依頼済み」に変更しました。`);
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "依頼処理を完了できませんでした。", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function updateRepairInstockDate(orderId: string): Promise<void> {
  const order = state.data?.orders.find((candidate) => candidate.id === orderId);
  if (
    state.workspace !== "repair"
    || state.etcWorkspace !== null
    || !order
    || !canRecordRepairInstockDate(order, state.isBulkUpdating, state.view === "history")
  ) return;
  state.repairInstockSavingOrderId = orderId;
  state.isBulkUpdating = true;
  render();
  try {
    const result = await requestRepairInstockDateUpdate([orderId]);
    if (result.repairInstockDate) order.repairInstockDate = result.repairInstockDate;
    const recordedDate = result.repairInstockDate
      ? formatCalendarDate(result.repairInstockDate)
      : "本日";
    notify(`${order.name}の後直し入荷日に${recordedDate}を記録しました。`);
    await loadData(false, true);
  } catch (error) {
    notify(error instanceof Error ? error.message : "後直し入荷日を記録できませんでした。", true);
  } finally {
    state.repairInstockSavingOrderId = null;
    state.isBulkUpdating = false;
    render();
  }
}

function resetOrderSearch(): void {
  state.query = "";
  orderSearchComposing = false;
}

async function switchWorkspace(workspace: DashboardWorkspace): Promise<void> {
  if ((state.workspace === workspace && state.etcWorkspace === null)
    || state.isWorkspaceSwitching
    || state.isFilterSwitching) return;
  const entryPlan = orderWorkspaceEntryPlan(
    state.workspace,
    workspace,
    state.etcWorkspace !== null,
  );
  const { needsLoad } = entryPlan;
  resetOrderSearch();
  state.isWorkspaceSwitching = true;
  state.etcWorkspace = null;
  state.workspace = workspace;
  state.view = entryPlan.view;
  state.filter = entryPlan.filter;
  state.sortMode = entryPlan.sortMode;
  state.expressFirst = entryPlan.expressFirst;
  state.historyPage = entryPlan.historyPage;
  state.checkedOrderIds.clear();
  state.repairAtelierSelections.clear();
  render();
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const minimumDelay = new Promise<void>((resolve) => window.setTimeout(resolve, 220));
  const loaded = needsLoad
    ? (await Promise.all([loadData(false), minimumDelay]))[0]
    : await minimumDelay.then(() => true);
  state.isWorkspaceSwitching = false;
  if (loaded) {
    render();
    if (workspace === "all") openDailyUnnecessaryNotice();
  }
}

function etcWorkspaceLabel(workspace: EtcWorkspace): string {
  return workspace === "inventory-transfer" ? "在庫転送管理"
    : workspace === "cleaning" ? "クリーニング"
    : workspace === "inventory-search" ? "在庫検索" : "返品・交換管理";
}

function resetCustomerManagementEditAccess(clearSelection = false): void {
  state.customerManagementEditToken = "";
  state.customerManagementEditExpiresAt = "";
  state.customerManagementUnlockedIds.clear();
  state.customerManagementUnlockBusy = false;
  state.customerManagementSaveBusy = false;
  state.customerManagementDrafts.clear();
  if (clearSelection) state.customerManagementSelectedIds.clear();
}

function customerManagementEditUnlocked(recordId: string): boolean {
  return Boolean(
    state.customerManagementEditToken
    && state.customerManagementEditExpiresAt
    && new Date(state.customerManagementEditExpiresAt).getTime() > Date.now()
    && state.customerManagementSelectedIds.has(recordId)
    && state.customerManagementUnlockedIds.has(recordId),
  );
}

function customerManagementRecordingActive(): boolean {
  return state.customerManagementSelectedIds.size > 0
    && Array.from(state.customerManagementSelectedIds).every((id) =>
      customerManagementEditUnlocked(id)
    );
}

function customerManagementEffectiveRecord(
  record: CustomerManagementRecord,
): CustomerManagementRecord {
  const draft = state.customerManagementDrafts.get(record.id);
  if (!draft) return record;
  const processingStatus = record.customerCompleted
    ? "対応完了"
    : draft.problemOccurred
      ? "商品問題あり・要確認"
      : draft.productCondition
        ? "異常なし・次の対応へ"
        : draft.warehouseArrival
          ? "商品到着・検品中"
          : "商品・入荷待ち";
  return { ...record, ...draft, processingStatus };
}

async function switchEtcWorkspace(workspace: EtcWorkspace): Promise<void> {
  if (state.etcWorkspace === workspace || state.isWorkspaceSwitching || state.isFilterSwitching) return;
  resetOrderSearch();
  state.isWorkspaceSwitching = true;
  state.etcWorkspace = workspace;
  state.checkedOrderIds.clear();
  state.repairAtelierSelections.clear();
  state.selectedLegacyId = null;
  state.maintenanceOpen = false;
  resetCustomerManagementEditAccess(true);
  if (workspace === "cleaning") {
    state.cleaningView = "received";
    state.cleaningLoading = true;
    state.cleaningError = "";
    state.cleaningSelectedIds.clear();
  }
  if (workspace === "return-exchange") state.customerManagementView = "active";
  if (workspace === "inventory-transfer") {
    state.inventoryTransferView = "active";
    state.inventoryReplenishmentSelectedIds.clear();
    state.inventoryRelocationSelectedGroups.clear();
  }
  render();
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const minimumDelay = new Promise<void>((resolve) => window.setTimeout(resolve, 220));
  if (workspace === "return-exchange") {
    await Promise.all([loadCustomerManagementData(), minimumDelay]);
  } else if (workspace === "inventory-transfer") {
    await Promise.all([loadInventoryTransferData(), minimumDelay]);
  } else if (workspace === "cleaning") {
    await Promise.all([loadCleaningData(1), minimumDelay]);
  } else {
    await minimumDelay;
  }
  state.isWorkspaceSwitching = false;
  render();
}

let cleaningLoadRequest = 0;

async function loadCleaningData(page = 1): Promise<void> {
  const requestId = ++cleaningLoadRequest;
  const view = state.cleaningView;
  state.cleaningLoading = true;
  state.cleaningError = "";
  state.cleaningSelectedIds.clear();
  render();
  try {
    const [data] = await Promise.all([
      api<CleaningManagementData>(`/api/cleaning-management?page=${page}&view=${view}`),
      new Promise<void>((resolve) => window.setTimeout(resolve, 220)),
    ]);
    if (requestId === cleaningLoadRequest && view === state.cleaningView) {
      state.cleaningData = data;
    }
  } catch (error) {
    if (requestId === cleaningLoadRequest) {
      state.cleaningError = error instanceof Error ? error.message : "クリーニングデータを読み込めませんでした。";
    }
  } finally {
    if (requestId === cleaningLoadRequest) {
      state.cleaningLoading = false;
      render();
    }
  }
}

async function switchCleaningView(view: CleaningView): Promise<void> {
  if (view === state.cleaningView || state.cleaningLoading || state.cleaningSavingIds.size > 0 || state.cleaningRequestSaving) return;
  state.cleaningView = view;
  await loadCleaningData(1);
  if (state.etcWorkspace === "cleaning" && state.cleaningView === view) {
    document.querySelector<HTMLButtonElement>(`[data-cleaning-view="${view}"]`)?.focus({ preventScroll: true });
  }
}

async function refreshCleaningAfterMove(): Promise<void> {
  // Invalidate any earlier read and clear selection before reclassifying the saved row.
  ++cleaningLoadRequest;
  state.cleaningLoading = false;
  state.cleaningSelectedIds.clear();
  if (state.etcWorkspace === "cleaning") await loadCleaningData(state.cleaningData?.pagination.page ?? 1);
}

async function recordCleaningArrival(id: string): Promise<void> {
  const record = state.cleaningData?.records.find((item) => item.id === id);
  if (!record || record.arrivalDate.trim() || state.cleaningSavingIds.size > 0 || state.cleaningRequestSaving || state.cleaningLoading) return;
  state.cleaningSavingIds.add(id);
  render();
  try {
    const result = await api<{ id: string; arrivalDate: string; processStatus: string; chatNotifications: { sent: number; failed: number } }>("/api/cleaning-management", {
      method: "POST", body: JSON.stringify({ action: "arrive", id }),
    });
    if (state.cleaningData) state.cleaningData.records = state.cleaningData.records.map((item) =>
      item.id === result.id ? { ...item, arrivalDate: result.arrivalDate, processStatus: result.processStatus } : item);
    if (result.chatNotifications.failed > 0) notify("倉庫到着日は記録しましたが、Google Chatへ通知できませんでした。", true);
    else notify(result.chatNotifications.sent > 0 ? "倉庫到着日を記録し、Google Chatへ通知しました。" : "倉庫到着日を記録しました。");
    await refreshCleaningAfterMove();
  } catch (error) {
    notify(error instanceof Error ? error.message : "倉庫到着日を記録できませんでした。", true);
  } finally {
    state.cleaningSavingIds.delete(id);
    if (state.etcWorkspace === "cleaning") render();
  }
}

async function recordCleaningVendorHandover(id: string): Promise<void> {
  const record = state.cleaningData?.records.find((item) => item.id === id);
  if (!record || !record.confirmedOrderNumber.trim() || record.vendorRequestDate.trim() || state.cleaningSavingIds.size > 0 || state.cleaningRequestSaving || state.cleaningLoading) return;
  state.cleaningSavingIds.add(id);
  render();
  try {
    const result = await api<{ id: string; vendorRequestDate: string; processStatus: string }>("/api/cleaning-management", {
      method: "POST", body: JSON.stringify({ action: "handover", id }),
    });
    if (state.cleaningData) state.cleaningData.records = state.cleaningData.records.map((item) =>
      item.id === result.id ? { ...item, vendorRequestDate: result.vendorRequestDate, processStatus: result.processStatus } : item);
    notify("業者渡し日を記録しました。");
    await refreshCleaningAfterMove();
  } catch (error) {
    notify(error instanceof Error ? error.message : "業者渡し日を記録できませんでした。", true);
  } finally {
    state.cleaningSavingIds.delete(id);
    if (state.etcWorkspace === "cleaning") render();
  }
}

async function requestCleaningShipping(): Promise<void> {
  if (state.etcWorkspace !== "cleaning" || state.cleaningView !== "cleaning") return;
  if (state.cleaningSavingIds.size > 0 || state.cleaningRequestSaving || state.cleaningLoading || state.cleaningError) return;
  const ids = state.cleaningData?.records.filter((record) => state.cleaningSelectedIds.has(record.id)).map((record) => record.id) ?? [];
  if (!ids.length) return;
  for (const id of ids) state.cleaningSavingIds.add(id);
  render();
  try {
    const result = await api<{ records: { id: string; processStatus: string }[]; updatedCount: number; chatNotifications?: { sent: number; failed: number } }>("/api/cleaning-management", {
      method: "POST", body: JSON.stringify({ action: "ship", ids }),
    });
    if (state.cleaningData) {
      const statuses = new Map(result.records.map((record) => [record.id, record.processStatus]));
      state.cleaningData.records = state.cleaningData.records.map((record) =>
        ({ ...record, processStatus: statuses.get(record.id) ?? record.processStatus }));
    }
    if (result.chatNotifications?.failed) {
      notify(`${result.records.length}件の出荷依頼を記録し、完了履歴に移動しましたが、Google Chatへ通知できませんでした。`, true);
    } else {
      notify(`${result.records.length}件の出荷依頼を記録しました。完了履歴に移動しました。${result.chatNotifications?.sent ? "Google Chatへ通知しました。" : ""}`);
    }
    await refreshCleaningAfterMove();
  } catch (error) {
    notify(error instanceof Error ? error.message : "出荷依頼を記録できませんでした。", true);
  } finally {
    for (const id of ids) state.cleaningSavingIds.delete(id);
    if (state.etcWorkspace === "cleaning") render();
  }
}

async function requestCleaningPickup(): Promise<void> {
  if (state.etcWorkspace !== "cleaning" || state.cleaningView !== "arrived") return;
  if (state.cleaningRequestSaving || state.cleaningSavingIds.size > 0 || state.cleaningLoading || state.cleaningError) return;
  state.cleaningRequestSaving = true;
  render();
  try {
    const result = await api<{ email?: { status: CleaningPickupStatus } }>("/api/cleaning-management", {
      method: "POST", body: JSON.stringify({ action: "request" }),
    });
    if (result.email?.status === "already_sent") {
      openCleaningPickupAlreadySentDialog(showLockedModal);
    } else {
      const feedback = cleaningPickupFeedback(result.email?.status);
      notify(feedback.message, feedback.isError);
    }
  } catch (error) {
    notify(error instanceof Error ? error.message : "集荷依頼メールを送信できませんでした。", true);
  } finally {
    state.cleaningRequestSaving = false;
    if (state.etcWorkspace === "cleaning") render();
  }
}

async function loadCustomerManagementData(): Promise<boolean> {
  resetCustomerManagementEditAccess(true);
  state.customerManagementLoading = true;
  state.customerManagementError = "";
  render();
  try {
    state.customerManagementData = await api<CustomerManagementData>("/api/customer-management");
    return true;
  } catch (error) {
    state.customerManagementError = error instanceof Error
      ? error.message
      : "返品・交換管理データを読み込めませんでした。";
    return false;
  } finally {
    state.customerManagementLoading = false;
    render();
  }
}

function switchCustomerManagementView(view: CustomerManagementView): void {
  if (
    state.customerManagementView === view
    || state.customerManagementLoading
    || state.customerManagementSaveBusy
    || state.customerManagementUnlockBusy
  ) return;
  resetCustomerManagementEditAccess(true);
  state.customerManagementView = view;
  render();
}

function customerManagementScrollLeft(): number {
  return document.querySelector<HTMLElement>(".customer-management-table-wrap")?.scrollLeft ?? 0;
}

function renderCustomerManagementAtScroll(scrollLeft: number): void {
  render();
  const wrapper = document.querySelector<HTMLElement>(".customer-management-table-wrap");
  if (wrapper) wrapper.scrollLeft = scrollLeft;
}

function stageCustomerManagementStatus(input: HTMLInputElement): void {
  const id = input.dataset.customerRecordId ?? "";
  const field = input.dataset.customerStatus as CustomerManagementStatusField | undefined;
  const record = state.customerManagementData?.records.find((candidate) => candidate.id === id);
  if (
    !id
    || !field
    || !record
    || state.customerManagementSaveBusy
    || !customerManagementEditUnlocked(id)
  ) return;

  const effective = customerManagementEffectiveRecord(record);
  const next: CustomerManagementDraft = {
    warehouseArrival: effective.warehouseArrival,
    problemOccurred: effective.problemOccurred,
    productCondition: effective.productCondition,
  };
  next[field] = input.checked;
  if (field === "warehouseArrival" && !input.checked) {
    next.problemOccurred = false;
    next.productCondition = false;
  }
  if (field === "problemOccurred" && input.checked) {
    next.productCondition = false;
  }
  if (field === "productCondition" && input.checked) {
    next.problemOccurred = false;
  }

  const unchanged = next.warehouseArrival === record.warehouseArrival
    && next.problemOccurred === record.problemOccurred
    && next.productCondition === record.productCondition;
  if (unchanged) {
    state.customerManagementDrafts.delete(id);
  } else {
    state.customerManagementDrafts.set(id, next);
  }
  renderCustomerManagementAtScroll(customerManagementScrollLeft());
}

async function saveCustomerManagementChanges(): Promise<void> {
  if (state.customerManagementSaveBusy || !customerManagementRecordingActive()) return;
  const pending = Array.from(state.customerManagementSelectedIds)
    .map((id) => [id, state.customerManagementDrafts.get(id)] as const)
    .filter((entry): entry is readonly [string, CustomerManagementDraft] => Boolean(entry[1]));
  if (pending.length === 0) {
    notify("記録する項目をチェックしてください。", true);
    return;
  }

  const scrollLeft = customerManagementScrollLeft();
  state.customerManagementSaveBusy = true;
  pending.forEach(([id]) => state.customerManagementUpdatingIds.add(id));
  renderCustomerManagementAtScroll(scrollLeft);
  let completed = false;
  try {
    for (const [id, draft] of pending) {
      const result = await api<{ record: CustomerManagementRecord }>(
        "/api/customer-management-save",
        {
          method: "POST",
          headers: {
            "x-customer-management-edit-token": state.customerManagementEditToken,
          },
          body: JSON.stringify({ id, ...draft }),
        },
      );
      if (state.customerManagementData) {
        state.customerManagementData = {
          ...state.customerManagementData,
          records: state.customerManagementData.records.map((candidate) =>
            candidate.id === id ? result.record : candidate
          ),
        };
      }
      state.customerManagementDrafts.delete(id);
      state.customerManagementUpdatingIds.delete(id);
    }
    completed = true;
    resetCustomerManagementEditAccess(true);
    notify("変更内容を記録しました。");
  } catch (error) {
    notify(error instanceof Error ? error.message : "変更内容を保存できませんでした。", true);
  } finally {
    state.customerManagementSaveBusy = false;
    state.customerManagementUpdatingIds.clear();
    if (!completed && state.customerManagementDrafts.size === 0) {
      resetCustomerManagementEditAccess(true);
    }
    renderCustomerManagementAtScroll(scrollLeft);
  }
}

function cancelCustomerManagementChanges(): void {
  if (state.customerManagementSaveBusy || !customerManagementRecordingActive()) return;
  const scrollLeft = customerManagementScrollLeft();
  resetCustomerManagementEditAccess(true);
  renderCustomerManagementAtScroll(scrollLeft);
  notify("記録を取り消しました。");
}

function openCustomerManagementEditDialog(): void {
  if (state.customerManagementSelectedIds.size === 0 || state.customerManagementUnlockBusy) return;
  const dialog = document.querySelector<HTMLDialogElement>("#customer-management-edit-dialog");
  if (!dialog) return;
  dialog.showModal();
  window.requestAnimationFrame(() => {
    dialog.querySelector<HTMLInputElement>('input[name="passcode"]')?.focus();
  });
}

async function unlockCustomerManagementEdits(form: HTMLFormElement): Promise<void> {
  if (state.customerManagementUnlockBusy) return;
  const recordIds = Array.from(state.customerManagementSelectedIds);
  if (recordIds.length === 0) return;
  const passcode = String(new FormData(form).get("passcode") ?? "");
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  state.customerManagementUnlockBusy = true;
  if (submit) {
    submit.disabled = true;
    submit.textContent = "認証中…";
  }
  try {
    const result = await api<{
      token: string;
      expiresAt: string;
      recordIds: string[];
    }>("/api/customer-management-unlock", {
      method: "POST",
      body: JSON.stringify({ passcode, recordIds }),
    });
    state.customerManagementEditToken = result.token;
    state.customerManagementEditExpiresAt = result.expiresAt;
    state.customerManagementUnlockedIds = new Set(
      result.recordIds.filter((id) => state.customerManagementSelectedIds.has(id)),
    );
    document.querySelector<HTMLDialogElement>("#customer-management-edit-dialog")?.close();
    notify("必要項目をチェックして「保存」を押してください。");
    renderCustomerManagementAtScroll(customerManagementScrollLeft());
  } catch (error) {
    const input = form.querySelector<HTMLInputElement>('input[name="passcode"]');
    if (input) {
      input.value = "";
      input.focus();
    }
    notify(error instanceof Error ? error.message : "暗証番号を確認できませんでした。", true);
  } finally {
    state.customerManagementUnlockBusy = false;
    if (submit) {
      submit.disabled = false;
      submit.textContent = "認証";
    }
  }
}

function toggleCustomerManagementSelection(input: HTMLInputElement): void {
  const id = input.dataset.customerSelect ?? "";
  if (!id) return;
  const scrollLeft = customerManagementScrollLeft();
  if (input.checked) {
    state.customerManagementSelectedIds.add(id);
  } else {
    state.customerManagementSelectedIds.delete(id);
    state.customerManagementUnlockedIds.delete(id);
  }
  if (state.customerManagementSelectedIds.size === 0) {
    resetCustomerManagementEditAccess();
  }
  renderCustomerManagementAtScroll(scrollLeft);
}

function statsCard(filter: ListFilter, color: string, icon: string, label: string, value: number): string {
  const historyFilter = orderFilterView(filter) === "history";
  const active = state.filter === filter
    && (historyFilter ? state.view === "history" : state.view === "active");
  const disabled = state.isWorkspaceSwitching || state.isFilterSwitching;
  return `<button class="stat-card${active ? " is-active" : ""}" data-filter="${filter}" type="button" ${disabled ? "disabled" : ""}>
    <span class="stat-icon ${color}">${icon}</span>
    <span><small>${label}</small><strong>${value}</strong><em>件</em></span>
  </button>`;
}

function filterLabel(): string {
  if (state.filter === "ended") return "終了データ履歴";
  if (state.filter === "received") return state.workspace === "all" ? "印刷履歴" : "入荷済み・リスト";
  if (state.filter === "shipped") return "出荷済み・リスト";
  if (state.filter === "hidden") return "非表示済・リスト";
  if (state.filter === "unnecessary") return "不要になった注文";
  if (state.view === "history") return "履歴";
  return state.workspace === "all" ? "日々注文リスト" : "対象注文リスト";
}

function openDailyUnnecessaryNotice(): void {
  const count = state.data?.stats.unnecessaryCount ?? 0;
  if (state.workspace !== "all" || state.etcWorkspace !== null || count <= 0) return;
  const dialog = document.querySelector<HTMLDialogElement>("#daily-unnecessary-notice-dialog");
  if (!dialog) return;
  dialog.innerHTML = `<article class="bulk-confirm-card daily-unnecessary-notice-card" aria-labelledby="daily-unnecessary-notice-title">
    <p class="receive-process-kicker">ORDER REVIEW NOTICE</p>
    <h2 id="daily-unnecessary-notice-title">不要になった注文${count}件があります。</h2>
    <p>印刷履歴にある注文のキャンセル状態がShopifyで更新されました。</p>
    <div class="bulk-confirm-actions">
      <button class="button secondary" type="button" data-daily-unnecessary-close>閉じる</button>
      <button class="button confirm-unnecessary" type="button" data-daily-unnecessary-open>不要になった注文へ移動</button>
    </div>
  </article>`;
  dialog.querySelector<HTMLButtonElement>("[data-daily-unnecessary-close]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  dialog.querySelector<HTMLButtonElement>("[data-daily-unnecessary-open]")?.addEventListener(
    "click",
    () => {
      dialog.close();
      void switchFilter("unnecessary");
    },
  );
  showLockedModal(dialog);
}

function dedicatedDateSortButton(): string {
  const mode = defaultOrderSortModeForFilter(state.filter);
  if (mode === "default") return "";
  const active = state.sortMode === mode;
  const label = mode === "arrival-desc"
    ? state.workspace === "repair" ? "後直し入荷日 最新順" : "入荷日 最新順"
    : "出荷依頼日 最新順";
  const tone = mode === "arrival-desc" ? " received-date" : " shipping-date";
  return `<button class="list-sort-toggle date-desc${tone}${active ? " is-active" : ""}" type="button" data-sort-mode="${mode}" aria-pressed="${active}">
    <span aria-hidden="true">↓</span> ${label}
  </button>`;
}

const bulkActionLabels: Record<BulkConfirmAction, string> = {
  receive: "入荷処理",
  reprint: "再印刷",
  ship: "出荷依頼",
  hide: "非表示",
  reset: "初期化",
  "bulk-delete": "一括削除",
  "repair-request": "依頼処理",
  "repair-print": "🖨️印刷",
  "confirm-unnecessary": "確認済みに変更",
};

interface HtmlPrintPreviewOptions {
  title: string;
  description: string;
  paper: HtmlPrintPaper;
  pagesHtml: string[];
  completionDescription?: string;
  listPrint?: { paper: HtmlPrintPaper; pagesHtml: string[]; description: string };
  labelSlots?: { renderPages: (usedSlots: readonly number[]) => string[] };
}

async function showHtmlPrintPreview(options: HtmlPrintPreviewOptions): Promise<boolean> {
  const dialog = document.querySelector<HTMLDialogElement>("#print-preview-dialog");
  const warningDialog = document.querySelector<HTMLDialogElement>("#html-print-cancel-warning-dialog");
  if (!dialog || !warningDialog) throw new Error("印刷プレビューを準備できませんでした。");
  if (options.pagesHtml.length === 0) throw new Error("印刷するページがありません。");

  const paper = htmlPrintPaperDefinition(options.paper);
  dialog.innerHTML = `<article class="html-print-preview-card" aria-labelledby="html-print-preview-title">
    <header class="html-print-preview-head">
      <div>
        <p>HTML PRINT PREVIEW</p>
        <h2 id="html-print-preview-title">${escapeHtml(options.title)}</h2>
        <span data-html-print-description>${escapeHtml(options.description)} · ${escapeHtml(paper.label)} · ${options.pagesHtml.length}ページ</span>
        ${options.labelSlots ? '<p>最初の用紙の使用済み枠にチェックしてください。チェックした枠を飛ばして配置します（チェックは印刷されません）。</p>' : ''}
      </div>
      <div class="html-print-preview-actions">
        <button class="button secondary" type="button" data-html-print-close>閉じる</button>
        <button class="button danger" type="button" data-html-print-cancel-warning hidden>取消</button>
        <button class="button primary" type="button" data-html-print-complete hidden>印刷完了</button>
        <button class="button daily-print" type="button" data-html-print-start>印刷</button>
        ${options.listPrint ? '<button class="button secondary" type="button" data-html-print-list>リスト印刷</button>' : ""}
      </div>
    </header>
    <div class="html-print-preview-confirmation" data-html-print-confirmation hidden>
      <strong>正常に印刷できましたか？</strong>
      <span>${escapeHtml(options.completionDescription ?? "履歴や処理状況は「印刷完了」を押した後に更新します。")}</span>
    </div>
    <div class="html-print-preview-pages ${paper.previewClassName}">
      ${options.pagesHtml.map((pageHtml, index) =>
        `<section class="html-print-preview-sheet" aria-label="${index + 1}ページ目">${pageHtml}</section>`
      ).join("")}
    </div>
  </article>`;

  const closeButton = dialog.querySelector<HTMLButtonElement>("[data-html-print-close]");
  const printButton = dialog.querySelector<HTMLButtonElement>("[data-html-print-start]");
  const listPrintButton = dialog.querySelector<HTMLButtonElement>("[data-html-print-list]");
  const cancelWarningButton = dialog.querySelector<HTMLButtonElement>("[data-html-print-cancel-warning]");
  const completeButton = dialog.querySelector<HTMLButtonElement>("[data-html-print-complete]");
  const confirmation = dialog.querySelector<HTMLElement>("[data-html-print-confirmation]");
  if (!closeButton || !printButton || !cancelWarningButton || !completeButton || !confirmation) {
    throw new Error("印刷プレビューの操作ボタンを準備できませんでした。");
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let cancellationRequested = false;
    let canComplete = false;
    const finish = (printed: boolean): void => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener("cancel", cancelPreview);
      dialog.removeEventListener("close", closePreview);
      warningDialog.removeEventListener("close", closeWarningPreview);
      if (warningDialog.open) warningDialog.close();
      if (dialog.open) dialog.close();
      resolve(printed);
    };
    const cancelPreview = (event: Event): void => {
      event.preventDefault();
      finish(false);
    };
    const closePreview = (): void => { if (!dialog.open) finish(false); };
    const closeWarningPreview = (): void => { if (cancellationRequested && !warningDialog.open) finish(false); };

    closeButton.addEventListener("click", () => finish(false));
    completeButton.addEventListener("click", () => { if (canComplete) finish(true); });
    cancelWarningButton.addEventListener("click", () => {
      if (!canComplete || warningDialog.open) return;
      cancellationRequested = true;
      warningDialog.innerHTML = `<article class="bulk-confirm-card" aria-labelledby="html-print-cancel-warning-title">
        <p class="receive-process-kicker">PRINT UPDATE WARNING</p>
        <h2 id="html-print-cancel-warning-title">更新処理できません。</h2>
        <div class="bulk-confirm-actions">
          <button class="button primary" type="button" data-html-print-cancel-warning-close>閉じる</button>
        </div>
      </article>`;
      warningDialog.querySelector<HTMLButtonElement>("[data-html-print-cancel-warning-close]")
        ?.addEventListener("click", () => warningDialog.close());
      showLockedModal(warningDialog);
    });
    const renderPrintFormat = (format: Pick<HtmlPrintPreviewOptions, "paper" | "pagesHtml" | "description">): void => {
        const definition = htmlPrintPaperDefinition(format.paper);
        const pages = dialog.querySelector<HTMLElement>(".html-print-preview-pages")!;
        pages.className = `html-print-preview-pages ${definition.previewClassName}`;
        pages.innerHTML = format.pagesHtml.map((html, index) =>
          `<section class="html-print-preview-sheet" aria-label="${index + 1}ページ目">${html}</section>`).join("");
        dialog.querySelector<HTMLElement>("[data-html-print-description]")!.textContent =
          `${format.description} · ${definition.label} · ${format.pagesHtml.length}ページ`;
        // Fit long names/SKUs on one line without dropping any printed text.
        for (const text of pages.querySelectorAll<HTMLElement>("[data-print-fit]")) {
          const cell = text.parentElement!;
          const cellStyle = getComputedStyle(cell);
          const available = cell.clientWidth - parseFloat(cellStyle.paddingLeft) - parseFloat(cellStyle.paddingRight);
          const width = text.getBoundingClientRect().width;
          if (width > available && available > 0) text.style.transform = `scaleX(${available / width})`;
        }
    };
    const usedLabelSlots = new Set<number>();
    dialog.querySelector(".html-print-preview-pages")?.addEventListener("change", (event) => {
      const input = event.target;
      if (settled || printButton.disabled || !options.labelSlots || !(input instanceof HTMLInputElement) || !input.matches('[data-label-used-slot]')) return;
      const slot = Number(input.dataset.labelUsedSlot);
      if (!Number.isInteger(slot) || slot < 1 || slot > 18) return;
      if (input.checked) usedLabelSlots.add(slot); else usedLabelSlots.delete(slot);
      options.pagesHtml = options.labelSlots.renderPages([...usedLabelSlots]);
      renderPrintFormat(options);
      dialog.querySelector<HTMLInputElement>(`[data-label-used-slot="${slot}"]`)?.focus({ preventScroll: true });
      // Changing layout invalidates the previous print; require printing again.
      canComplete = false;
      closeButton.hidden = false;
      cancelWarningButton.hidden = completeButton.hidden = confirmation.hidden = true;
    });
    const printFormat = (format: Pick<HtmlPrintPreviewOptions, "paper" | "pagesHtml" | "description">, updatesState = true): void => {
      canComplete = false;
      if (options.listPrint || options.labelSlots) renderPrintFormat(format);
      const pageStyle = document.createElement("style");
      pageStyle.dataset.htmlPrintPageRule = format.paper;
      pageStyle.textContent = htmlPrintPageRule(format.paper);
      document.head.appendChild(pageStyle);
      document.body.classList.add("html-printing");
      printButton.disabled = true;
      if (listPrintButton) listPrintButton.disabled = true;
      printButton.textContent = "印刷画面を表示中…";
      cancelWarningButton.hidden = true;
      completeButton.hidden = true;
      confirmation.hidden = true;
      try {
        window.print();
      } finally {
        document.body.classList.remove("html-printing");
        pageStyle.remove();
        printButton.disabled = false;
        if (listPrintButton) listPrintButton.disabled = false;
        printButton.textContent = "印刷";
        canComplete = updatesState;
        closeButton.hidden = updatesState;
        cancelWarningButton.hidden = !updatesState;
        completeButton.hidden = !updatesState;
        confirmation.hidden = !updatesState;
        if (updatesState) confirmation.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    };
    printButton.addEventListener("click", () => printFormat(options));
    listPrintButton?.addEventListener("click", () => {
      if (options.listPrint) printFormat(options.listPrint, false);
    });
    dialog.addEventListener("cancel", cancelPreview);
    dialog.addEventListener("close", closePreview);
    warningDialog.addEventListener("close", closeWarningPreview);
    showLockedModal(dialog);
  });
}

function executeBulkAction(action: BulkConfirmAction): void {
  if (action === "ship") void updateSelectedStatus("出荷依頼");
  if (action === "hide") void updateSelectedStatus("非表示済");
  if (action === "reset") void updateSelectedStatus("");
  if (action === "bulk-delete") void deleteSelectedHistoryOrders();
  if (action === "repair-request") void updateSelectedRepairRequest();
  if (action === "confirm-unnecessary") void confirmSelectedDailyUnnecessaryOrders();
}

type ReceiveProcessLogTone = "running" | "success" | "error";

function appendReceiveProcessLog(
  list: HTMLOListElement,
  message: string,
  tone: ReceiveProcessLogTone,
): HTMLLIElement {
  const item = document.createElement("li");
  item.className = `receive-process-log ${tone}`;
  const text = document.createElement("span");
  text.textContent = message;
  item.appendChild(text);
  list.appendChild(item);
  item.scrollIntoView({ block: "nearest" });
  return item;
}

function updateReceiveProcessLog(
  item: HTMLLIElement,
  message: string,
  tone: ReceiveProcessLogTone,
): void {
  item.className = `receive-process-log ${tone}`;
  const text = item.querySelector("span");
  if (text) text.textContent = message;
}

async function requestOrderStatusUpdate(
  orderIds: string[],
  status: CsOrderStatus,
): Promise<{
  updated: number;
  status: string;
  chatNotifications: { sent: number; failed: number } | null;
}> {
  return api("/api/orders/status", {
    method: "POST",
    headers: status === "" ? resetHeaders() : undefined,
    body: JSON.stringify({ orderIds, status, workspace: state.workspace }),
  });
}

async function requestRepairInstockDateUpdate(orderIds: string[]): Promise<{
  updated: number;
  repairInstockDate: string | null;
}> {
  return api("/api/orders/status", {
    method: "POST",
    body: JSON.stringify({
      orderIds,
      status: null,
      workspace: "repair",
      repairInstockOnly: true,
    }),
  });
}

async function requestOrderRecordCorrection(
  orderIds: string[],
  status: CsOrderStatus | null,
  itemArrivalDate?: string,
  shippingRequestDate?: string,
  repairAtelier?: string,
  repairRequestDate?: string,
): Promise<{
  updated: number;
  status: string | null;
  itemArrivalDate: string | null;
  shippingRequest: string | null;
  shippingRequestDate: string | null;
  repairInstockDate: string | null;
  repairAtelier: string | null;
  repairRequestDate: string | null;
}> {
  const requiresResetAccess = status === ""
    || itemArrivalDate === ""
    || shippingRequestDate === ""
    || repairAtelier === ""
    || repairRequestDate === "";
  return api("/api/orders/status", {
    method: "POST",
    headers: requiresResetAccess ? resetHeaders() : undefined,
    body: JSON.stringify({
      orderIds,
      status,
      ...(state.workspace === "repair"
        ? { repairInstockDate: itemArrivalDate, repairAtelier, repairRequestDate }
        : { itemArrivalDate }),
      shippingRequestDate,
      workspace: state.workspace,
      correction: true,
    }),
  });
}

function isDailyOrderPrintHistoryEligible(
  order: DashboardOrder,
  printedAt: Date,
): boolean {
  return order.recordType === "order"
    && order.csOrderStatus !== "印刷完了"
    && order.csOrderStatus !== "入荷完了"
    && !isShippingRequestStatus(order.csOrderStatus)
    && order.csOrderStatus !== "非表示済"
    && !isShopifyFulfilled(order.shopifyFulfillmentStatus)
    && shouldDisplayDailyOrderListOrder(order.items)
    && isDailyOrderPrintEligible(order.createdAt, printedAt);
}

function dailyOrderPrintEligibleOrders(printedAt: Date): DashboardOrder[] {
  if (
    state.workspace !== "all"
    || state.view !== "active"
    || state.filter !== "all"
    || !state.data
  ) return [];
  const eligible = state.data.orders.filter((order) =>
    isDailyOrderPrintHistoryEligible(order, printedAt)
  );
  return sortDashboardOrders(eligible, {
    mode: state.sortMode,
    expressFirst: false,
    workspace: "all",
  });
}

function selectedDailyOrderPrintOrders(): DashboardOrder[] {
  if (!state.data) return [];
  const selected = state.data.orders.filter((order) =>
    order.recordType === "order" && state.checkedOrderIds.has(order.id)
  );
  return sortDashboardOrders(selected, {
    mode: state.sortMode,
    expressFirst: false,
    workspace: "all",
  });
}

interface DailyOrderPrintContext {
  createdAt: Date;
  mode: DailyOrderPrintMode;
  orders: DashboardOrder[];
  moveToHistory: boolean;
}

interface DailyOrderPrintSelection {
  orders: DashboardOrder[];
  eligibleExcludedOrders: DashboardOrder[];
  moveEligibleExcludedToHistory: boolean;
}

function dailyOrderPrintContext(createdAt: Date): DailyOrderPrintContext | null {
  if (state.workspace !== "all") return null;
  const mode = resolveDailyOrderPrintMode(
    state.checkedOrderIds.size,
    state.view,
    state.filter,
  );
  if (!mode) return null;
  const orders = mode === "scheduled"
    ? dailyOrderPrintEligibleOrders(createdAt)
    : selectedDailyOrderPrintOrders();
  if (orders.length === 0) return null;
  return { createdAt, mode, orders, moveToHistory: mode !== "reprint" };
}

function dailyOrderPrintSourceOptionHtml(
  sourceName: DailyOrderPrintSalesSource,
  orders: DashboardOrder[],
): string {
  const tone = sourceName === "大阪" ? "osaka" : sourceName === "東京" ? "tokyo" : "web";
  const count = orders.filter((order) => order.salesSource === sourceName).length;
  return `<label class="daily-print-source-option ${tone}">
    <input type="checkbox" value="${escapeHtml(sourceName)}" data-daily-print-sales-source checked>
    <span>${escapeHtml(sourceName)}<small>${count}件</small></span>
  </label>`;
}

function openDailyOrderPrintConfirmation(): void {
  if (state.isBulkUpdating) return;
  const dialog = document.querySelector<HTMLDialogElement>("#bulk-confirm-dialog");
  if (!dialog) {
    notify("印刷画面を準備できませんでした。", true);
    return;
  }
  const context = dailyOrderPrintContext(new Date());
  if (!context) {
    notify(state.view === "history"
      ? "再印刷する注文を選択してください。"
      : "注文日時が本日14:02より前の未印刷注文はありません。");
    return;
  }

  const selectionPrint = context.mode !== "scheduled";
  const reprint = context.mode === "reprint";
  dialog.innerHTML = `<form class="bulk-confirm-card daily-print-confirm-card" aria-labelledby="daily-print-confirm-title">
    <p class="receive-process-kicker">${reprint ? "DAILY ORDER REPRINT" : "DAILY ORDER PRINT"}</p>
    <h2 id="daily-print-confirm-title">印刷しますか？</h2>
    <p>${reprint
      ? `印刷履歴で選択した${context.orders.length}件から、販売元を選んで再印刷します。履歴の記録内容は変更しません。`
      : selectionPrint
        ? `選択した${context.orders.length}件から、販売元を選んで印刷プレビューを表示します。`
        : `注文日時が本日14:02より前の未印刷注文${context.orders.length}件から、販売元を選んで印刷プレビューを表示します。`}</p>
    <fieldset class="daily-print-source-fieldset">
      <legend>印刷する【販売元】を選択してください。</legend>
      <div class="daily-print-source-options">
        ${DAILY_ORDER_PRINT_SALES_SOURCES.map((sourceName) =>
          dailyOrderPrintSourceOptionHtml(sourceName, context.orders)
        ).join("")}
      </div>
    </fieldset>
    ${context.moveToHistory
      ? `<label class="daily-print-move-excluded">
          <input type="checkbox" data-daily-print-move-excluded>
          <span>対象外注文を印刷せず印刷履歴に移動させる</span>
          <small>未印刷・未出荷など、通常の印刷履歴移動条件に当てはまる注文だけが対象です。</small>
        </label>`
      : ""}
    <ul class="bulk-confirm-orders" data-daily-print-order-list></ul>
    <div class="bulk-confirm-count" data-daily-print-count aria-live="polite"></div>
    <div class="bulk-confirm-actions">
      <button class="button secondary" type="button" data-daily-print-cancel>キャンセル</button>
      <button class="button daily-print" type="button" data-daily-print-confirm>プレビュー表示</button>
    </div>
  </form>`;

  const sourceCheckboxes = Array.from(
    dialog.querySelectorAll<HTMLInputElement>("[data-daily-print-sales-source]"),
  );
  const moveExcludedCheckbox = dialog.querySelector<HTMLInputElement>("[data-daily-print-move-excluded]");
  const orderList = dialog.querySelector<HTMLUListElement>("[data-daily-print-order-list]");
  const count = dialog.querySelector<HTMLElement>("[data-daily-print-count]");
  const confirmButton = dialog.querySelector<HTMLButtonElement>("[data-daily-print-confirm]");
  if (!orderList || !count || !confirmButton) {
    notify("印刷画面を準備できませんでした。", true);
    return;
  }

  const currentSelection = (): DailyOrderPrintSelection => {
    const selectedSources = sourceCheckboxes
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value as DailyOrderPrintSalesSource);
    const { included, excluded } = filterDailyOrderPrintOrdersBySalesSource(
      context.orders,
      selectedSources,
    );
    const eligibleExcludedOrders = context.moveToHistory
      ? excluded.filter((order) => isDailyOrderPrintHistoryEligible(order, context.createdAt))
      : [];
    return {
      orders: included,
      eligibleExcludedOrders,
      moveEligibleExcludedToHistory: Boolean(moveExcludedCheckbox?.checked),
    };
  };

  const updateSelection = (): void => {
    const selection = currentSelection();
    orderList.innerHTML = selection.orders.length > 0
      ? selection.orders.map(
        (order) => `<li>${escapeHtml(order.name)} · ${escapeHtml(order.customerName || "ゲスト注文")} 様</li>`,
      ).join("")
      : '<li class="daily-print-no-orders">印刷対象の注文がありません。</li>';
    const excludedCount = context.orders.length - selection.orders.length;
    const moveCount = selection.moveEligibleExcludedToHistory
      ? selection.eligibleExcludedOrders.length
      : 0;
    count.innerHTML = `印刷対象 <strong>${selection.orders.length}件</strong> / 対象外 ${excludedCount}件${moveCount > 0
      ? ` · 対象外から印刷履歴へ ${moveCount}件`
      : ""}`;
    confirmButton.disabled = selection.orders.length === 0;
  };

  for (const checkbox of sourceCheckboxes) {
    checkbox.addEventListener("change", updateSelection);
  }
  moveExcludedCheckbox?.addEventListener("change", updateSelection);
  dialog.querySelector<HTMLButtonElement>("[data-daily-print-cancel]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  confirmButton.addEventListener("click", () => {
    const selection = currentSelection();
    if (selection.orders.length === 0) return;
    confirmButton.disabled = true;
    void runDailyOrderPrintProcess(context, selection, dialog);
  }, { once: true });
  updateSelection();
  showLockedModal(dialog);
}
async function runDailyOrderPrintProcess(
  context: DailyOrderPrintContext,
  selection: DailyOrderPrintSelection,
  dialog: HTMLDialogElement,
): Promise<void> {
  if (state.isBulkUpdating || selection.orders.length === 0) return;
  const { createdAt, moveToHistory } = context;
  const selectedOrders = selection.orders;
  const reprint = context.mode === "reprint";
  const historyOrderIds = moveToHistory
    ? dailyOrderPrintHistoryOrderIds(
      selectedOrders,
      selection.eligibleExcludedOrders,
      selection.moveEligibleExcludedToHistory,
    )
    : [];
  const printedOrderIds = new Set(selectedOrders.map((order) => order.id));
  const excludedHistoryCount = historyOrderIds.filter((id) => !printedOrderIds.has(id)).length;
  let printConfirmed = false;

  dialog.close();
  state.isBulkUpdating = true;
  render();
  try {
    const pagesHtml = buildDailyOrderHtmlPrintPages(
      selectedOrders,
      createdAt,
      dailyOrderPrintConditionLabel(context.mode),
    );
    state.isBulkUpdating = false;
    render();
    printConfirmed = await showHtmlPrintPreview({
      title: reprint ? "日々注文リスト・再印刷" : "日々注文リスト",
      description: `対象${selectedOrders.length}件${excludedHistoryCount > 0 ? `・印刷せず履歴へ移動 ${excludedHistoryCount}件` : ""}`,
      paper: "a4-landscape",
      pagesHtml,
    });
    if (!printConfirmed) return;

    state.isBulkUpdating = true;
    render();
    if (moveToHistory && historyOrderIds.length > 0) {
      const result = await requestOrderStatusUpdate(historyOrderIds, "印刷完了");
      notify(`${result.updated}件を「印刷履歴」へ移動しました。`);
    } else {
      notify("再印刷が完了しました。印刷履歴の記録内容は変更していません。");
    }
    state.checkedOrderIds.clear();
    await loadData(false);
  } catch (error) {
    const message = error instanceof Error ? error.message : "日々注文リストを印刷できませんでした。";
    notify(
      printConfirmed && moveToHistory
        ? `印刷は完了しましたが、印刷履歴へ移動できませんでした: ${message}`
        : message,
      true,
    );
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function runDailyOrderPrintProcessLegacy(
  context: DailyOrderPrintContext,
  selection: DailyOrderPrintSelection,
  dialog: HTMLDialogElement,
): Promise<void> {
  if (state.isBulkUpdating || selection.orders.length === 0) return;
  const { createdAt, moveToHistory } = context;
  const selectedOrders = selection.orders;
  const reprint = context.mode === "reprint";
  const historyOrderIds = moveToHistory
    ? dailyOrderPrintHistoryOrderIds(
      selectedOrders,
      selection.eligibleExcludedOrders,
      selection.moveEligibleExcludedToHistory,
    )
    : [];
  const printedOrderIds = new Set(selectedOrders.map((order) => order.id));
  const excludedHistoryCount = historyOrderIds.filter((id) => !printedOrderIds.has(id)).length;

  state.isBulkUpdating = true;
  dialog.innerHTML = `<article class="receive-process-card" aria-labelledby="daily-print-process-title">
    <p class="receive-process-kicker">${reprint ? "DAILY ORDER REPRINT" : "DAILY ORDER PRINT"}</p>
    <h2 id="daily-print-process-title">${reprint ? "日々注文リストPDFを再作成中" : "日々注文リストPDFを作成中"}</h2>
    <p class="receive-process-summary">${reprint
      ? `印刷履歴から販売元で絞り込んだ${selectedOrders.length}件を、A4横向きPDFへまとめて再ダウンロードします。履歴の記録内容は変更しません。`
      : `販売元で絞り込んだ${selectedOrders.length}件をA4横向きPDFへまとめてダウンロードします。完了後は${historyOrderIds.length}件を印刷履歴へ移動します。${excludedHistoryCount > 0 ? `（うちPDF対象外 ${excludedHistoryCount}件）` : ""}`}</p>
    <details class="receive-process-orders">
      <summary>PDF対象注文 ${selectedOrders.length}件を確認</summary>
      <ul>${selectedOrders.map((order) => `<li>${escapeHtml(order.name)} · ${escapeHtml(order.customerName || "ゲスト注文")} 様</li>`).join("")}</ul>
    </details>
    <ol class="receive-process-logs" aria-live="polite"></ol>
    <div class="receive-process-actions">
      <button class="button secondary" type="button" data-receive-process-close disabled>処理中…</button>
    </div>
  </article>`;
  const logList = dialog.querySelector<HTMLOListElement>(".receive-process-logs");
  const title = dialog.querySelector<HTMLHeadingElement>("#daily-print-process-title");
  const closeButton = dialog.querySelector<HTMLButtonElement>("[data-receive-process-close]");
  if (!logList || !title || !closeButton) {
    state.isBulkUpdating = false;
    notify("印刷画面を準備できませんでした。", true);
    return;
  }

  const preventCloseWhileRunning = (event: Event): void => {
    if (state.isBulkUpdating) event.preventDefault();
  };
  dialog.addEventListener("cancel", preventCloseWhileRunning);
  dialog.addEventListener("close", () => {
    dialog.removeEventListener("cancel", preventCloseWhileRunning);
    state.isBulkUpdating = false;
    void loadData(false);
  }, { once: true });

  let pdfDownloaded = false;
  let printCompleted = false;
  try {
    const pdfLog = appendReceiveProcessLog(logList, `対象${selectedOrders.length}件のA4横向きPDF作成を開始しました`, "running");
    const pageLog = appendReceiveProcessLog(logList, "一覧をA4ページへ分割しています…", "running");
    const pdfResult = await buildDailyOrderPrintPdf(
      selectedOrders,
      createdAt,
      (current, total) => {
        updateReceiveProcessLog(pageLog, `PDFページを作成中（${current} / ${total}ページ）`, "running");
      },
      dailyOrderPrintConditionLabel(context.mode),
    );
    updateReceiveProcessLog(pageLog, `${pdfResult.pageCount}ページを1つのPDFファイルへまとめました`, "success");
    updateReceiveProcessLog(pdfLog, `A4横向きPDFを${pdfResult.pageCount}ページ作成しました`, "success");

    downloadReceivePdf(pdfResult.blob, pdfResult.filename);
    pdfDownloaded = true;
    appendReceiveProcessLog(logList, `${pdfResult.filename} のダウンロードを開始しました`, "success");

    if (moveToHistory && historyOrderIds.length > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
      const statusLog = appendReceiveProcessLog(
        logList,
        excludedHistoryCount > 0
          ? `PDF対象${selectedOrders.length}件と、条件を満たすPDF対象外${excludedHistoryCount}件の印刷済みを記録しています…`
          : "ShopifyメタフィールドとD1へ印刷済みを記録しています…",
        "running",
      );
      const result = await requestOrderStatusUpdate(historyOrderIds, "印刷完了");
      updateReceiveProcessLog(statusLog, `${result.updated}件を「印刷履歴」へ移動しました`, "success");
    } else {
      appendReceiveProcessLog(logList, "印刷履歴の記録内容は変更していません。", "success");
    }
    appendReceiveProcessLog(logList, `日々注文リストの${reprint ? "再印刷" : "印刷"}処理が完了しました。`, "success");
    title.textContent = `日々注文リスト${reprint ? "再印刷" : "印刷"}完了`;
    state.checkedOrderIds.clear();
    printCompleted = true;
  } catch (error) {
    const message = error instanceof Error ? error.message : "日々注文リストPDFを作成できませんでした。";
    appendReceiveProcessLog(
      logList,
      pdfDownloaded
        ? moveToHistory && historyOrderIds.length > 0
          ? `PDFはダウンロード済みですが、印刷履歴への移動に失敗しました: ${message}`
          : `PDFはダウンロード済みですが、再印刷処理でエラーが発生しました: ${message}`
        : `PDF作成に失敗したため、印刷履歴への移動は行っていません: ${message}`,
      "error",
    );
    title.textContent = "日々注文リスト印刷エラー";
  } finally {
    state.isBulkUpdating = false;
    closeButton.className = "button primary";
    if (printCompleted) {
      closeButton.disabled = true;
      closeButton.textContent = reprint ? "一覧を更新中…" : "印刷履歴を更新中…";
      window.setTimeout(() => {
        if (dialog.open) dialog.close();
      }, 650);
    } else {
      closeButton.disabled = false;
      closeButton.textContent = "閉じる";
      closeButton.addEventListener("click", () => dialog.close(), { once: true });
    }
  }
}

async function runReceivePdfProcess(
  selectedOrders: DashboardOrder[],
  dialog: HTMLDialogElement,
  mode: "receive" | "reprint",
): Promise<void> {
  if (selectedOrders.length === 0 || state.isBulkUpdating) return;
  const isReprint = mode === "reprint";
  let printConfirmed = false;

  dialog.close();
  state.isBulkUpdating = true;
  render();
  try {
    const pagesHtml = await buildReceiveHtmlPrintPages(selectedOrders, "size-order");
    state.isBulkUpdating = false;
    render();
    printConfirmed = await showHtmlPrintPreview({
      title: isReprint ? "サイズオーダー・再印刷" : "サイズオーダー入荷",
      description: `対象注文 ${selectedOrders.length}件`,
      completionDescription: isReprint ? "再印刷では入荷日や処理状況は変更しません。" : undefined,
      paper: "b5-portrait",
      pagesHtml,
    });
    if (!printConfirmed) return;

    state.isBulkUpdating = true;
    render();
    if (isReprint) {
      notify("再印刷が完了しました。処理状況は変更していません。");
    } else {
      const result = await requestOrderStatusUpdate(
        selectedOrders.map((order) => order.id),
        "入荷完了",
      );
      notify(`${result.updated}件の「入荷完了」と入荷日を記録しました。`);
    }
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
    await loadData(false);
  } catch (error) {
    const message = error instanceof Error ? error.message : "サイズオーダーを印刷できませんでした。";
    notify(
      printConfirmed && !isReprint
        ? `印刷は完了しましたが、入荷済みを記録できませんでした: ${message}`
        : message,
      true,
    );
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function runReceivePdfProcessLegacy(
  selectedOrders: DashboardOrder[],
  dialog: HTMLDialogElement,
  mode: "receive" | "reprint",
): Promise<void> {
  if (selectedOrders.length === 0 || state.isBulkUpdating) return;
  state.isBulkUpdating = true;
  const isReprint = mode === "reprint";
  const createdAt = new Date();
  const arrivalDateLabel = state.workspace === "repair" ? "後直し入荷日" : "入荷日";
  dialog.innerHTML = `<article class="receive-process-card" aria-labelledby="receive-process-title">
    <p class="receive-process-kicker">${isReprint ? "REPRINT PROCESS" : "RECEIVING PROCESS"}</p>
    <h2 id="receive-process-title">${isReprint ? "再印刷PDFを作成中" : "入荷処理を実行中"}</h2>
    <p class="receive-process-summary">${isReprint
      ? `選択した入荷済み注文${selectedOrders.length}件をB5 PDFへまとめて再ダウンロードします。処理状況は変更しません。`
      : `選択した${selectedOrders.length}件をB5 PDFへまとめ、ダウンロード後に入荷済みを記録します。`}</p>
    <details class="receive-process-orders">
      <summary>対象注文 ${selectedOrders.length}件を確認</summary>
      <ul>${selectedOrders.map((order) => `<li>${escapeHtml(order.name)}</li>`).join("")}</ul>
    </details>
    <ol class="receive-process-logs" aria-live="polite"></ol>
    <div class="receive-process-actions">
      <button class="button secondary" type="button" data-receive-process-close disabled>処理中…</button>
    </div>
  </article>`;
  const logList = dialog.querySelector<HTMLOListElement>(".receive-process-logs");
  const title = dialog.querySelector<HTMLHeadingElement>("#receive-process-title");
  const closeButton = dialog.querySelector<HTMLButtonElement>("[data-receive-process-close]");
  if (!logList || !title || !closeButton) {
    state.isBulkUpdating = false;
    dialog.close();
    notify(isReprint ? "再印刷画面を準備できませんでした。" : "入荷処理画面を準備できませんでした。", true);
    return;
  }

  const preventCloseWhileRunning = (event: Event): void => {
    if (state.isBulkUpdating) event.preventDefault();
  };
  dialog.addEventListener("cancel", preventCloseWhileRunning);
  dialog.addEventListener("close", () => {
    dialog.removeEventListener("cancel", preventCloseWhileRunning);
    state.isBulkUpdating = false;
    void loadData(false);
  }, { once: true });

  let pdfDownloaded = false;
  try {
    const pdfLog = appendReceiveProcessLog(
      logList,
      `選択した${selectedOrders.length}件のB5 PDF作成を開始しました`,
      "running",
    );
    const pageLog = appendReceiveProcessLog(logList, "内容量を確認してページを分割しています…", "running");
    const pdfResult = await buildReceivePdf(
      selectedOrders,
      createdAt,
      (current, total, orderName) => {
        updateReceiveProcessLog(
          pageLog,
          `${orderName} のPDFページを作成中（${current} / ${total}ページ）`,
          "running",
        );
      },
    );
    updateReceiveProcessLog(
      pageLog,
      `${pdfResult.pageCount}ページを1つのPDFファイルへまとめました`,
      "success",
    );
    updateReceiveProcessLog(
      pdfLog,
      `B5 PDFを${pdfResult.pageCount}ページ作成しました`,
      "success",
    );

    downloadReceivePdf(pdfResult.blob, pdfResult.filename);
    pdfDownloaded = true;
    appendReceiveProcessLog(
      logList,
      `${pdfResult.filename} の${isReprint ? "再ダウンロード" : "ダウンロード"}を開始しました`,
      "success",
    );
    if (isReprint) {
      appendReceiveProcessLog(logList, "再印刷用PDFを作成しました。処理状況は変更していません。", "success");
      title.textContent = "再印刷PDF作成完了";
    } else {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
      const statusLog = appendReceiveProcessLog(
        logList,
        `ShopifyメタフィールドとD1へ「入荷完了」と${arrivalDateLabel}を記録しています…`,
        "running",
      );
      const result = await requestOrderStatusUpdate(
        selectedOrders.map((order) => order.id),
        "入荷完了",
      );
      updateReceiveProcessLog(
        statusLog,
        `${result.updated}件の「入荷完了」と${arrivalDateLabel}を記録しました`,
        "success",
      );
      appendReceiveProcessLog(logList, "入荷処理完了しました。", "success");
      title.textContent = "入荷処理完了";
    }
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : isReprint
        ? "再印刷PDFを作成できませんでした。"
        : "入荷処理を完了できませんでした。";
    appendReceiveProcessLog(
      logList,
      isReprint
        ? `再印刷PDFの作成に失敗しました: ${message}`
        : pdfDownloaded
          ? `PDFはダウンロード済みですが、入荷済みの記録に失敗しました: ${message}`
          : `PDF作成に失敗したため、入荷済みの記録は行っていません: ${message}`,
      "error",
    );
    title.textContent = isReprint ? "再印刷エラー" : "入荷処理エラー";
  } finally {
    state.isBulkUpdating = false;
    closeButton.disabled = false;
    closeButton.textContent = "閉じる";
    closeButton.className = "button primary";
    closeButton.addEventListener("click", () => dialog.close(), { once: true });
  }
}

async function runRepairPrintPdfProcess(
  selectedOrders: DashboardOrder[],
  dialog: HTMLDialogElement,
): Promise<void> {
  if (selectedOrders.length === 0 || state.isBulkUpdating || state.workspace !== "repair") return;
  dialog.close();
  state.isBulkUpdating = true;
  render();
  try {
    const pagesHtml = await buildReceiveHtmlPrintPages(selectedOrders, "repair");
    state.isBulkUpdating = false;
    render();
    const printConfirmed = await showHtmlPrintPreview({
      title: "お直し指示書",
      description: `対象注文 ${selectedOrders.length}件`,
      paper: "b5-portrait",
      pagesHtml,
    });
    if (!printConfirmed) return;
    state.checkedOrderIds.clear();
    notify("お直し指示書の印刷が完了しました。注文の記録内容は変更していません。");
  } catch (error) {
    notify(error instanceof Error ? error.message : "お直し指示書を印刷できませんでした。", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function runRepairPrintPdfProcessLegacy(
  selectedOrders: DashboardOrder[],
  dialog: HTMLDialogElement,
): Promise<void> {
  if (selectedOrders.length === 0 || state.isBulkUpdating || state.workspace !== "repair") return;
  state.isBulkUpdating = true;
  const createdAt = new Date();
  dialog.innerHTML = `<article class="receive-process-card" aria-labelledby="repair-print-process-title">
    <p class="receive-process-kicker">REPAIR PRINT PROCESS</p>
    <h2 id="repair-print-process-title">印刷用B5 PDFを作成中</h2>
    <p class="receive-process-summary">選択した${selectedOrders.length}件を印刷用B5 PDFへまとめてダウンロードします。注文の記録内容は変更しません。</p>
    <details class="receive-process-orders">
      <summary>対象注文 ${selectedOrders.length}件を確認</summary>
      <ul>${selectedOrders.map((order) => `<li>${escapeHtml(order.name)}</li>`).join("")}</ul>
    </details>
    <ol class="receive-process-logs" aria-live="polite"></ol>
    <div class="receive-process-actions">
      <button class="button secondary" type="button" data-receive-process-close disabled>処理中…</button>
    </div>
  </article>`;
  const logList = dialog.querySelector<HTMLOListElement>(".receive-process-logs");
  const title = dialog.querySelector<HTMLHeadingElement>("#repair-print-process-title");
  const closeButton = dialog.querySelector<HTMLButtonElement>("[data-receive-process-close]");
  if (!logList || !title || !closeButton) {
    state.isBulkUpdating = false;
    dialog.close();
    notify("印刷画面を準備できませんでした。", true);
    return;
  }

  const preventCloseWhileRunning = (event: Event): void => {
    if (state.isBulkUpdating) event.preventDefault();
  };
  dialog.addEventListener("cancel", preventCloseWhileRunning);
  dialog.addEventListener("close", () => {
    dialog.removeEventListener("cancel", preventCloseWhileRunning);
    state.isBulkUpdating = false;
    render();
  }, { once: true });

  try {
    const pdfLog = appendReceiveProcessLog(
      logList,
      `選択した${selectedOrders.length}件のB5 PDF作成を開始しました`,
      "running",
    );
    const pageLog = appendReceiveProcessLog(logList, "内容量を確認してページを分割しています…", "running");
    const pdfResult = await buildReceivePdf(
      selectedOrders,
      createdAt,
      (current, total, orderName) => {
        updateReceiveProcessLog(
          pageLog,
          `${orderName} のPDFページを作成中（${current} / ${total}ページ）`,
          "running",
        );
      },
      formatRepairPrintPdfFilename(createdAt),
      "repair",
    );
    updateReceiveProcessLog(
      pageLog,
      `${pdfResult.pageCount}ページを1つのPDFファイルへまとめました`,
      "success",
    );
    updateReceiveProcessLog(
      pdfLog,
      `B5 PDFを${pdfResult.pageCount}ページ作成しました`,
      "success",
    );
    downloadReceivePdf(pdfResult.blob, pdfResult.filename);
    appendReceiveProcessLog(
      logList,
      `${pdfResult.filename} のダウンロードを開始しました`,
      "success",
    );
    appendReceiveProcessLog(logList, "印刷用B5 PDFを作成しました。注文の記録内容は変更していません。", "success");
    title.textContent = "印刷用B5 PDF作成完了";
    state.checkedOrderIds.clear();
  } catch (error) {
    const message = error instanceof Error ? error.message : "印刷用B5 PDFを作成できませんでした。";
    appendReceiveProcessLog(
      logList,
      `印刷用B5 PDFの作成に失敗しました: ${message}`,
      "error",
    );
    title.textContent = "印刷エラー";
  } finally {
    state.isBulkUpdating = false;
    closeButton.disabled = false;
    closeButton.textContent = "閉じる";
    closeButton.className = "button primary";
    closeButton.addEventListener("click", () => dialog.close(), { once: true });
  }
}

function beginResetAction(
  onUnlocked: () => void = () => openBulkConfirmation("reset"),
): void {
  if (resetUnlocked()) {
    onUnlocked();
    return;
  }
  const dialog = document.querySelector<HTMLDialogElement>("#reset-auth-dialog");
  if (!dialog) return;
  dialog.innerHTML = `<form id="reset-auth-form" class="reset-auth-card">
    <p class="reset-auth-kicker">RESET AUTHORIZATION</p>
    <h2>権限</h2>
    <p>初期化を実行するには、4桁のパスコードを入力してください。認証は3分間有効です。</p>
    <label>パスコード
      <input name="passcode" type="password" inputmode="numeric" pattern="[0-9]{4}" maxlength="4" autocomplete="off" required>
    </label>
    <p class="reset-auth-error" role="alert" hidden></p>
    <div class="bulk-confirm-actions">
      <button class="button secondary" type="button" data-reset-cancel>キャンセル</button>
      <button class="button primary" type="submit">認証する</button>
    </div>
  </form>`;
  dialog.querySelector<HTMLButtonElement>("[data-reset-cancel]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  dialog.querySelector<HTMLFormElement>("#reset-auth-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void unlockResetAction(event.currentTarget as HTMLFormElement, dialog, onUnlocked);
  });
  showLockedModal(dialog);
  dialog.querySelector<HTMLInputElement>('input[name="passcode"]')?.focus();
}

async function unlockResetAction(
  form: HTMLFormElement,
  dialog: HTMLDialogElement,
  onUnlocked: () => void,
): Promise<void> {
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const errorElement = form.querySelector<HTMLParagraphElement>(".reset-auth-error");
  const passcodeInput = form.elements.namedItem("passcode") as HTMLInputElement | null;
  if (!submitButton || !errorElement || !passcodeInput || !form.reportValidity()) return;
  submitButton.disabled = true;
  submitButton.textContent = "認証中…";
  errorElement.hidden = true;
  try {
    const result = await api<{ token: string; expiresAt: string }>("/api/orders/reset-unlock", {
      method: "POST",
      body: JSON.stringify({ passcode: passcodeInput.value }),
    });
    passcodeInput.value = "";
    state.resetToken = result.token;
    state.resetExpiresAt = result.expiresAt;
    dialog.close();
    onUnlocked();
  } catch (error) {
    passcodeInput.value = "";
    errorElement.textContent = error instanceof Error ? error.message : "権限を確認できませんでした。";
    errorElement.hidden = false;
    passcodeInput.focus();
  } finally {
    submitButton.disabled = false;
    submitButton.textContent = "認証する";
  }
}

function openRecordCorrectionDialog(): void {
  if (!state.data || state.checkedOrderIds.size === 0 || state.isBulkUpdating) return;
  const dialog = document.querySelector<HTMLDialogElement>("#record-correction-dialog");
  if (!dialog) return;
  const selectedOrders = state.data.orders.filter(
    (order) => order.recordType !== "ended" && state.checkedOrderIds.has(order.id),
  );
  if (selectedOrders.length === 0) return;

  const statuses = new Set(selectedOrders.map((order) => order.csOrderStatus));
  const arrivalLabel = state.workspace === "repair" ? "後直し入荷日" : "入荷日";
  const arrivalDates = new Set(selectedOrders.map((order) =>
    state.workspace === "repair" ? order.repairInstockDate : order.itemArrivalDate
  ));
  const shippingRequestDates = new Set(selectedOrders.map((order) => order.shippingRequestDate));
  const commonStatus = statuses.size === 1 ? selectedOrders[0]?.csOrderStatus ?? "" : "";
  const commonArrivalDate = arrivalDates.size === 1
    ? state.workspace === "repair"
      ? selectedOrders[0]?.repairInstockDate ?? ""
      : selectedOrders[0]?.itemArrivalDate ?? ""
    : "";
  const commonShippingRequestDate = shippingRequestDates.size === 1
    ? selectedOrders[0]?.shippingRequestDate ?? ""
    : "";
  const repairAteliers = new Set(selectedOrders.map((order) => order.repairAtelier));
  const repairRequestDates = new Set(selectedOrders.map((order) => order.repairRequestDate));
  const commonRepairAtelier = state.workspace === "repair" && repairAteliers.size === 1
    ? selectedOrders[0]?.repairAtelier ?? ""
    : "";
  const commonRepairRequestDate = state.workspace === "repair" && repairRequestDates.size === 1
    ? selectedOrders[0]?.repairRequestDate ?? ""
    : "";
  const hasCommonStatus = (state.workspace === "size" && commonStatus === "受付済み")
    || commonStatus === "依頼済み"
    || commonStatus === "輸入中"
    || commonStatus === "入荷完了"
    || commonStatus === "印刷完了"
    || commonStatus === "出荷済み" || commonStatus === "出荷依頼"
    || commonStatus === "非表示済";
  const hasCommonArrivalDate = Boolean(commonArrivalDate);
  const hasCommonShippingRequestDate = Boolean(commonShippingRequestDate);
  const hasCommonRepairAtelier = Boolean(commonRepairAtelier);
  const hasCommonRepairRequestDate = Boolean(commonRepairRequestDate);
  const repairAtelierOptions = [...new Set([
    ...state.data.repairAtelierOptions,
    ...(commonRepairAtelier ? [commonRepairAtelier] : []),
  ])];
  const repairCorrectionFields = state.workspace === "repair" ? `
      <div class="record-correction-field">
        <label>
          <span><input name="updateRepairAtelier" type="checkbox" ${hasCommonRepairAtelier ? "checked" : ""}> 依頼工房を変更</span>
          <select name="repairAtelier" ${hasCommonRepairAtelier ? "required" : "disabled"}>
            <option value="" disabled ${hasCommonRepairAtelier ? "" : "selected"}>依頼工房を選択</option>
            ${repairAtelierOptions.map((option) => `<option value="${escapeHtml(option)}" ${commonRepairAtelier === option ? "selected" : ""}>${escapeHtml(option)}</option>`).join("")}
          </select>
        </label>
      </div>
      <div class="record-correction-field">
        <label>
          <span><input name="updateRepairRequestDate" type="checkbox" ${hasCommonRepairRequestDate ? "checked" : ""}> 依頼日を変更</span>
          <input name="repairRequestDate" type="date" value="${escapeHtml(commonRepairRequestDate)}" ${hasCommonRepairRequestDate ? "required" : "disabled"}>
        </label>
      </div>` : "";

  dialog.innerHTML = `<form id="record-correction-form" class="record-correction-card">
    <p class="record-correction-kicker">RECORD CORRECTION</p>
    <h2>記録内容修正</h2>
    <p class="record-correction-summary">選択した${selectedOrders.length}件へ、チェックを入れた項目だけを一括反映します。</p>
    <details class="record-correction-orders">
      <summary>対象注文 ${selectedOrders.length}件を確認</summary>
      <ul>${selectedOrders.map((order) => `<li>${escapeHtml(order.name)}</li>`).join("")}</ul>
    </details>
    <div class="record-correction-fields">
      <div class="record-correction-field">
        <label>
          <span><input name="updateItemArrivalDate" type="checkbox" ${hasCommonArrivalDate ? "checked" : ""}> ${escapeHtml(arrivalLabel)}を変更</span>
          <input name="itemArrivalDate" type="date" value="${escapeHtml(commonArrivalDate)}" ${hasCommonArrivalDate ? "required" : "disabled"}>
        </label>
      </div>
      <div class="record-correction-field">
        <label>
          <span><input name="updateShippingRequestDate" type="checkbox" ${hasCommonShippingRequestDate ? "checked" : ""}> 出荷依頼日を変更</span>
          <input name="shippingRequestDate" type="date" value="${escapeHtml(commonShippingRequestDate)}" ${hasCommonShippingRequestDate ? "required" : "disabled"}>
        </label>
      </div>
      ${repairCorrectionFields}
      <div class="record-correction-field">
        <label>
          <span><input name="updateStatus" type="checkbox" ${hasCommonStatus ? "checked" : ""}> 処理状況を変更</span>
          <select name="status" ${hasCommonStatus ? "required" : "disabled"}>
            <option value="" disabled ${hasCommonStatus ? "" : "selected"}>処理状況を選択</option>
            ${state.workspace === "size"
              ? `<option value="受付済み" ${commonStatus === "受付済み" ? "selected" : ""}>受付済み</option>
                 <option value="依頼済み" ${commonStatus === "依頼済み" ? "selected" : ""}>依頼済み</option>
                 <option value="輸入中" ${commonStatus === "輸入中" ? "selected" : ""}>輸入中</option>`
              : ""}
            ${state.workspace === "repair"
              ? `<option value="受付済み" ${commonStatus === "受付済み" ? "selected" : ""}>受付済み</option>
                 <option value="依頼済み" ${commonStatus === "依頼済み" ? "selected" : ""}>依頼済み</option>
                 <option value="入荷済み" ${commonStatus === "手続き済" || commonStatus === "手続き中" || commonStatus === "入荷済み" ? "selected" : ""}>入荷済み</option>`
              : ""}
            ${state.workspace === "repair"
              ? ""
              : state.workspace === "all"
              ? `<option value="印刷完了" ${commonStatus === "印刷完了" || commonStatus === "入荷完了" ? "selected" : ""}>印刷完了</option>`
              : `<option value="入荷完了" ${commonStatus === "入荷完了" ? "selected" : ""}>入荷完了</option>`}
            <option value="出荷依頼" ${isShippingRequestStatus(commonStatus) ? "selected" : ""}>出荷依頼</option>
            <option value="非表示済" ${commonStatus === "非表示済" ? "selected" : ""}>非表示済</option>
          </select>
        </label>
      </div>
    </div>
    <p class="record-correction-note">注文ごとに現在値が異なる項目は初期状態でオフになります。変更する項目だけオンにしてください。</p>
    <p class="record-correction-error" role="alert" hidden></p>
    <div class="record-correction-actions">
      <div>
        <button class="button secondary" type="button" data-record-correction-cancel>キャンセル</button>
        <button class="button reset" type="button" data-record-correction-reset>リセット</button>
        <button class="button primary" type="submit">変更を保存</button>
      </div>
    </div>
  </form>`;

  const form = dialog.querySelector<HTMLFormElement>("#record-correction-form");
  const arrivalToggle = form?.elements.namedItem("updateItemArrivalDate") as HTMLInputElement | null;
  const arrivalInput = form?.elements.namedItem("itemArrivalDate") as HTMLInputElement | null;
  const shippingRequestDateToggle = form?.elements.namedItem("updateShippingRequestDate") as HTMLInputElement | null;
  const shippingRequestDateInput = form?.elements.namedItem("shippingRequestDate") as HTMLInputElement | null;
  const repairAtelierToggle = form?.elements.namedItem("updateRepairAtelier") as HTMLInputElement | null;
  const repairAtelierSelect = form?.elements.namedItem("repairAtelier") as HTMLSelectElement | null;
  const repairRequestDateToggle = form?.elements.namedItem("updateRepairRequestDate") as HTMLInputElement | null;
  const repairRequestDateInput = form?.elements.namedItem("repairRequestDate") as HTMLInputElement | null;
  const statusToggle = form?.elements.namedItem("updateStatus") as HTMLInputElement | null;
  const statusSelect = form?.elements.namedItem("status") as HTMLSelectElement | null;
  const resetButton = dialog.querySelector<HTMLButtonElement>("[data-record-correction-reset]");
  const submitButton = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (
    !form
    || !arrivalToggle
    || !arrivalInput
    || !shippingRequestDateToggle
    || !shippingRequestDateInput
    || !statusToggle
    || !statusSelect
    || !resetButton
    || !submitButton
    || (state.workspace === "repair" && (
      !repairAtelierToggle
      || !repairAtelierSelect
      || !repairRequestDateToggle
      || !repairRequestDateInput
    ))
  ) return;

  const hasCheckedCorrectionField = (): boolean => arrivalToggle.checked
    || shippingRequestDateToggle.checked
    || repairAtelierToggle?.checked === true
    || repairRequestDateToggle?.checked === true
    || statusToggle.checked;
  const syncCorrectionFields = (): void => {
    arrivalInput.disabled = !arrivalToggle.checked;
    arrivalInput.required = arrivalToggle.checked;
    shippingRequestDateInput.disabled = !shippingRequestDateToggle.checked;
    shippingRequestDateInput.required = shippingRequestDateToggle.checked;
    if (repairAtelierToggle && repairAtelierSelect) {
      repairAtelierSelect.disabled = !repairAtelierToggle.checked;
      repairAtelierSelect.required = repairAtelierToggle.checked;
    }
    if (repairRequestDateToggle && repairRequestDateInput) {
      repairRequestDateInput.disabled = !repairRequestDateToggle.checked;
      repairRequestDateInput.required = repairRequestDateToggle.checked;
    }
    statusSelect.disabled = !statusToggle.checked;
    statusSelect.required = statusToggle.checked;
    const hasCheckedField = hasCheckedCorrectionField();
    resetButton.disabled = !hasCheckedField;
    submitButton.disabled = !hasCheckedField;
  };
  arrivalToggle.addEventListener("change", syncCorrectionFields);
  shippingRequestDateToggle.addEventListener("change", syncCorrectionFields);
  repairAtelierToggle?.addEventListener("change", syncCorrectionFields);
  repairRequestDateToggle?.addEventListener("change", syncCorrectionFields);
  statusToggle.addEventListener("change", syncCorrectionFields);
  dialog.querySelector<HTMLButtonElement>("[data-record-correction-cancel]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  resetButton.addEventListener("click", () => {
    const fields: RecordCorrectionResetFields = {
      itemArrivalDate: arrivalToggle.checked,
      shippingRequestDate: shippingRequestDateToggle.checked,
      repairAtelier: repairAtelierToggle?.checked === true,
      repairRequestDate: repairRequestDateToggle?.checked === true,
      status: statusToggle.checked,
    };
    const labels = [
      fields.itemArrivalDate ? arrivalLabel : "",
      fields.shippingRequestDate ? "出荷依頼日" : "",
      fields.repairAtelier ? "依頼工房" : "",
      fields.repairRequestDate ? "依頼日" : "",
      fields.status ? "処理状況" : "",
    ].filter(Boolean);
    if (labels.length === 0) return;
    const confirmed = window.confirm(
      `選択した${selectedOrders.length}件の「${labels.join("・")}」をリセットします。チェックしていない項目は変更しません。よろしいですか？`,
    );
    if (!confirmed) return;
    dialog.close();
    beginResetAction(() => void resetSelectedOrderRecordFields(fields));
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!hasCheckedCorrectionField()) {
      const errorElement = form.querySelector<HTMLParagraphElement>(".record-correction-error");
      if (errorElement) {
        errorElement.textContent = "変更する項目を1つ以上選択してください。";
        errorElement.hidden = false;
      }
      return;
    }
    if (!form.reportValidity()) return;
    const status = statusToggle.checked
      ? statusSelect.value as RecordCorrectionStatus
      : null;
    const itemArrivalDate = arrivalToggle.checked ? arrivalInput.value : undefined;
    const shippingRequestDate = shippingRequestDateToggle.checked
      ? shippingRequestDateInput.value
      : undefined;
    const repairAtelier = repairAtelierToggle?.checked ? repairAtelierSelect?.value : undefined;
    const repairRequestDate = repairRequestDateToggle?.checked
      ? repairRequestDateInput?.value
      : undefined;
    void saveSelectedOrderRecords(
      form, dialog, status, itemArrivalDate, shippingRequestDate, repairAtelier, repairRequestDate,
    );
  });
  syncCorrectionFields();
  showLockedModal(dialog);
}

async function saveSelectedOrderRecords(
  form: HTMLFormElement,
  dialog: HTMLDialogElement,
  status: RecordCorrectionStatus | null,
  itemArrivalDate: string | undefined,
  shippingRequestDate: string | undefined,
  repairAtelier: string | undefined,
  repairRequestDate: string | undefined,
): Promise<void> {
  if (state.checkedOrderIds.size === 0 || state.isBulkUpdating) return;
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const errorElement = form.querySelector<HTMLParagraphElement>(".record-correction-error");
  if (!submitButton || !errorElement) return;
  state.isBulkUpdating = true;
  submitButton.disabled = true;
  submitButton.textContent = "保存中…";
  errorElement.hidden = true;
  try {
    const result = await requestOrderRecordCorrection(
      [...state.checkedOrderIds],
      status,
      itemArrivalDate,
      shippingRequestDate,
      repairAtelier,
      repairRequestDate,
    );
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
    dialog.close();
    notify(`${result.updated}件の記録内容を修正しました`);
    await loadData(false);
  } catch (error) {
    errorElement.textContent = error instanceof Error ? error.message : "記録内容を修正できませんでした。";
    errorElement.hidden = false;
  } finally {
    state.isBulkUpdating = false;
    submitButton.disabled = false;
    submitButton.textContent = "変更を保存";
  }
}

async function resetSelectedOrderRecordFields(fields: RecordCorrectionResetFields): Promise<void> {
  if (state.checkedOrderIds.size === 0 || state.isBulkUpdating) return;
  state.isBulkUpdating = true;
  render();
  try {
    const status: CsOrderStatus | null = fields.status ? "" : null;
    const itemArrivalDate = fields.itemArrivalDate ? "" : undefined;
    const shippingRequestDate = fields.shippingRequestDate ? "" : undefined;
    const repairAtelier = fields.repairAtelier ? "" : undefined;
    const repairRequestDate = fields.repairRequestDate ? "" : undefined;
    const labels = [
      fields.itemArrivalDate ? state.workspace === "repair" ? "後直し入荷日" : "入荷日" : "",
      fields.shippingRequestDate ? "出荷依頼日" : "",
      fields.repairAtelier ? "依頼工房" : "",
      fields.repairRequestDate ? "依頼日" : "",
      fields.status ? "処理状況" : "",
    ].filter(Boolean);
    const result = await requestOrderRecordCorrection(
      [...state.checkedOrderIds],
      status,
      itemArrivalDate,
      shippingRequestDate,
      repairAtelier,
      repairRequestDate,
    );
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
    notify(`${result.updated}件の${labels.join("・")}をリセットしました`);
    await loadData(false);
  } catch (error) {
    state.resetToken = "";
    state.resetExpiresAt = "";
    notify(error instanceof Error ? error.message : "記録内容をリセットできませんでした。", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

function openBulkConfirmation(action: BulkConfirmAction): void {
  if (!state.data || state.isBulkUpdating) return;
  if (action === "receive" && state.workspace === "repair") return;
  const selectedOrderIds = action === "repair-request"
    ? new Set(state.repairAtelierSelections.keys())
    : state.checkedOrderIds;
  if (selectedOrderIds.size === 0) return;
  if (action === "repair-print" && (state.workspace !== "repair" || state.view === "history")) return;
  if (action === "reprint" && !shouldShowSizeOrderReprintAction(state.workspace, state.view, state.filter)) return;
  const dialog = document.querySelector<HTMLDialogElement>("#bulk-confirm-dialog");
  if (!dialog) return;
  const acceptsAfterAlteration = action === "repair-request"
    || action === "receive"
    || action === "ship"
    || action === "hide"
    || action === "reset";
  const selectedOrders = state.data.orders.filter(
    (order) => selectedOrderIds.has(order.id)
      && (order.recordType === "order"
        || (acceptsAfterAlteration && order.recordType === "after-alteration")),
  );
  if (selectedOrders.length === 0) return;
  const actionLabel = bulkActionLabels[action];
  dialog.innerHTML = `<form class="bulk-confirm-card">
    <h2>注文番号</h2>
    <ul class="bulk-confirm-orders">${selectedOrders.map(
      (order) => `<li>${escapeHtml(order.name)}</li>`,
    ).join("")}</ul>
    <div class="bulk-confirm-count">合計 <strong>${selectedOrders.length}件</strong></div>
    <p>「${escapeHtml(actionLabel)}」処理を実行しますか？</p>
    <div class="bulk-confirm-actions">
      <button class="button secondary" type="button" data-bulk-cancel>キャンセル</button>
      <button class="button primary" type="button" data-bulk-confirm>実行する</button>
    </div>
  </form>`;
  dialog.querySelector<HTMLButtonElement>("[data-bulk-cancel]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  dialog.querySelector<HTMLButtonElement>("[data-bulk-confirm]")?.addEventListener(
    "click",
    () => {
      if (action === "receive" || action === "reprint") {
        void runReceivePdfProcess(selectedOrders, dialog, action);
        return;
      }
      if (action === "repair-print") {
        void runRepairPrintPdfProcess(selectedOrders, dialog);
        return;
      }
      dialog.close();
      executeBulkAction(action);
    },
  );
  showLockedModal(dialog);
}

function historyPaginationHtml(data: BootstrapData): string {
  if (state.view !== "history" || data.pagination.totalPages <= 1) return "";
  const { page, totalPages } = data.pagination;
  const visiblePages = new Set([1, totalPages, page - 2, page - 1, page, page + 1, page + 2]);
  const pages = [...visiblePages]
    .filter((value) => value >= 1 && value <= totalPages)
    .sort((a, b) => a - b);
  let previousPage = 0;
  const buttons: string[] = [];
  for (const value of pages) {
    if (previousPage > 0 && value - previousPage > 1) {
      buttons.push('<span class="pagination-ellipsis" aria-hidden="true">…</span>');
    }
    buttons.push(`<button type="button" data-history-page="${value}" ${value === page ? 'class="is-active" aria-current="page"' : ""}>${value}</button>`);
    previousPage = value;
  }
  return `<nav class="history-pagination" aria-label="履歴ページ">
    <button type="button" data-history-page="${Math.max(1, page - 1)}" ${page <= 1 ? "disabled" : ""}>‹</button>
    ${buttons.join("")}
    <button type="button" data-history-page="${Math.min(totalPages, page + 1)}" ${page >= totalPages ? "disabled" : ""}>›</button>
  </nav>`;
}

function maintenanceResultsHtml(): string {
  if (state.maintenanceResults.length === 0) {
    return state.maintenanceQuery
      ? '<p class="maintenance-empty">一致するShopify注文はありません。</p>'
      : "";
  }
  return `<div class="maintenance-results">${state.maintenanceResults.map((candidate) => {
    const order = candidate.shopifyOrder;
    const selected = state.maintenanceSelectedGid === order.orderGid;
    return `<button type="button" class="maintenance-result${selected ? " is-selected" : ""}" data-maintenance-select="${escapeHtml(order.orderGid)}">
      <span><strong>${escapeHtml(order.orderName)}</strong><small>${escapeHtml(order.customerName || "ゲスト注文")} 様 · ${formatDate(order.createdAt, false)}</small></span>
      <span class="maintenance-result-flags">
        ${candidate.existing ? "<b>DB登録済み</b>" : ""}
        ${candidate.manuallyDeleted ? "<b>手動削除済み</b>" : ""}
        ${candidate.cancelled ? "<b>キャンセル済み</b>" : ""}
      </span>
    </button>`;
  }).join("")}</div>`;
}

function maintenanceEditorHtml(): string {
  const candidate = state.maintenanceResults.find(
    (entry) => entry.shopifyOrder.orderGid === state.maintenanceSelectedGid,
  );
  if (!candidate) return "";
  const shopifyOrder = candidate.shopifyOrder;
  const existing = candidate.existing;
  const customerName = existing?.customerName ?? shopifyOrder.customerName;
  const csOrderStatus = existing?.csOrderStatus ?? shopifyOrder.csOrderStatus;
  const memo = existing?.memo ?? "";
  const itemRows = shopifyOrder.matchedItems.map((item) => `<div class="maintenance-item">
    <code>${escapeHtml(item.sku || "SKUなし")}</code>
    <span>${escapeHtml(item.title)}${item.variantTitle ? ` / ${escapeHtml(item.variantTitle)}` : ""}
      ${item.properties.length > 0 ? `<small>${item.properties.map((property) =>
        `${escapeHtml(property.name)}: ${escapeHtml(property.value)}`).join(" / ")}</small>` : ""}
    </span>
    <b>× ${item.quantity}</b>
  </div>`).join("");
  return `<form id="maintenance-editor" class="maintenance-editor">
    <div class="maintenance-editor-head">
      <div><p>選択中のShopify注文</p><h4>${escapeHtml(shopifyOrder.orderName)}</h4></div>
      ${existing ? '<span class="manual-badge">登録内容を修正</span>' : '<span class="manual-badge new">条件無視で登録</span>'}
    </div>
    <div class="maintenance-id-grid">
      <label>Order GID<input value="${escapeHtml(shopifyOrder.orderGid)}" readonly></label>
      <label>Order ID<input value="${escapeHtml(shopifyOrder.orderLegacyId)}" readonly></label>
    </div>
    <div class="maintenance-form-grid">
      <label>注文者名<input name="customerName" maxlength="200" value="${escapeHtml(customerName)}"></label>
      <label>処理状態
        <select name="csOrderStatus">
          <option value="" ${csOrderStatus === "" ? "selected" : ""}>未処理</option>
          <option value="依頼済み" ${csOrderStatus === "依頼済み" ? "selected" : ""}>依頼済み</option>
          <option value="入荷完了" ${csOrderStatus === "入荷完了" ? "selected" : ""}>入荷完了</option>
          <option value="出荷依頼" ${isShippingRequestStatus(csOrderStatus) ? "selected" : ""}>出荷依頼</option>
          <option value="非表示済" ${csOrderStatus === "非表示済" ? "selected" : ""}>非表示済</option>
        </select>
      </label>
    </div>
    <label>作業メモ<textarea name="memo" maxlength="5000">${escapeHtml(memo)}</textarea></label>
    <div class="maintenance-items">${itemRows || "<p>明細がありません。</p>"}</div>
    ${candidate.lineItemsTruncated ? '<p class="maintenance-warning">この注文は250明細を超えるため、先頭250明細のみ登録対象です。</p>' : ""}
    ${candidate.cancelled ? '<p class="maintenance-warning">キャンセル済み注文です。強制登録すると履歴としてD1に保存されます。</p>' : ""}
    <div class="maintenance-editor-actions">
      ${existing ? '<button type="button" class="button danger" data-maintenance-delete>DBから削除</button>' : "<span></span>"}
      <button type="submit" class="button primary" ${state.maintenanceBusy ? "disabled" : ""}>${existing ? "登録内容を保存" : "条件を無視して登録"}</button>
    </div>
  </form>`;
}

function complianceRequestsHtml(): string {
  const requestRows = state.complianceRequests.map((request) => `
    <article class="compliance-request">
      <div>
        <strong>開示要求 ${escapeHtml(request.requestReference || request.id)}</strong>
        <small>対象注文 ${request.requestedOrderCount}件 / 受信 ${escapeHtml(formatDate(request.receivedAt))}</small>
      </div>
      <div class="compliance-request-actions">
        <button class="button secondary" type="button" data-compliance-export="${escapeHtml(request.id)}" ${state.maintenanceBusy ? "disabled" : ""}>JSONを書き出す</button>
        <button class="button primary" type="button" data-compliance-complete="${escapeHtml(request.id)}" ${state.maintenanceBusy ? "disabled" : ""}>提供済みにする</button>
      </div>
    </article>
  `).join("");
  return `<section class="compliance-section">
    <div class="compliance-head">
      <div><p>PRIVACY REQUESTS</p><h4>顧客データ開示要求</h4></div>
      <button class="button secondary" type="button" data-compliance-refresh ${state.maintenanceBusy ? "disabled" : ""}>再読込</button>
    </div>
    <p class="maintenance-description">JSONを書き出してストアオーナーへ提供した後、「提供済みにする」で保留情報を消去してください。顧客・ショップの削除要求はWebhook受信時に自動処理されます。</p>
    <div class="compliance-list">${requestRows || '<p class="maintenance-empty">保留中の顧客データ開示要求はありません。</p>'}</div>
  </section>`;
}

function maintenancePanelHtml(): string {
  if (!state.maintenanceOpen) return "";
  if (!maintenanceUnlocked()) {
    return `<section class="maintenance-panel">
      <div class="maintenance-head"><div><p>SECURE DB MAINTENANCE</p><h3>DB保守</h3></div><button class="dialog-close" type="button" data-action="maintenance">×</button></div>
      <p class="maintenance-description">手動登録・修正・削除は保護されています。4桁のパスコードを入力してください。</p>
      <form id="maintenance-unlock" class="maintenance-unlock">
        <input name="passcode" type="password" inputmode="numeric" pattern="[0-9]{4}" maxlength="4" autocomplete="off" aria-label="保守パスコード" required>
        <button class="button primary" type="submit" ${state.maintenanceBusy ? "disabled" : ""}>認証</button>
      </form>
    </section>`;
  }
  return `<section class="maintenance-panel">
    <div class="maintenance-head"><div><p>SECURE DB MAINTENANCE</p><h3>DB保守</h3></div><button class="dialog-close" type="button" data-action="maintenance">×</button></div>
    <p class="maintenance-description">注文番号をShopifyで検索し、登録済みなら現在のD1データを修正できます。未登録なら通常の抽出条件を無視して登録します。</p>
    ${state.maintenanceMessage ? `<p class="maintenance-message">${escapeHtml(state.maintenanceMessage)}</p>` : ""}
    ${complianceRequestsHtml()}
    <form id="maintenance-search" class="maintenance-search">
      <input name="query" value="${escapeHtml(state.maintenanceQuery)}" placeholder="注文番号（例: #1234）" autocomplete="off" required>
      <button class="button secondary" type="submit" ${state.maintenanceBusy ? "disabled" : ""}>${state.maintenanceBusy ? "処理中…" : "注文番号検索"}</button>
    </form>
    ${maintenanceResultsHtml()}
    ${maintenanceEditorHtml()}
  </section>`;
}

function customerFlagBadge(
  active: boolean,
  activeLabel: string,
  inactiveLabel: string,
  tone = "positive",
): string {
  return `<span class="customer-flag-badge ${active ? `is-active ${tone}` : "is-inactive"}">${active ? activeLabel : inactiveLabel}</span>`;
}

function customerStatusCheckbox(
  record: CustomerManagementRecord,
  field: CustomerManagementStatusField,
  disabled: boolean,
): string {
  const checked = record[field];
  const saving = state.customerManagementUpdatingIds.has(record.id);
  const editable = customerManagementEditUnlocked(record.id);
  const isDisabled = saving || !editable || disabled;
  const label = field === "warehouseArrival"
    ? "倉庫到着判定"
    : field === "problemOccurred"
      ? "問題発生"
      : "返品・交換可";
  return `<label class="customer-status-control${checked ? " is-checked" : ""}${editable ? " is-editable" : ""}${isDisabled ? " is-disabled" : ""}${saving ? " is-saving" : ""}">
    <input type="checkbox" data-customer-record-id="${escapeHtml(record.id)}" data-customer-status="${field}" aria-label="${label}" ${checked ? "checked" : ""} ${isDisabled ? "disabled" : ""}>
    <span>${checked ? "済" : "未"}</span>
  </label>`;
}

function customerProcessingTone(status: string): string {
  if (status === "対応完了") return "completed";
  if (status.includes("問題")) return "problem";
  if (status.includes("異常なし")) return "ready";
  if (status.includes("到着")) return "arrived";
  return "waiting";
}

function switchInventoryTransferView(view: InventoryTransferView): void {
  if (state.inventoryTransferView === view || state.inventoryTransferLoading || state.inventoryTransferSubmitting) return;
  state.inventoryTransferView = view;
  if (view === "relocation") void loadInventoryTransferData();
  else render();
}

async function loadInventoryTransferData(): Promise<boolean> {
  state.inventoryTransferLoading = true;
  state.inventoryTransferError = "";
  render();
  try {
    const locationsRequest = state.inventoryTransferView === "relocation"
      ? api<{ locations: InventorySourceLocation[] }>("/api/inventory-relocations")
        .then(({ locations }) => ({ relocationLocations: locations, relocationLocationsError: "" }))
        .catch(() => ({ relocationLocations: [], relocationLocationsError: "Shopifyのロケーションを取得できませんでした。" }))
      : Promise.resolve({});
    const [inventoryTransferData, locationData] = await Promise.all([
      api<InventoryTransferData>("/api/inventory-transfers"), locationsRequest,
    ]);
    state.inventoryTransferData = { ...inventoryTransferData, ...locationData };
    state.inventoryReplenishmentCount = inventoryTransferData.replenishmentCount
      ?? inventoryTransferData.replenishmentRecords?.length
      ?? 0;
    state.inventoryReplenishmentSelectedIds.clear();
    const existingGroups = new Set(inventoryTransferData.relocationRecords?.map((record) => record.groupName));
    for (const group of state.inventoryRelocationSelectedGroups) {
      if (!existingGroups.has(group)) state.inventoryRelocationSelectedGroups.delete(group);
    }
    return true;
  } catch (error) {
    state.inventoryTransferError = error instanceof Error
      ? error.message
      : "在庫転送依頼を読み込めませんでした。";
    return false;
  } finally {
    state.inventoryTransferLoading = false;
    render();
  }
}

async function confirmInventoryRelocationRegroup(plan: InventoryRelocationRegroupPlan): Promise<boolean> {
  if (state.etcWorkspace !== "inventory-transfer" || state.inventoryTransferView !== "relocation") return false;
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-relocation-dialog");
  if (!dialog) return false;
  dialog.innerHTML = inventoryRelocationRegroupConfirmationHtml(plan);
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const cancel = (event?: Event) => { event?.preventDefault(); finish(false); };
    const finish = (confirmed: boolean) => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener("cancel", cancel);
      dialog.removeEventListener("close", cancel);
      dialog.close();
      resolve(confirmed);
    };
    dialog.querySelectorAll<HTMLButtonElement>("[data-regroup-cancel]").forEach((button) => button.addEventListener("click", () => finish(false)));
    dialog.querySelector<HTMLButtonElement>("[data-regroup-confirm]")?.addEventListener("click", () => finish(true));
    dialog.addEventListener("cancel", cancel);
    dialog.addEventListener("close", cancel);
    showLockedModal(dialog);
  });
}

async function saveInventoryRelocationLocation(requestId: string, locationId: string, field: "source" | "replenishment"): Promise<void> {
  if (state.inventoryTransferSubmitting || state.inventoryRelocationSavingIds.size > 0
    || !state.inventoryTransferData?.relocationLocations?.some((location) => location.id === locationId)) return;
  state.inventoryRelocationSavingIds.add(requestId);
  state.inventoryTransferSubmitting = true;
  let reloadNeeded = false;
  const label = field === "source" ? "在庫元" : "補充元";
  render();
  try {
    let saved = await api<InventoryRelocationLocationResult>("/api/inventory-relocations", {
      method: "PATCH", body: JSON.stringify({ requestId, locationId, field }),
    });
    if (saved.confirmationRequired) {
      if (!await confirmInventoryRelocationRegroup(saved.plan)) return;
      saved = await api<InventoryRelocationLocationResult>("/api/inventory-relocations", {
        method: "PATCH", body: JSON.stringify({ requestId, locationId, field, confirmRegroup: true, expectedVersion: saved.plan.expectedVersion }),
      });
    }
    if (saved.confirmationRequired) throw new Error("変更内容を再確認してください。");
    reloadNeeded = true;
    const sync = inventoryRegistrationSyncSummary(saved.shopifySync);
    notify((saved.fromGroupName === saved.groupName ? `${label}を保存しました。` : `${label}を変更し、${saved.fromGroupName}から${saved.groupName}へ移動しました。`) + sync.message, sync.failed);
  } catch (error) {
    reloadNeeded = true;
    notify(error instanceof Error ? error.message : `${label}を保存できませんでした。`, true);
  } finally {
    state.inventoryRelocationSavingIds.delete(requestId);
    state.inventoryTransferSubmitting = false;
    if (reloadNeeded) await loadInventoryTransferData();
    else render();
  }
}

function openInventoryRelocationDialog(): void {
  if (state.etcWorkspace !== "inventory-transfer" || state.inventoryTransferView !== "replenishment"
    || state.inventoryTransferLoading || state.inventoryTransferSubmitting) return;
  const selected = (state.inventoryTransferData?.replenishmentRecords ?? [])
    .filter((record) => state.inventoryReplenishmentSelectedIds.has(record.id));
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-relocation-dialog");
  if (!dialog || selected.length === 0) return;
  const { eligible } = inventoryRelocationSelection(selected);
  dialog.innerHTML = inventoryRelocationConfirmationHtml(selected);
  const form = dialog.querySelector<HTMLFormElement>("[data-relocation-form]");
  if (!form) return;
  dialog.querySelectorAll<HTMLButtonElement>("[data-relocation-close]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!state.inventoryTransferSubmitting) dialog.close();
    });
  });
  dialog.oncancel = (event) => {
    if (state.inventoryTransferSubmitting) event.preventDefault();
  };
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (state.inventoryTransferSubmitting || eligible.length === 0) return;
    state.inventoryTransferSubmitting = true;
    form.setAttribute("aria-busy", "true");
    dialog.querySelectorAll<HTMLButtonElement>("button").forEach((button) => { button.disabled = true; });
    const submit = dialog.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.textContent = "移管中…";
    try {
      const result = await api<{ updated: number; skipped: number; groupName: string; shopifySync?: InventoryRegistrationSyncResult[] }>("/api/inventory-relocations", {
        method: "POST",
        body: JSON.stringify({ requestIds: eligible.map((record) => record.id) }),
      });
      dialog.close();
      state.inventoryReplenishmentSelectedIds.clear();
      state.inventoryTransferView = "relocation";
      const sync = inventoryRegistrationSyncSummary(result.shopifySync);
      notify(`${result.updated}件を${result.groupName}に移管しました。${result.skipped ? `処理状況が変わった${result.skipped}件は対象外です。` : ""}${sync.message}`, sync.failed);
    } catch (error) {
      dialog.close();
      notify(error instanceof Error ? error.message : "在庫移管を記録できませんでした。", true);
    } finally {
      form.removeAttribute("aria-busy");
      state.inventoryTransferSubmitting = false;
      await loadInventoryTransferData();
    }
  });
  showLockedModal(dialog);
}

async function openInventoryRegistrationDialog(): Promise<void> {
  if (state.etcWorkspace !== "inventory-transfer" || state.inventoryTransferView !== "relocation"
    || state.inventoryTransferLoading || state.inventoryTransferSubmitting || state.inventoryRelocationSavingIds.size > 0) return;
  const records = selectedInventoryRelocationRecords(state.inventoryTransferData?.relocationRecords ?? [], state.inventoryRelocationSelectedGroups);
  const groups = [...new Set(records.map((record) => record.groupName))];
  if (!groups.length) return;
  if (groups.length > 10) { notify("一度に移管登録できるのは10グループまでです。", true); return; }
  state.inventoryTransferSubmitting = true;
  render();
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-registration-dialog");
  if (!dialog) { state.inventoryTransferSubmitting = false; render(); return; }
  dialog.innerHTML = inventoryRegistrationLoadingDialogHtml();
  dialog.oncancel = (event) => event.preventDefault();
  showLockedModal(dialog);
  try {
    const { plans } = await api<{ plans: InventoryRegistrationPlan[] }>("/api/inventory-relocation-registrations", {
      method: "POST", body: JSON.stringify({ groups: groups.map((groupName) => ({ groupName })) }),
    });
    dialog.innerHTML = inventoryRegistrationDialogHtml(plans);
    const form = dialog.querySelector<HTMLFormElement>("[data-registration-form]");
    if (!form) throw new Error("移管登録の確認画面を準備できませんでした。");
    let submitting = false;
    let attempted = false;
    const closeButtons = form.querySelectorAll<HTMLButtonElement>("[data-registration-close]");
    closeButtons.forEach((button) => button.addEventListener("click", () => { if (!submitting) dialog.close(); }));
    dialog.oncancel = (event) => { if (submitting) event.preventDefault(); };
    const refreshOnClose = () => { if (attempted) void loadInventoryTransferData(); else render(); };
    dialog.addEventListener("close", refreshOnClose, { once: true });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (submitting || attempted || !plans.some((plan) => plan.items.length > 0)) return;
      submitting = true;
      attempted = true;
      state.inventoryTransferSubmitting = true;
      form.setAttribute("aria-busy", "true");
      form.querySelectorAll<HTMLButtonElement>("button").forEach((button) => { button.disabled = true; });
      const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      const resultLabel = form.querySelector<HTMLElement>("[data-registration-result]");
      if (submit) submit.textContent = "Shopifyに登録中…";
      try {
        const submittedGroups = plans.filter((p) => p.items.length > 0).map((p) => ({ groupName: p.groupName, revision: p.revision }));
        const { results } = await api<{ results: InventoryRegistrationResult[] }>("/api/inventory-relocation-registrations", {
          method: "POST", body: JSON.stringify({ confirm: true, groups: submittedGroups }),
        });
        if (!Array.isArray(results) || !results.length) throw new Error("登録結果を確認できませんでした。");
        const allSucceeded = inventoryRegistrationAllSucceeded(results, submittedGroups.map((group) => group.groupName));
        if (resultLabel) resultLabel.textContent = results.map(inventoryRegistrationResultMessage).join("\n")
          + (allSucceeded ? "" : "\nすべてのグループの正常登録を確認できませんでした。閉じて再読込後、該当グループを再確認してください。");
        for (const result of results) if (result.ok) state.inventoryRelocationSelectedGroups.delete(result.groupName);
        if (allSucceeded) {
          const successDialog = document.querySelector<HTMLDialogElement>("#inventory-registration-success-dialog");
          if (!successDialog) throw new Error("登録完了画面を表示できませんでした。");
          successDialog.innerHTML = inventoryRegistrationSuccessDialogHtml(results);
          successDialog.querySelector("[data-registration-success-close]")?.addEventListener("click", () => successDialog.close());
          // Refresh only after the success message is dismissed; rendering on
          // the first dialog's close would remove the new modal immediately.
          successDialog.addEventListener("close", () => { void loadInventoryTransferData(); }, { once: true });
          showLockedModal(successDialog);
          dialog.removeEventListener("close", refreshOnClose);
          dialog.close();
        }
      } catch (error) {
        if (resultLabel) resultLabel.textContent = `${error instanceof Error ? error.message : "登録結果を確認できませんでした。"}\n閉じて再読込後、同じグループで再確認してください。`;
      } finally {
        submitting = false;
        state.inventoryTransferSubmitting = false;
        form.removeAttribute("aria-busy");
        closeButtons.forEach((button) => { button.disabled = false; if (button.textContent === "取消") button.textContent = "閉じる"; });
        if (submit) submit.textContent = "登録処理終了";
      }
    });
    state.inventoryTransferSubmitting = false;
    showLockedModal(dialog);
  } catch (error) {
    if (dialog.open) dialog.close();
    notify(error instanceof Error ? error.message : "登録内容を取得できませんでした。", true);
    state.inventoryTransferSubmitting = false;
    render();
  } finally { state.inventoryTransferSubmitting = false; }
}

function openInventoryRelocationReturnDialog(): void {
  if (state.etcWorkspace !== "inventory-transfer" || state.inventoryTransferView !== "relocation"
    || state.inventoryTransferLoading || state.inventoryTransferSubmitting || state.inventoryRelocationSavingIds.size > 0) return;
  const records = selectedInventoryRelocationRecords(state.inventoryTransferData?.relocationRecords ?? [], state.inventoryRelocationSelectedGroups);
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-relocation-return-dialog");
  if (!dialog || dialog.open || records.length === 0) return;
  dialog.innerHTML = inventoryRelocationReturnDialogHtml(records);
  const form = dialog.querySelector<HTMLFormElement>("[data-relocation-return-form]");
  if (!form) return;
  const selectedRecords = () => {
    const ids = new Set([...form.querySelectorAll<HTMLInputElement>("[data-relocation-return-select]:checked")]
      .map((input) => input.dataset.relocationReturnSelect));
    return records.filter((record) => ids.has(record.id) && (!record.shopifySyncOperationId || record.shopifySyncOperationId.startsWith("ACTION-")));
  };
  const updateSelection = () => {
    const selected = selectedRecords();
    const count = selected.length;
    const requiresAdmin = selected.some((r) => r.shopifyTransferId);
    const passcodeArea = form.querySelector<HTMLElement>("[data-return-passcode-area]");
    if (passcodeArea) passcodeArea.hidden = !requiresAdmin;
    const passcode = form.querySelector<HTMLInputElement>("[data-return-passcode]");
    if (passcode) { passcode.required = requiresAdmin; if (!requiresAdmin) passcode.value = ""; }
    const countLabel = form.querySelector<HTMLElement>("[data-relocation-return-count]");
    if (countLabel) countLabel.textContent = `${count}件を選択${count > 500 ? "（一度に500件まで）" : ""}`;
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = state.inventoryTransferSubmitting || count === 0 || count > 500;
  };
  form.querySelectorAll<HTMLInputElement>("[data-relocation-return-select]").forEach((input) => input.addEventListener("change", updateSelection));
  form.querySelectorAll<HTMLButtonElement>("[data-relocation-return-close]").forEach((button) => button.addEventListener("click", () => {
    if (!state.inventoryTransferSubmitting) dialog.close();
  }));
  dialog.oncancel = (event) => { if (state.inventoryTransferSubmitting) event.preventDefault(); };
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const selected = selectedRecords();
    if (state.inventoryTransferSubmitting || selected.length === 0 || selected.length > 500) return;
    state.inventoryTransferSubmitting = true;
    form.setAttribute("aria-busy", "true");
    form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button").forEach((control) => { control.disabled = true; });
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.textContent = "戻しています…";
    try {
      const items = selected.map((record) => ({ requestId: record.id, groupName: record.groupName, expectedRevision: record.revision }));
      if (selected.some((r) => r.shopifyTransferId)) {
        const passcodeInput = form.querySelector<HTMLInputElement>("[data-return-passcode]");
        const passcode = passcodeInput?.value ?? "";
        if (passcodeInput) passcodeInput.value = "";
        const result = await confirmRelocationAdminReturn(dialog, api, items, passcode);
        if (!result) return;
        const updated = result.results.reduce((n, r) => n + (r.updated ?? 0), 0);
        const failed = result.results.filter((r) => !r.ok);
        notify(`${updated}件を補充依頼管理に戻しました。残りの商品は同じPSTに未登録で残しています。${failed.map((r) => `${r.groupName}：${r.message}`).join(" ")}`, failed.length > 0);
        for (const r of result.results) if (r.ok) state.inventoryRelocationSelectedGroups.delete(r.groupName);
        return;
      }
      const result = await api<{ updated: number; skipped: number }>("/api/inventory-relocation-returns", {
        method: "POST", body: JSON.stringify({ items }),
      });
      state.inventoryRelocationSelectedGroups.clear();
      state.inventoryTransferView = "replenishment";
      notify(`${result.updated}件を補充依頼管理に戻しました。${result.skipped ? `状態やグループが変更された${result.skipped}件は戻していません。` : ""}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "補充依頼管理に戻せませんでした。", true);
    } finally {
      dialog.close();
      state.inventoryTransferSubmitting = false;
      await loadInventoryTransferData();
    }
  });
  showLockedModal(dialog);
}

async function printSelectedInventoryReplenishments(): Promise<void> {
  if (
    state.etcWorkspace !== "inventory-transfer"
    || state.inventoryTransferView !== "relocation"
    || state.inventoryTransferLoading
    || state.inventoryTransferSubmitting
  ) return;
  const selectedRecords = selectedInventoryRelocationRecords(
    state.inventoryTransferData?.relocationRecords ?? [], state.inventoryRelocationSelectedGroups,
  );
  if (selectedRecords.length === 0) {
    notify("受付・印刷する在庫移管グループを選択してください。", true);
    return;
  }
  if (selectedRecords.length > 500 || selectedRecords.some((record) => record.shopifySyncOperationId)) {
    notify("Shopify連携処理が完了した商品を、500件以内で選択してください。", true);
    return;
  }
  const printedItems = selectedRecords.map((record) => ({
    requestId: record.id, groupName: record.groupName, expectedRevision: record.revision,
  }));
  state.inventoryTransferSubmitting = true;
  render();
  try {
    const printConfirmed = await showHtmlPrintPreview({
      title: "在庫移管 受付・印刷",
      description: `選択した在庫移管 ${selectedRecords.length}件・72318（3列×6段）・倍率100%`,
      completionDescription: "「印刷完了」を押すと、印刷した商品の処理状況を「商品検品中」に更新します。在庫移管グループは変更しません。",
      paper: "a4-label-72318",
      pagesHtml: inventoryReplenishmentPrintPages(selectedRecords),
      labelSlots: { renderPages: (usedSlots) => inventoryReplenishmentPrintPages(selectedRecords, usedSlots) },
      listPrint: {
        paper: "a4-landscape",
        description: "在庫移管リスト・A4横・四辺12mm余白・倍率100%・状態更新なし",
        pagesHtml: inventoryRelocationListPrintPages(selectedRecords),
      },
    });
    if (!printConfirmed) return;
    const result = await api<{ updated: number; skipped: number; updatedIds: string[] }>(
      "/api/inventory-relocation-print-completions", { method: "POST", body: JSON.stringify({ items: printedItems }) },
    );
    const updatedIds = new Set(result.updatedIds);
    for (const group of new Set(printedItems.map((item) => item.groupName))) {
      if (printedItems.filter((item) => item.groupName === group).every((item) => updatedIds.has(item.requestId))) {
        state.inventoryRelocationSelectedGroups.delete(group);
      }
    }
    await loadInventoryTransferData();
    notify(`${result.updated}件を「商品検品中」に更新しました。${result.skipped ? ` ${result.skipped}件は印刷中に状態が変わったため更新していません。確認してください。` : ""}`, result.skipped > 0);
  } catch (error) {
    notify(
      `印刷または印刷完了の更新に失敗しました。 ${error instanceof Error ? error.message : ""}`.trim(),
      true,
    );
  } finally {
    state.inventoryTransferSubmitting = false;
    render();
  }
}

function openInventoryReplenishmentIssueDialog(requestId: string): void {
  if (
    state.etcWorkspace !== "inventory-transfer"
    || state.inventoryTransferView !== "relocation"
    || state.inventoryTransferLoading
    || state.inventoryTransferSubmitting
  ) return;
  const record = state.inventoryTransferData?.relocationRecords?.find(
    (candidate) => candidate.id === requestId,
  );
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-replenishment-issue-dialog");
  if (!record || !dialog) return;

  dialog.innerHTML = `<form class="inventory-replenishment-issue-form" data-inventory-replenishment-issue-form data-request-id="${escapeHtml(record.id)}">
    <header class="inventory-replenishment-issue-head">
      <div><p>REPLENISHMENT ISSUE</p><h2 id="inventory-replenishment-issue-title">問題内容を記録</h2></div>
      <button class="dialog-close" type="button" data-inventory-replenishment-issue-close aria-label="閉じる">×</button>
    </header>
    <div class="inventory-replenishment-issue-body">
      <dl>
        <div><dt>商品名</dt><dd>${escapeHtml(record.productTitle || "—")}</dd></div>
        <div><dt>SKU</dt><dd><code>${escapeHtml(record.sku || "—")}</code></dd></div>
      </dl>
      <label class="inventory-replenishment-issue-field">
        <span>問題内容</span>
        <textarea name="replenishmentIssue" rows="7" maxlength="2000" required placeholder="確認が必要な問題内容を入力してください。">${escapeHtml(record.replenishmentIssue)}</textarea>
        <small>1〜2000文字で入力してください。</small>
      </label>
    </div>
    <footer class="inventory-replenishment-issue-actions">
      <button class="button secondary" type="button" data-inventory-replenishment-issue-close>取消</button>
      <button class="button inventory-replenishment-issue-save" type="submit">記録</button>
    </footer>
  </form>`;
  const form = dialog.querySelector<HTMLFormElement>("[data-inventory-replenishment-issue-form]");
  if (!form) return;
  dialog.querySelectorAll<HTMLButtonElement>("[data-inventory-replenishment-issue-close]").forEach(
    (button) => button.addEventListener("click", () => dialog.close()),
  );
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitInventoryReplenishmentIssue(form, dialog);
  });
  showLockedModal(dialog);
  window.requestAnimationFrame(() => {
    dialog.querySelector<HTMLTextAreaElement>('textarea[name="replenishmentIssue"]')?.focus();
  });
}

async function submitInventoryReplenishmentIssue(
  form: HTMLFormElement,
  dialog: HTMLDialogElement,
): Promise<void> {
  if (state.inventoryTransferSubmitting) return;
  const requestId = form.dataset.requestId ?? "";
  const textarea = form.querySelector<HTMLTextAreaElement>('textarea[name="replenishmentIssue"]');
  const replenishmentIssue = textarea?.value.trim() ?? "";
  if (!requestId || !textarea) return;
  if (!replenishmentIssue) {
    textarea.setCustomValidity("問題内容を入力してください。");
    textarea.reportValidity();
    return;
  }
  textarea.setCustomValidity("");

  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const closeButtons = form.querySelectorAll<HTMLButtonElement>("[data-inventory-replenishment-issue-close]");
  state.inventoryTransferSubmitting = true;
  form.setAttribute("aria-busy", "true");
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "記録中…";
  }
  closeButtons.forEach((button) => { button.disabled = true; });
  try {
    const result = await api<{
      chatNotifications: { sent: number; failed: number };
    }>("/api/inventory-transfers", {
      method: "PUT",
      body: JSON.stringify({ requestId, replenishmentIssue }),
    });
    dialog.close();
    if (result.chatNotifications.failed > 0) {
      notify("問題内容は記録しましたが、Google Chatへ送信できませんでした。", true);
    } else {
      notify("問題内容を記録し、Google Chatへ送信しました。");
    }
    await loadInventoryTransferData();
  } catch (error) {
    notify(error instanceof Error ? error.message : "問題内容を記録できませんでした。", true);
    form.removeAttribute("aria-busy");
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "記録";
    }
    closeButtons.forEach((button) => { button.disabled = false; });
  } finally {
    state.inventoryTransferSubmitting = false;
  }
}

function selectedInventoryTransferOrders(): DashboardOrder[] {
  if (!state.data) return [];
  return state.data.orders.filter((order) =>
    order.recordType === "order" && state.checkedOrderIds.has(order.id)
  );
}

function updateInventoryTransferRequestForm(form: HTMLFormElement): void {
  const selected = [...form.querySelectorAll<HTMLInputElement>("[data-transfer-line-item]:checked")];
  const quantitySelect = form.querySelector('select[name="quantity"]') as HTMLSelectElement | null;
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const count = form.querySelector<HTMLElement>("[data-transfer-selection-count]");
  const sharedMaximum = Math.min(999, inventoryTransferSharedMaxQuantity(
    selected.map((input) => Number(input.dataset.maxQuantity ?? "0")),
  ));
  if (count) count.textContent = String(selected.length);
  if (quantitySelect) {
    const previous = Number(quantitySelect.value || "1");
    quantitySelect.innerHTML = sharedMaximum > 0
      ? Array.from({ length: sharedMaximum }, (_, index) => `<option value="${index + 1}">${index + 1}</option>`).join("")
      : '<option value="1">1</option>';
    quantitySelect.value = String(Math.min(Math.max(previous, 1), Math.max(sharedMaximum, 1)));
    quantitySelect.disabled = sharedMaximum === 0 || state.inventoryTransferSubmitting;
  }
  if (submitButton) {
    submitButton.disabled = selected.length === 0
      || sharedMaximum === 0
      || state.inventoryTransferSubmitting;
  }
}

function openInventoryTransferRequestDialog(): void {
  if (
    state.workspace !== "all"
    || state.view !== "history"
    || state.filter !== "received"
    || state.checkedOrderIds.size === 0
    || state.isBulkUpdating
  ) return;
  const orders = selectedInventoryTransferOrders();
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-transfer-request-dialog");
  if (!dialog || orders.length === 0) return;
  dialog.innerHTML = inventoryTransferRequestDialogHtml(orders);
  const form = dialog.querySelector<HTMLFormElement>(".inventory-transfer-request-form");
  if (!form) return;
  form.querySelector("[data-inventory-transfer-close]")?.addEventListener("click", () => dialog.close());
  form.querySelectorAll<HTMLInputElement>("[data-transfer-line-item]").forEach((input) => {
    input.addEventListener("change", () => updateInventoryTransferRequestForm(form));
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitInventoryTransferRequest(form, dialog);
  });
  updateInventoryTransferRequestForm(form);
  dialog.showModal();
}

async function submitInventoryTransferRequest(
  form: HTMLFormElement,
  dialog: HTMLDialogElement,
): Promise<void> {
  if (state.inventoryTransferSubmitting) return;
  const selected = [...form.querySelectorAll<HTMLInputElement>("[data-transfer-line-item]:checked")];
  const receivingLocationSelect = form.querySelector('select[name="receivingLocation"]') as HTMLSelectElement | null;
  const quantitySelect = form.querySelector('select[name="quantity"]') as HTMLSelectElement | null;
  const receivingLocation = receivingLocationSelect?.value ?? "";
  const quantity = Number(quantitySelect?.value ?? "1");
  if (selected.length === 0 || !receivingLocation || !Number.isInteger(quantity) || quantity < 1) return;
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const closeButton = form.querySelector<HTMLButtonElement>("[data-inventory-transfer-close]");
  state.inventoryTransferSubmitting = true;
  form.setAttribute("aria-busy", "true");
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "依頼を記録中…";
  }
  if (closeButton) closeButton.disabled = true;
  try {
    const result = await api<{
      created: number;
      records: Array<{ orderId: string }>;
    }>("/api/inventory-transfers", {
      method: "POST",
      body: JSON.stringify({
        items: selected.map((input) => ({
          orderId: input.dataset.orderId ?? "",
          lineItemId: input.dataset.lineItemId ?? "",
        })),
        receivingLocation,
        quantity,
      }),
    });
    state.inventoryTransferData = null;
    const requestedOrderIds = new Set(result.records.map((record) => record.orderId));
    if (state.data) {
      state.data = {
        ...state.data,
        orders: state.data.orders.map((order) => requestedOrderIds.has(order.id)
          ? { ...order, hasInventoryTransferRequest: true }
          : order),
      };
    }
    state.checkedOrderIds.clear();
    dialog.close();
    render();
    notify(`${result.created}件の在庫転送依頼を記録しました。`);
  } catch (error) {
    notify(error instanceof Error ? error.message : "在庫転送依頼を記録できませんでした。", true);
    form.removeAttribute("aria-busy");
    if (submitButton) submitButton.textContent = "在庫転送依頼する";
    if (closeButton) closeButton.disabled = false;
  } finally {
    state.inventoryTransferSubmitting = false;
    if (dialog.open) updateInventoryTransferRequestForm(form);
  }
}

function customerManagementRowsHtml(
  records: CustomerManagementRecord[],
  view: CustomerManagementView,
): string {
  if (records.length === 0) {
    return `<tr><td class="customer-management-empty" colspan="14">${view === "history"
      ? "完了した返品・交換管理データはまだありません。"
      : "対応中の返品・交換管理データはありません。"}</td></tr>`;
  }
  return records.map((record) => {
    const orderNumber = record.orderNumber.replace(/^#+/, "");
    const orderLegacyId = record.sourceOrderGid.match(/\/Order\/(\d+)$/)?.[1] ?? "";
    const orderNumberHtml = orderLegacyId
      ? `<a class="customer-order-link" href="shopify://admin/orders/${escapeHtml(orderLegacyId)}" target="_blank" rel="noopener noreferrer" aria-label="注文${escapeHtml(orderNumber)}をShopifyで開く"><strong>${escapeHtml(orderNumber)}</strong></a>`
      : `<strong>${escapeHtml(orderNumber)}</strong>`;
    const sku = record.sku.trim();
    const skuHtml = sku
      ? `<button class="sku-inventory-trigger customer-sku-trigger" type="button" data-inventory-sku="${escapeHtml(sku)}" data-inventory-title="" aria-label="SKU ${escapeHtml(sku)}の在庫を表示"><code>${escapeHtml(sku)}</code></button>`
      : "<code>—</code>";
    const supportTone = record.supportType === "返品"
      ? "return"
      : record.supportType === "交換他品"
        ? "exchange-other"
        : "exchange-same";
    const editHistory = record.editCount > 0
      ? `<div class="customer-edit-summary">
          <strong>${escapeHtml(record.lastEditedByName || "—")}</strong>
          <span>${record.lastEditedAt ? escapeHtml(formatDate(record.lastEditedAt)) : "—"}</span>
          <small>履歴 ${record.editCount}件</small>
        </div>`
      : '<span class="customer-history-empty">未修正</span>';
    const effectiveRecord = customerManagementEffectiveRecord(record);
    const saving = state.customerManagementUpdatingIds.has(record.id);
    const selected = state.customerManagementSelectedIds.has(record.id);
    const editable = customerManagementEditUnlocked(record.id);
    const hasDraft = state.customerManagementDrafts.has(record.id);
    const selectionLocked = customerManagementRecordingActive();
    const problemDisabled = !effectiveRecord.warehouseArrival
      || (effectiveRecord.productCondition && !effectiveRecord.problemOccurred);
    const productConditionDisabled = !effectiveRecord.warehouseArrival
      || (effectiveRecord.problemOccurred && !effectiveRecord.productCondition);
    const arrivalDate = effectiveRecord.warehouseArrival ? record.warehouseArrivalDate : "";
    return `<tr class="${saving ? "is-updating" : ""}${selected ? " is-selected" : ""}${editable ? " is-editable" : ""}${hasDraft ? " has-draft" : ""}${record.customerCompleted ? " is-completed" : ""}" aria-busy="${saving}">
      <td class="customer-select-cell">
        <input type="checkbox" data-customer-select="${escapeHtml(record.id)}" aria-label="${escapeHtml(orderNumber)}を選択" ${selected ? "checked" : ""} ${saving || selectionLocked ? "disabled" : ""}>
      </td>
      <td class="customer-order-number">${orderNumberHtml}</td>
      <td class="customer-name-cell">${escapeHtml(record.customerName || "—")}</td>
      <td class="customer-sku-cell">${skuHtml}</td>
      <td><span class="customer-support-badge ${supportTone}">${escapeHtml(record.supportType || "—")}</span></td>
      <td class="customer-notes-cell">${escapeHtml(record.specialNotes || "—")}</td>
      <td><div class="customer-registration"><strong>${escapeHtml(record.registeredByName || "—")}</strong><small>${record.createdAt ? escapeHtml(formatDate(record.createdAt)) : "—"}</small></div></td>
      <td>${editHistory}</td>
      <td><span class="customer-processing-badge ${customerProcessingTone(effectiveRecord.processingStatus)}">${escapeHtml(effectiveRecord.processingStatus || "—")}</span></td>
      <td>${customerStatusCheckbox(effectiveRecord, "warehouseArrival", false)}</td>
      <td class="customer-arrival-date">${arrivalDate ? escapeHtml(formatCalendarDate(arrivalDate)) : hasDraft && effectiveRecord.warehouseArrival ? "保存時に記録" : "—"}</td>
      <td>${customerStatusCheckbox(effectiveRecord, "problemOccurred", problemDisabled)}</td>
      <td>${customerStatusCheckbox(effectiveRecord, "productCondition", productConditionDisabled)}</td>
      <td>${customerFlagBadge(record.customerCompleted, "完了", "未完了", "completed")}</td>
    </tr>`;
  }).join("");
}

function customerManagementEditDialogHtml(): string {
  const selectedCount = state.customerManagementSelectedIds.size;
  return `<dialog id="customer-management-edit-dialog" class="customer-management-edit-dialog" aria-labelledby="customer-management-edit-title">
    <form id="customer-management-edit-form">
      <div class="customer-management-edit-dialog-head">
        <div><small>SECURE RECORD</small><h2 id="customer-management-edit-title">記録の認証</h2></div>
        <button type="button" class="dialog-close" data-customer-edit-close aria-label="閉じる">×</button>
      </div>
      <p>選択した${selectedCount}件を記録対象にします。4桁の暗証番号を入力してください。</p>
      <label>暗証番号
        <input name="passcode" type="password" inputmode="numeric" pattern="[0-9]{4}" maxlength="4" autocomplete="off" required aria-label="4桁の暗証番号">
      </label>
      <div class="customer-management-edit-dialog-actions">
        <button type="button" class="button secondary" data-customer-edit-close>キャンセル</button>
        <button type="submit" class="button primary">認証</button>
      </div>
    </form>
  </dialog>`;
}

function customerManagementWorkspaceHtml(): string {
  const payload = state.customerManagementData;
  const allRecords = payload?.records ?? [];
  const records = customerManagementViewRecords(allRecords, state.customerManagementView);
  const counts = customerManagementViewCounts(allRecords);
  const historyView = state.customerManagementView === "history";
  const selectedCount = state.customerManagementSelectedIds.size;
  const recordingActive = customerManagementRecordingActive();
  const draftCount = Array.from(state.customerManagementSelectedIds)
    .filter((id) => state.customerManagementDrafts.has(id)).length;
  const body = state.customerManagementError
    ? `<div class="customer-management-error" role="alert">
        <strong>データを読み込めませんでした</strong>
        <p>${escapeHtml(state.customerManagementError)}</p>
        <button class="button secondary" type="button" data-action="return-exchange-refresh">再試行</button>
      </div>`
    : !payload && state.customerManagementLoading
      ? `<div class="customer-management-loading" role="status"><span class="workspace-loading-spinner" aria-hidden="true"></span><strong>CSO-CRデータを読み込んでいます…</strong></div>`
      : `<div class="customer-management-table-wrap">
          <table class="customer-management-table" aria-label="${historyView ? "返品・交換管理履歴" : "返品・交換管理一覧"}">
            <colgroup>
              <col class="col-select"><col class="col-order"><col class="col-name"><col class="col-sku"><col class="col-support">
              <col class="col-notes"><col class="col-registration"><col class="col-history"><col class="col-processing">
              <col class="col-warehouse"><col class="col-arrival-date"><col class="col-problem"><col class="col-allowed">
              <col class="col-completed">
            </colgroup>
            <thead><tr>
              <th><span class="sr-only">選択</span></th><th>注文番号</th><th>お名前</th><th>品番・SKU</th><th>対応タイプ</th><th>特記事項</th>
              <th>登録</th><th>編集履歴</th><th>対応・処理状況</th><th>倉庫到着判定</th><th>倉庫到着日</th>
              <th class="customer-problem-head">問題発生</th><th class="customer-allowed-head">返品・交換可</th><th class="customer-completed-head">対応済・在庫調整済</th>
            </tr></thead>
            <tbody>${customerManagementRowsHtml(records, state.customerManagementView)}</tbody>
          </table>
        </div>`;
  return `<main class="etc-workspace-main customer-management-main">
    <section class="customer-management-workspace" aria-labelledby="customer-management-title">
      <header class="customer-management-head">
        <div><p>RETURN &amp; EXCHANGE / ${escapeHtml(payload?.classificationCode ?? "CSO-CR")}</p><h1 id="customer-management-title">返品・交換管理</h1></div>
        <div class="customer-management-head-actions">
          <span><strong>${records.length}</strong>件</span>
          ${selectedCount > 0 ? `<span class="customer-selection-count"><strong>${selectedCount}</strong>件選択</span>` : ""}
          <button class="button customer-management-edit-button${recordingActive ? " is-unlocked" : ""}" type="button" data-action="customer-management-edit" ${selectedCount === 0 || state.customerManagementLoading || state.customerManagementUnlockBusy || state.customerManagementSaveBusy || (recordingActive && draftCount === 0) ? "disabled" : ""}>${recordingActive ? state.customerManagementSaveBusy ? "保存中…" : "保存" : "記録"}</button>
          ${recordingActive ? `<button class="button customer-management-cancel-button" type="button" data-action="customer-management-edit-cancel" ${state.customerManagementSaveBusy ? "disabled" : ""}>取消</button>` : ""}
          <button class="button secondary" type="button" data-action="return-exchange-refresh" ${state.customerManagementLoading || state.customerManagementUnlockBusy ? "disabled" : ""}>
            <span class="button-icon${state.customerManagementLoading ? " is-spinning" : ""}">↻</span>${state.customerManagementLoading ? "更新中…" : "再読込"}
          </button>
        </div>
      </header>
      <nav class="customer-management-subtabs" role="tablist" aria-label="返品・交換管理表示">
        <button class="customer-management-subtab${historyView ? "" : " is-active"}" type="button" role="tab" aria-selected="${!historyView}" data-action="customer-management-active" ${state.customerManagementLoading || state.customerManagementSaveBusy ? "disabled" : ""}>返品・交換管理 <span>${counts.active}</span></button>
        <button class="customer-management-subtab history${historyView ? " is-active" : ""}" type="button" role="tab" aria-selected="${historyView}" data-action="customer-management-history" ${state.customerManagementLoading || state.customerManagementSaveBusy ? "disabled" : ""}>返品・交換管理履歴 <span>${counts.history}</span></button>
      </nav>
      ${body}
      ${customerManagementEditDialogHtml()}
    </section>
  </main>`;
}

let disposeInventoryStickyHeader = (): void => {};

function visibleOrderSearchResults(): DashboardOrder[] {
  return sortDashboardOrders(filterOrders(state.data?.orders ?? []), {
    mode: state.sortMode,
    expressFirst: state.workspace === "size" && state.expressFirst,
    workspace: state.workspace,
  });
}

function orderSearchResultsHtml(orders: DashboardOrder[]): string {
  if (state.workspace === "repair") {
    return repairOrderRows(orders, state.data?.repairAtelierOptions ?? []);
  }
  const allOrdersLayout = state.workspace === "all";
  const selectionEnabled = !allOrdersLayout
    || (state.view === "active" && state.filter === "all")
    || (state.view === "history" && (state.filter === "received" || state.filter === "unnecessary"));
  return orderRows(orders, selectionEnabled, allOrdersLayout,
    shouldShowInventoryTransferRequestStatus(state.workspace, state.view, state.filter));
}

function renderOrderSearchResults(): void {
  const list = document.querySelector<HTMLElement>(".order-list");
  const count = document.querySelector<HTMLElement>(".panel-title > span");
  if (!list || !count || !state.data) return;
  const orders = visibleOrderSearchResults();
  // Keep the active input connected: replacing/refocusing it during an input
  // event can reset the browser's text selection, caret, and IME editing state.
  list.innerHTML = orderSearchResultsHtml(orders);
  count.textContent = `${orders.length}件を表示${state.view === "history" ? ` / 全${state.data.pagination.totalItems}件` : ""}`;
  bindEvents(list);
}

function render(): void {
  const root = document.querySelector<HTMLDivElement>("#app");
  if (!root || !state.data) return;
  const replenishmentScrollLeft = root.querySelector<HTMLElement>(".has-sticky-replenishment .inventory-transfer-table-wrap")?.scrollLeft ?? 0;
  disposeInventoryStickyHeader();
  const { data } = state;
  pruneUnpaidRepairSelections(state.workspace, data.orders, state.checkedOrderIds);
  const isNavigationSwitching = state.isWorkspaceSwitching
    || state.isFilterSwitching
    || state.isPageReloading
    || state.isOrderDetailsRefreshing;
  const etcWorkspace = state.etcWorkspace;
  const isOrderWorkspace = etcWorkspace === null;
  const etcWorkspaceClass = etcWorkspace === "inventory-transfer"
    ? " inventory-transfer-workspace"
    : etcWorkspace === "return-exchange"
      ? " return-exchange-workspace"
      : etcWorkspace === "inventory-search"
        ? " inventory-search-workspace"
        : etcWorkspace === "cleaning"
          ? " cleaning-workspace"
          : "";
  const visibleOrders = visibleOrderSearchResults();
  const checkedCount = state.checkedOrderIds.size;
  const workflowActionsDisabled = checkedCount === 0 || state.isBulkUpdating || state.view === "history";
  const historyDeleteDisabled = checkedCount === 0 || state.isBulkUpdating;
  const correctionActionDisabled = checkedCount === 0 || state.isBulkUpdating;
  const correctionAction = `<button class="button correction" data-action="record-correction" ${correctionActionDisabled ? "disabled" : ""}>記録内容修正</button>`;
  const resetActionVisible = state.filter === "received" || state.filter === "shipped";
  const resetActionDisabled = checkedCount === 0 || state.isBulkUpdating;
  const resetAction = resetActionVisible
    ? `<button class="button reset" data-action="reset" ${resetActionDisabled ? "disabled" : ""}>${state.isBulkUpdating ? "初期化中…" : "初期化"}</button>`
    : "";
  const reprintAction = shouldShowSizeOrderReprintAction(state.workspace, state.view, state.filter)
    ? `<button class="button reprint" type="button" data-action="reprint" ${checkedCount === 0 || state.isBulkUpdating ? "disabled" : ""}>${state.isBulkUpdating ? "プレビュー準備中…" : "🖨️ 印刷"}</button>`
    : "";
  const receiveAction = state.workspace === "repair" || state.filter === "received"
    ? ""
    : `<button class="button receive" data-action="receive" ${workflowActionsDisabled ? "disabled" : ""}>入荷処理</button>`;
  const repairRequestDisabled = state.repairAtelierSelections.size === 0
    || state.isBulkUpdating
    || state.view === "history";
  const repairRequestAction = state.workspace === "repair" && state.filter !== "received"
    ? `<button class="button repair-request" data-action="repair-request" ${repairRequestDisabled ? "disabled" : ""}>依頼処理</button>`
    : "";
  const repairPrintAction = state.workspace === "repair"
    ? `<button class="button repair-print" data-action="repair-print" ${workflowActionsDisabled ? "disabled" : ""}>🖨️印刷</button>`
    : "";
  const shippingRequestAction = shouldShowShippingRequestAction(
    state.workspace,
    state.view,
    state.filter,
  )
    ? `<button class="button ship" data-action="ship" ${checkedCount === 0 || state.isBulkUpdating ? "disabled" : ""}>出荷依頼</button>`
    : "";
  const allOrdersLayout = state.workspace === "all";
  const showInventoryTransferStatus = shouldShowInventoryTransferRequestStatus(
    state.workspace,
    state.view,
    state.filter,
  );
  const dailyOrderSelectionEnabled = allOrdersLayout && (
    (state.view === "active" && state.filter === "all")
    || (state.view === "history" && (state.filter === "received" || state.filter === "unnecessary"))
  );
  const allOrdersReadOnly = allOrdersLayout && !dailyOrderSelectionEnabled;
  const dailyPrintCount = dailyOrderPrintEligibleOrders(new Date()).length;
  const dailyPrintVisible = allOrdersLayout && (
    (state.view === "active" && state.filter === "all")
    || (state.view === "history" && state.filter === "received")
  );
  const dailyPrintDisabled = state.isBulkUpdating
    || (state.view === "history" ? checkedCount === 0 : checkedCount === 0 && dailyPrintCount === 0);
  const dailyPrintAction = dailyPrintVisible
    ? `<button class="button daily-print" data-action="daily-print" ${dailyPrintDisabled ? "disabled" : ""}>${state.isBulkUpdating ? "プレビュー準備中…" : "🖨️ 印刷"}</button>`
    : "";
  const inventoryTransferRequestAction = allOrdersLayout
    && state.view === "history"
    && state.filter === "received"
    ? `<button class="button inventory-transfer-request" data-action="inventory-transfer-request" ${checkedCount === 0 || state.isBulkUpdating ? "disabled" : ""}>在庫転送依頼</button>`
    : "";
  const confirmUnnecessaryAction = allOrdersLayout
    && state.view === "history"
    && state.filter === "unnecessary"
    ? `<button class="button confirm-unnecessary" data-action="confirm-unnecessary" ${checkedCount === 0 || state.isBulkUpdating ? "disabled" : ""}>${state.isBulkUpdating ? "変更中…" : "確認済みに変更"}</button>`
    : "";
  const inventoryReplenishmentNavBadge = inventoryReplenishmentNavigationBadge(
    state.inventoryReplenishmentCount,
  );

  root.innerHTML = `<div class="app-shell${isOrderWorkspace && state.workspace === "all" ? " all-orders-workspace" : ""}${isOrderWorkspace && state.workspace === "repair" ? " repair-workspace" : ""}${etcWorkspace ? " etc-workspace" : ""}${etcWorkspaceClass}${isNavigationSwitching ? " is-workspace-switching" : ""}" aria-busy="${isNavigationSwitching}">
    <header class="topbar">
      <div class="brand-block"><div class="brand-mark" aria-hidden="true">T</div></div>
      <div class="topbar-navigation">
        <nav class="all-order-tabs" role="tablist" aria-label="全注文メニュー">
          <button class="all-order-tab${isOrderWorkspace && state.workspace === "all" ? " is-active" : ""}" type="button" role="tab" aria-selected="${isOrderWorkspace && state.workspace === "all"}" data-action="all-orders" ${isNavigationSwitching ? "disabled" : ""}>日々注文リスト</button>
          <button class="etc-tab inventory-transfer-tab${etcWorkspace === "inventory-transfer" ? " is-active" : ""}" type="button" role="tab" aria-selected="${etcWorkspace === "inventory-transfer"}" data-action="inventory-transfer" ${isNavigationSwitching ? "disabled" : ""}>在庫転送管理${inventoryReplenishmentNavBadge}</button>
        </nav>
        <nav class="app-tabs" role="tablist" aria-label="注文管理メニュー">
          <button class="app-tab${isOrderWorkspace && state.workspace === "size" ? " is-active" : ""}" type="button" role="tab" aria-selected="${isOrderWorkspace && state.workspace === "size"}" data-action="size" ${isNavigationSwitching ? "disabled" : ""}>サイズオーダー</button>
          <button class="app-tab repair-tab${isOrderWorkspace && state.workspace === "repair" ? " is-active" : ""}" type="button" role="tab" aria-selected="${isOrderWorkspace && state.workspace === "repair"}" data-action="repair" ${isNavigationSwitching ? "disabled" : ""}>お直し</button>
          ${cleaningTabHtml(etcWorkspace === "cleaning", isNavigationSwitching)}
        </nav>
        <nav class="etc-tabs" role="tablist" aria-label="その他管理メニュー">
          <button class="etc-tab return-exchange-tab${etcWorkspace === "return-exchange" ? " is-active" : ""}" type="button" role="tab" aria-selected="${etcWorkspace === "return-exchange"}" data-action="return-exchange" ${isNavigationSwitching ? "disabled" : ""}>返品・交換管理</button>
        </nav>
        <nav class="inventory-search-tabs" role="tablist" aria-label="在庫検索メニュー">
          <button id="inventory-search-tab" class="etc-tab inventory-search-tab${etcWorkspace === "inventory-search" ? " is-active" : ""}" type="button" role="tab" aria-selected="${etcWorkspace === "inventory-search"}" aria-controls="inventory-search-panel" data-action="inventory-search" ${isNavigationSwitching ? "disabled" : ""}>在庫検索</button>
        </nav>
      </div>
      ${isOrderWorkspace ? `<div class="hero-actions">
        <button class="button page-reload" data-action="page-reload" type="button" ${state.isPageReloading || state.isOrderDetailsRefreshing ? "disabled" : ""}>
          <span class="button-icon${state.isPageReloading ? " is-spinning" : ""}">↻</span>${state.isPageReloading ? "再読込中…" : "更新"}
        </button>
      </div>` : ""}
      <div class="topbar-status">${etcWorkspace === "return-exchange"
        ? '<span class="etc-ready-status">CSO-CR</span>'
        : etcWorkspace === "inventory-transfer"
          ? '<span class="etc-ready-status">STOCK TRANSFER</span>'
          : etcWorkspace === "cleaning"
            ? '<span class="etc-ready-status">CLEANING</span>'
          : etcWorkspace
            ? '<span class="etc-ready-status">準備中</span>'
            : '<span class="live-dot"><i></i>リアルタイム監視中</span>'}</div>
    </header>
    ${isNavigationSwitching ? `<div class="workspace-loading-overlay" role="status" aria-live="polite">
      <div class="workspace-loading-card">
        <span class="workspace-loading-spinner" aria-hidden="true"></span>
        <strong>${state.isOrderDetailsRefreshing
          ? "既存注文の詳細情報を再更新しています…"
          : state.isPageReloading
            ? `${escapeHtml(filterLabel())}を再読み込みしています…`
            : state.isFilterSwitching
            ? `${escapeHtml(filterLabel())}を読み込んでいます…`
            : `${etcWorkspace ? etcWorkspaceLabel(etcWorkspace) : state.workspace === "all" ? "日々注文リスト" : state.workspace === "repair" ? "お直し" : "サイズオーダー"}を読み込んでいます…`}</strong>
        <small>${state.isOrderDetailsRefreshing ? "Shopifyから既存注文を再取得しています" : state.isPageReloading ? "現在のタブの表示内容を更新しています" : state.isFilterSwitching ? "注文リストを切り替えています" : etcWorkspace ? "管理画面を切り替えています" : "注文データを更新中です"}</small>
      </div>
    </div>` : ""}

    ${etcWorkspace === "return-exchange"
      ? customerManagementWorkspaceHtml()
      : etcWorkspace === "inventory-transfer"
        ? inventoryTransferWorkspaceHtml(
          state.inventoryTransferData,
          state.inventoryTransferLoading,
          state.inventoryTransferError,
          state.inventoryTransferView,
          state.inventoryReplenishmentSelectedIds,
          state.inventoryRelocationSelectedGroups,
          state.inventoryRelocationSavingIds,
        )
        : etcWorkspace === "cleaning" ? cleaningWorkspaceHtml(state.cleaningData, state.cleaningLoading, state.cleaningError, { view: state.cleaningView, selectedIds: state.cleaningSelectedIds, savingIds: state.cleaningSavingIds, requestSaving: state.cleaningRequestSaving })
        : etcWorkspace ? `<main id="inventory-search-panel" class="etc-workspace-main" role="tabpanel" aria-labelledby="inventory-search-tab">
      <section class="etc-empty-state" aria-labelledby="etc-workspace-title">
        <span class="etc-empty-kicker">NEW WORKSPACE</span>
        <span class="etc-empty-icon" aria-hidden="true">⌕</span>
        <h1 id="etc-workspace-title">${etcWorkspaceLabel(etcWorkspace)}</h1>
        <p>データはまだありません。</p>
        <span class="etc-empty-status">準備中</span>
      </section>
    </main>` : ""}
    <main class="order-workspace-main${etcWorkspace ? " is-hidden" : ""}">
      ${state.importError ? `<p class="operation-error" role="alert">再取込エラー: ${escapeHtml(state.importError)}</p>` : ""}
      ${state.importMessage ? `<p class="operation-success" role="status">${escapeHtml(state.importMessage)}</p>` : ""}
      ${maintenancePanelHtml()}

      <div class="sticky-workspace">
        <section class="stats-grid" aria-label="表示リスト切替">
          ${statsCard("all", "coral", "▦", state.workspace === "all" ? "日々注文リスト" : "対象注文リスト", data.stats.orderCount)}
          ${state.workspace === "repair" ? "" : statsCard("received", "blue", "◆", state.workspace === "all" ? "印刷履歴" : "入荷済み・リスト", data.stats.receivedCount)}
          ${state.workspace === "all" ? statsCard("unnecessary", "caution", "!", "不要になった注文", data.stats.unnecessaryCount) : statsCard("shipped", "green", "✓", "出荷済み・リスト", data.stats.shippedCount)}
          ${statsCard("hidden", "red", "−", "非表示済・リスト", data.stats.hiddenCount)}
        </section>

        <div class="panel-head">
          <div class="panel-title"><h3>${filterLabel()}</h3><span>${visibleOrders.length}件を表示${state.view === "history" ? ` / 全${data.pagination.totalItems}件` : ""}</span>${allOrdersReadOnly ? "" : `<b>${checkedCount}件選択中</b>`}</div>
          <div class="panel-tools">
            <div class="bulk-actions">
              ${allOrdersLayout
                ? state.filter === "unnecessary"
                  ? confirmUnnecessaryAction
                  : `${inventoryTransferRequestAction}${dailyPrintAction}`
                : state.view === "history"
                ? state.filter === "ended"
                  ? '<button class="button secondary" data-action="ended-history">通常の履歴へ戻る</button>'
                  : `${reprintAction}${correctionAction}
                    ${shippingRequestAction}
                    <button class="button danger" data-action="bulk-delete" ${historyDeleteDisabled ? "disabled" : ""}>${state.isBulkUpdating ? "一括削除中…" : "一括削除"}</button>
                    ${resetAction}
                    <button class="button secondary" data-action="ended-history">終了データ履歴</button>`
                : `${correctionAction}
                  ${receiveAction}
                  ${repairRequestAction}
                  ${repairPrintAction}
                  ${shippingRequestAction}
                  <button class="button hide" data-action="hide" ${workflowActionsDisabled ? "disabled" : ""}>非表示</button>
                  ${resetAction}`}
            </div>
            <div class="list-sort-controls" role="group" aria-label="一覧の並び順">
              ${dedicatedDateSortButton()}
              <button class="list-sort-toggle${state.sortMode === "schedule" ? " is-active" : ""}" type="button" data-sort-mode="schedule" aria-pressed="${state.sortMode === "schedule"}">
                <span aria-hidden="true">↕</span> 配送／着用日順
              </button>
              ${state.workspace === "size"
                ? `<button class="list-sort-toggle express${state.expressFirst ? " is-active" : ""}" type="button" data-express-first aria-pressed="${state.expressFirst}">
                    <span aria-hidden="true">⚡</span> 特急優先
                  </button>`
                : ""}
            </div>
            <label class="search-box"><span>⌕</span><input id="order-search" type="search" value="${escapeHtml(state.query)}" placeholder="${state.filter === "ended" ? "注文番号・認識番号・終了データを検索" : "注文番号・注文者・SKU・注文メモ・作業検索"}"></label>
            <button class="icon-button" data-action="refresh" aria-label="一覧を再読込">↻</button>
          </div>
        </div>
        ${state.workspace === "repair"
          ? '<div class="table-head repair-table-head"><span></span><span>注文・注文者</span><span>販売元</span><span>お渡し方法</span><span>仕上り</span><span>後直し入荷日</span><span>依頼工房</span><span>依頼日</span><span>配送／着用日</span><span>対象SKU</span><span>都道府県</span><span>作業メモ</span><span>出荷依頼日</span><span>処理状況</span><span>ステータス</span><span>決済状況</span><span></span></div>'
          : state.workspace === "all"
            ? `<div class="table-head all-order-table-head${showInventoryTransferStatus ? " has-transfer-status" : ""}"><span></span><span>注文・注文者</span><span>販売元</span><span>お渡し方法</span><span>配送／着用日</span><span>仕上り</span><span>対象SKU</span><span>サイズ</span><span>特定名</span><span>消耗在庫</span><span>残り在庫</span><span>都道府県</span>${showInventoryTransferStatus ? "<span>転送依頼</span>" : ""}<span></span></div>`
            : '<div class="table-head"><span></span><span>注文・注文者</span><span>販売元</span><span>お渡し方法</span><span>入荷次第連絡</span><span>配送／着用日</span><span>対象SKU</span><span>都道府県</span><span>作業メモ</span><span>入荷日</span><span>出荷依頼日</span><span>処理状況</span><span>ステータス</span><span></span></div>'}
      </div>

      <section class="orders-panel">
        <div class="order-list">${orderSearchResultsHtml(visibleOrders)}</div>
      </section>
      ${historyPaginationHtml(data)}
    </main>

    <footer></footer>
  </div>
  <dialog id="order-dialog" class="order-dialog"></dialog>
  <dialog id="daily-unnecessary-notice-dialog" class="bulk-confirm-dialog"></dialog>
  <dialog id="inventory-transfer-request-dialog" class="inventory-transfer-request-dialog"></dialog>
  <dialog id="inventory-relocation-dialog" class="inventory-transfer-request-dialog" aria-labelledby="inventory-relocation-title"></dialog>
  <dialog id="inventory-relocation-return-dialog" class="inventory-transfer-request-dialog" aria-labelledby="inventory-relocation-return-title"></dialog>
  <dialog id="inventory-registration-dialog" class="inventory-transfer-request-dialog" aria-labelledby="inventory-registration-title"></dialog>
  <dialog id="inventory-registration-success-dialog" class="inventory-transfer-request-dialog inventory-shopify-message-dialog" aria-labelledby="inventory-registration-success-title"></dialog>
        <dialog id="inventory-transfer-detail-dialog" class="inventory-transfer-request-dialog" aria-label="Shopify 在庫移管詳細"></dialog>
  <dialog id="inventory-replenishment-issue-dialog" class="inventory-replenishment-issue-dialog" aria-labelledby="inventory-replenishment-issue-title"></dialog>
  <dialog id="inventory-popover" class="inventory-popover"></dialog>
  <dialog id="bulk-confirm-dialog" class="bulk-confirm-dialog"></dialog>
  <dialog id="print-preview-dialog" class="html-print-preview-dialog"></dialog>
  <dialog id="html-print-cancel-warning-dialog" class="bulk-confirm-dialog"></dialog>
  <dialog id="record-correction-dialog" class="record-correction-dialog"></dialog>
  <dialog id="reset-auth-dialog" class="reset-auth-dialog"></dialog>`;

  bindEvents();
  if (etcWorkspace === "inventory-search" || etcWorkspace === "cleaning") {
    root.querySelector<HTMLElement>(`#${etcWorkspace}-tab`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  const replenishmentTableWrap = root.querySelector<HTMLElement>(".has-sticky-replenishment .inventory-transfer-table-wrap");
  if (replenishmentTableWrap) replenishmentTableWrap.scrollLeft = replenishmentScrollLeft;
  disposeInventoryStickyHeader = bindInventoryReplenishmentStickyHeader(root);
  if (state.selectedLegacyId) openOrder(state.selectedLegacyId);
}

function bindEvents(scope: Document | HTMLElement = document): void {
  const cleaningPanel = scope.querySelector<HTMLElement>("#cleaning-panel");
  if (cleaningPanel) bindCleaningSelection(cleaningPanel, state.cleaningSelectedIds, render);
  const orderSearch = scope.querySelector<HTMLInputElement>("#order-search");
  const applyOrderSearch = (input: HTMLInputElement): void => {
    // Ignore a late IME event from an input removed during navigation/rendering.
    if (!input.isConnected || input !== document.querySelector("#order-search")) return;
    if (state.query === input.value) return;
    state.query = input.value;
    renderOrderSearchResults();
  };
  orderSearch?.addEventListener("compositionstart", () => {
    orderSearchComposing = true;
  });
  orderSearch?.addEventListener("compositionend", (event) => {
    orderSearchComposing = false;
    applyOrderSearch(event.currentTarget as HTMLInputElement);
  });
  orderSearch?.addEventListener("input", (event) => {
    if (orderSearchComposing || (event as InputEvent).isComposing) return;
    applyOrderSearch(event.currentTarget as HTMLInputElement);
  });

  scope.querySelectorAll<HTMLElement>("[data-filter]").forEach((element) => {
    element.addEventListener("click", () => {
      void switchFilter((element.dataset.filter ?? "all") as ListFilter);
    });
  });

  scope.querySelectorAll<HTMLButtonElement>("[data-sort-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      const mode = (button.dataset.sortMode ?? "default") as OrderSortMode;
      const fallbackMode = defaultOrderSortModeForFilter(state.filter);
      state.sortMode = state.sortMode === mode ? fallbackMode : mode;
      render();
    });
  });
  scope.querySelector<HTMLButtonElement>("[data-express-first]")?.addEventListener("click", () => {
    state.expressFirst = !state.expressFirst;
    render();
  });
  scope.querySelectorAll<HTMLButtonElement>("[data-repair-instock-order]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      const orderId = button.dataset.repairInstockOrder;
      if (!orderId || button.disabled) return;
      void updateRepairInstockDate(orderId);
    });
  });
  scope.querySelectorAll<HTMLInputElement>("[data-repair-atelier-order]").forEach((radio) => {
    radio.addEventListener("click", (event) => {
      event.stopPropagation();
      const orderId = radio.dataset.repairAtelierOrder;
      if (!orderId || radio.disabled || state.isBulkUpdating) return;
      state.repairAtelierSelections.set(orderId, radio.value);
      render();
    });
  });
  scope.querySelectorAll<HTMLButtonElement>("[data-reset-repair-atelier-order]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      const orderId = button.dataset.resetRepairAtelierOrder;
      if (!orderId || button.disabled
        || !resetRepairAtelierSelection(state.repairAtelierSelections, orderId, state.isBulkUpdating)) return;
      render();
      const radios = [...document.querySelectorAll<HTMLInputElement>("[data-repair-atelier-order]")]
        .filter((radio) => radio.dataset.repairAtelierOrder === orderId);
      (radios.find((radio) => radio.checked) ?? radios[0])?.focus({ preventScroll: true });
    });
  });

  scope.querySelectorAll<HTMLButtonElement>("[data-history-page]").forEach((button) => {
    button.addEventListener("click", () => {
      const page = Number.parseInt(button.dataset.historyPage ?? "1", 10);
      if (Number.isSafeInteger(page) && page > 0 && page !== state.historyPage) {
        state.historyPage = page;
        void loadData(false);
      }
    });
  });

  scope.querySelectorAll<HTMLInputElement>("[data-select-order]").forEach((checkbox) => {
    checkbox.addEventListener("click", (event) => event.stopPropagation());
    checkbox.addEventListener("change", () => {
      const orderId = checkbox.dataset.selectOrder;
      if (!orderId) return;
      const order = state.data?.orders.find((item) => item.id === orderId);
      if (checkbox.disabled || !order || isUnpaidRepairSelection(state.workspace, order)) {
        state.checkedOrderIds.delete(orderId);
        checkbox.checked = false;
        render();
        return;
      }
      if (checkbox.checked) state.checkedOrderIds.add(orderId);
      else state.checkedOrderIds.delete(orderId);
      render();
    });
  });

  const replenishmentRecords = state.inventoryTransferData?.replenishmentRecords ?? [];
  const replenishmentSelectAll = scope.querySelector<HTMLInputElement>(
    "[data-inventory-replenishment-select-all]",
  );
  if (replenishmentSelectAll) {
    const selectedCount = replenishmentRecords.reduce(
      (count, record) => count + (state.inventoryReplenishmentSelectedIds.has(record.id) ? 1 : 0),
      0,
    );
    replenishmentSelectAll.indeterminate = selectedCount > 0 && selectedCount < replenishmentRecords.length;
    replenishmentSelectAll.addEventListener("click", (event) => event.stopPropagation());
    replenishmentSelectAll.addEventListener("change", () => {
      for (const record of replenishmentRecords) {
        if (replenishmentSelectAll.checked) state.inventoryReplenishmentSelectedIds.add(record.id);
        else state.inventoryReplenishmentSelectedIds.delete(record.id);
      }
      render();
    });
  }
  scope.querySelectorAll<HTMLInputElement>("[data-inventory-replenishment-select]").forEach((checkbox) => {
    checkbox.addEventListener("click", (event) => event.stopPropagation());
    checkbox.addEventListener("change", () => {
      const requestId = checkbox.dataset.inventoryReplenishmentSelect;
      if (!requestId) return;
      if (checkbox.checked) state.inventoryReplenishmentSelectedIds.add(requestId);
      else state.inventoryReplenishmentSelectedIds.delete(requestId);
      render();
    });
  });

  scope.querySelectorAll<HTMLInputElement>("[data-relocation-group-select]").forEach((checkbox) => {
    checkbox.addEventListener("click", (event) => event.stopPropagation());
    checkbox.addEventListener("change", () => {
      const group = checkbox.dataset.relocationGroupSelect;
      if (!group) return;
      if (checkbox.checked) state.inventoryRelocationSelectedGroups.add(group);
      else state.inventoryRelocationSelectedGroups.delete(group);
      render();
    });
  });
  scope.querySelectorAll<HTMLElement>("[data-relocation-detail]").forEach((cell) => {
    const open = () => {
      if (state.inventoryTransferLoading || state.inventoryTransferSubmitting || state.inventoryRelocationSavingIds.size) return;
      const dialog = document.querySelector<HTMLDialogElement>("#inventory-transfer-detail-dialog");
      if (!dialog) return;
      void openRelocationDetail(dialog, cell.dataset.relocationDetail ?? "", api, showLockedModal, () => { void loadInventoryTransferData(); })
        .catch((error) => notify(error instanceof Error ? error.message : "移管詳細を取得できませんでした。", true));
    };
    cell.addEventListener("click", (event) => { event.stopPropagation(); open(); });
    cell.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.stopPropagation(); open(); }
    });
  });
  scope.querySelectorAll<HTMLElement & { value: string }>("select[data-relocation-location-field]").forEach((select) => {
    select.addEventListener("click", (event) => event.stopPropagation());
    select.addEventListener("change", () => {
      const field = select.dataset.relocationLocationField;
      if (field !== "source" && field !== "replenishment") return;
      const requestId = field === "source" ? select.dataset.relocationSource : select.dataset.relocationReplenishment;
      if (requestId) void saveInventoryRelocationLocation(requestId, select.value, field);
    });
  });

  scope.querySelectorAll<HTMLButtonElement>("[data-inventory-sku]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const sku = button.dataset.inventorySku ?? "";
      if (!sku) return;
      void openInventoryPopover(sku, button.dataset.inventoryTitle ?? "", button);
    });
  });

  scope.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => {
    element.addEventListener("click", () => {
      const action = element.dataset.action;
      if (action === "page-reload") void reloadCurrentTab();
      if (action === "order-details-refresh") openOrderDetailsRefreshDialog();
      if (action === "refresh") void loadData(true);
      if (action === "import") void importOrders();
      if (action === "all-orders") void switchWorkspace("all");
      if (action === "size") void switchWorkspace("size");
      if (action === "repair") void switchWorkspace("repair");
      if (action === "cleaning") void switchEtcWorkspace("cleaning");
      if (state.etcWorkspace === "cleaning" && !state.cleaningLoading && !state.isWorkspaceSwitching) {
        const page = state.cleaningData?.pagination.page ?? 1;
        if (action === "cleaning-arrive") void recordCleaningArrival(element.dataset.cleaningId ?? "");
        if (action === "cleaning-handover") void recordCleaningVendorHandover(element.dataset.cleaningId ?? "");
        if (state.cleaningSavingIds.size === 0 && !state.cleaningRequestSaving) {
          if (action === "cleaning-view" && isCleaningView(element.dataset.cleaningView)) void switchCleaningView(element.dataset.cleaningView);
          if (action === "cleaning-refresh") void loadCleaningData(page);
          if (action === "cleaning-previous" && page > 1) void loadCleaningData(page - 1);
          if (action === "cleaning-next" && page < (state.cleaningData?.pagination.totalPages ?? 1)) void loadCleaningData(page + 1);
          if (action === "cleaning-request") void requestCleaningPickup();
          if (action === "cleaning-memo-edit" || action === "cleaning-memo-view") {
            const record = state.cleaningData?.records.find((item) => item.id === element.dataset.cleaningId);
            if (record) openCleaningMemoDialog(record, action === "cleaning-memo-edit", {
              showModal: showLockedModal,
              save: (input) => api<{ id: string; memo: string }>("/api/cleaning-management", {
                method: "POST", body: JSON.stringify({ action: "memo", ...input }),
              }),
              onSaved: (result) => {
                if (state.cleaningData) state.cleaningData.records = state.cleaningData.records.map((item) =>
                  item.id === result.id ? { ...item, memo: result.memo } : item);
                // Invalidate an older GET without clearing the current row selections.
                ++cleaningLoadRequest;
                state.cleaningLoading = false;
                notify("メモを保存しました。");
                if (state.etcWorkspace === "cleaning") render();
              },
            });
          }
          if (state.cleaningData?.records.some((record) => state.cleaningSelectedIds.has(record.id))) {
            if (action === "cleaning-ship" && state.cleaningView === "cleaning") void requestCleaningShipping();
          }
        }
      }
      if (action === "inventory-transfer") void switchEtcWorkspace("inventory-transfer");
      if (action === "return-exchange") void switchEtcWorkspace("return-exchange");
      if (action === "inventory-search") void switchEtcWorkspace("inventory-search");
      if (action === "return-exchange-refresh") void loadCustomerManagementData();
      if (action === "customer-management-active") switchCustomerManagementView("active");
      if (action === "customer-management-history") switchCustomerManagementView("history");
      if (action === "customer-management-edit") {
        if (customerManagementRecordingActive()) {
          void saveCustomerManagementChanges();
        } else {
          openCustomerManagementEditDialog();
        }
      }
      if (action === "customer-management-edit-cancel") cancelCustomerManagementChanges();
      if (action === "history") void toggleHistory();
      if (action === "maintenance") {
        state.maintenanceOpen = !state.maintenanceOpen;
        render();
      }
      if (action === "daily-print") openDailyOrderPrintConfirmation();
      if (action === "inventory-transfer-request") openInventoryTransferRequestDialog();
      if (action === "confirm-unnecessary") openBulkConfirmation("confirm-unnecessary");
      if (action === "inventory-transfer-refresh") void loadInventoryTransferData();
      if (action === "inventory-replenishment-receipt-print") void printSelectedInventoryReplenishments();
      if (action === "inventory-relocation-return") openInventoryRelocationReturnDialog();
      if (action === "inventory-relocation-register") void openInventoryRegistrationDialog();
      if (action === "inventory-replenishment-relocate") openInventoryRelocationDialog();
      if (action === "inventory-replenishment-issue") {
        openInventoryReplenishmentIssueDialog(element.dataset.inventoryReplenishmentId ?? "");
      }
      if (action === "inventory-transfer-replenishment") switchInventoryTransferView("replenishment");
      if (action === "inventory-transfer-relocation") switchInventoryTransferView("relocation");
      if (action === "inventory-transfer-active") switchInventoryTransferView("active");
      if (action === "inventory-transfer-history") switchInventoryTransferView("history");
      if (action === "receive") openBulkConfirmation("receive");
      if (action === "repair-request") openBulkConfirmation("repair-request");
      if (action === "repair-print") openBulkConfirmation("repair-print");
      if (action === "reprint" && state.filter === "received") openBulkConfirmation("reprint");
      if (action === "ship") openBulkConfirmation("ship");
      if (action === "hide") openBulkConfirmation("hide");
      if (action === "record-correction") openRecordCorrectionDialog();
      if (action === "reset") beginResetAction();
      if (action === "bulk-delete") openBulkConfirmation("bulk-delete");
      if (action === "ended-history") void toggleEndedHistory();
    });
  });

  scope.querySelectorAll<HTMLInputElement>("[data-customer-status]").forEach((input) => {
    input.addEventListener("change", () => {
      stageCustomerManagementStatus(input);
    });
  });
  scope.querySelectorAll<HTMLInputElement>("[data-customer-select]").forEach((input) => {
    input.addEventListener("change", () => toggleCustomerManagementSelection(input));
  });
  scope.querySelector<HTMLFormElement>("#customer-management-edit-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void unlockCustomerManagementEdits(event.currentTarget as HTMLFormElement);
  });
  scope.querySelectorAll<HTMLButtonElement>("[data-customer-edit-close]").forEach((button) => {
    button.addEventListener("click", () => {
      document.querySelector<HTMLDialogElement>("#customer-management-edit-dialog")?.close();
    });
  });

  scope.querySelectorAll<HTMLElement>("[data-order-id]").forEach((element) => {
    element.addEventListener("click", (event) => {
      if (blocksOrderDialog(event.target)) return;
      openOrder(element.dataset.orderId ?? "");
    });
    element.addEventListener("keydown", (event) => {
      if (blocksOrderDialog(event.target)) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openOrder(element.dataset.orderId ?? "");
      }
    });
  });

  scope.querySelector<HTMLFormElement>("#maintenance-unlock")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void unlockMaintenance(event.currentTarget as HTMLFormElement);
  });
  scope.querySelector<HTMLFormElement>("#maintenance-search")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const query = String(new FormData(form).get("query") ?? "");
    void searchMaintenanceOrders(query);
  });
  scope.querySelectorAll<HTMLElement>("[data-maintenance-select]").forEach((element) => {
    element.addEventListener("click", () => {
      state.maintenanceSelectedGid = element.dataset.maintenanceSelect ?? null;
      render();
    });
  });
  scope.querySelector<HTMLFormElement>("#maintenance-editor")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void saveMaintenanceOrder(event.currentTarget as HTMLFormElement);
  });
  scope.querySelector<HTMLElement>("[data-maintenance-delete]")?.addEventListener("click", () => {
    void deleteMaintenanceOrder();
  });
  scope.querySelector<HTMLButtonElement>("[data-compliance-refresh]")?.addEventListener("click", () => {
    void refreshComplianceRequests();
  });
  scope.querySelectorAll<HTMLButtonElement>("[data-compliance-export]").forEach((button) => {
    button.addEventListener("click", () => {
      const requestId = button.dataset.complianceExport;
      if (requestId) void exportComplianceRequest(requestId);
    });
  });
  scope.querySelectorAll<HTMLButtonElement>("[data-compliance-complete]").forEach((button) => {
    button.addEventListener("click", () => {
      const requestId = button.dataset.complianceComplete;
      if (requestId) void completeCustomerDataRequest(requestId);
    });
  });
}

async function switchFilter(filter: ListFilter): Promise<void> {
  if (state.isWorkspaceSwitching || state.isFilterSwitching) return;
  const view: DashboardView = orderFilterView(filter);
  if (state.filter === filter && state.view === view) return;
  const needsLoad = state.view !== view || view === "history";
  resetOrderSearch();
  state.isFilterSwitching = true;
  state.filter = filter;
  Object.assign(state, defaultOrderSortSettingsForList(state.workspace, filter));
  state.historyPage = 1;
  state.checkedOrderIds.clear();
  state.repairAtelierSelections.clear();
  state.view = view;
  render();
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const minimumDelay = new Promise<void>((resolve) => window.setTimeout(resolve, 220));
  const loaded = needsLoad
    ? (await Promise.all([loadData(false), minimumDelay]))[0]
    : await minimumDelay.then(() => true);
  state.isFilterSwitching = false;
  if (loaded) render();
}

async function toggleHistory(): Promise<void> {
  resetOrderSearch();
  state.view = state.view === "history" ? "active" : "history";
  state.filter = "all";
  Object.assign(state, defaultOrderSortSettingsForList(state.workspace, state.filter));
  state.historyPage = 1;
  state.checkedOrderIds.clear();
  state.repairAtelierSelections.clear();
  await loadData(false);
}

async function toggleEndedHistory(): Promise<void> {
  if (state.view !== "history") return;
  resetOrderSearch();
  state.filter = state.filter === "ended" ? "all" : "ended";
  Object.assign(state, defaultOrderSortSettingsForList(state.workspace, state.filter));
  state.historyPage = 1;
  state.checkedOrderIds.clear();
  state.repairAtelierSelections.clear();
  await loadData(false);
}

function inventoryImage(
  image: { url: string; altText: string | null } | null,
  fallbackLabel: string,
  className: string,
): string {
  if (!image) {
    return `<span class="${className} inventory-image-empty" aria-hidden="true">NO IMAGE</span>`;
  }
  return `<img class="${className}" src="${escapeHtml(image.url)}"
    alt="${escapeHtml(image.altText || fallbackLabel)}" loading="lazy">`;
}

function positionInventoryPopover(
  dialog: HTMLDialogElement,
  trigger: HTMLButtonElement,
): void {
  const viewportMargin = 12;
  const gap = 14;
  dialog.classList.remove("arrow-bottom");
  dialog.style.left = `${viewportMargin}px`;
  dialog.style.top = `${viewportMargin}px`;

  const triggerRect = trigger.getBoundingClientRect();
  const dialogRect = dialog.getBoundingClientRect();
  const maximumLeft = Math.max(viewportMargin, window.innerWidth - dialogRect.width - viewportMargin);
  const desiredLeft = triggerRect.left + (triggerRect.width / 2) - (dialogRect.width / 2);
  const left = Math.min(maximumLeft, Math.max(viewportMargin, desiredLeft));
  const roomBelow = window.innerHeight - triggerRect.bottom - viewportMargin;
  const roomAbove = triggerRect.top - viewportMargin;
  const showAbove = roomBelow < Math.min(dialogRect.height, 360) && roomAbove > roomBelow;
  const top = showAbove
    ? Math.max(viewportMargin, triggerRect.top - dialogRect.height - gap)
    : Math.min(
      Math.max(viewportMargin, triggerRect.bottom + gap),
      Math.max(viewportMargin, window.innerHeight - dialogRect.height - viewportMargin),
    );
  const arrowLeft = Math.min(
    Math.max(26, triggerRect.left + (triggerRect.width / 2) - left),
    Math.max(26, dialogRect.width - 26),
  );

  if (showAbove) dialog.classList.add("arrow-bottom");
  dialog.style.left = `${Math.round(left)}px`;
  dialog.style.top = `${Math.round(top)}px`;
  dialog.style.setProperty("--inventory-arrow-left", `${Math.round(arrowLeft)}px`);
}

function sizeInventoryPopover(dialog: HTMLDialogElement): void {
  const locationCount = dialog.querySelectorAll(
    ".inventory-stock-table thead th:not(:first-child)",
  ).length;
  const preferredWidth = 760 + (Math.max(0, locationCount - 2) * 86);
  dialog.style.setProperty("--inventory-popover-width", `${preferredWidth}px`);
}

function scrollSelectedInventoryVariant(dialog: HTMLDialogElement): void {
  window.requestAnimationFrame(() => {
    if (!dialog.open) return;
    const panel = dialog.querySelector<HTMLElement>(".inventory-stock-panel");
    const selectedRow = dialog.querySelector<HTMLElement>(
      ".inventory-stock-table tbody tr.is-selected",
    );
    if (!panel || !selectedRow || panel.scrollHeight <= panel.clientHeight + 1) return;

    const panelRect = panel.getBoundingClientRect();
    const selectedRect = selectedRow.getBoundingClientRect();
    const centeredTop = panel.scrollTop
      + selectedRect.top
      - panelRect.top
      - ((panel.clientHeight - selectedRect.height) / 2);
    panel.scrollTo({ top: Math.max(0, centeredTop), behavior: "auto" });
  });
}

function inventoryPopoverContent(inventory: InventoryPopoverData): string {
  const displayVariants = inventory.variants.filter((variant) => variant.sku.trim() !== "");
  const locationLabel = (name: string): string => {
    const normalized = name.normalize("NFKC");
    if (normalized.includes("大阪")) return "大阪";
    if (normalized.includes("東京")) return "東京";
    return name;
  };
  const locationColumns = inventoryStockColumns(displayVariants);
  const variants = displayVariants.map((variant) => {
    const cells = locationColumns.map((location) => {
      const available = location.id === "__total__"
        ? variant.totalAvailable
        : variant.locations.find((entry) => entry.id === location.id)?.available ?? 0;
      const tone = available === null
        ? "untracked"
        : available > 0
        ? "in-stock"
        : "out-of-stock";
      const totalClass = location.isTotal ? " inventory-total" : "";
      return `<td><span class="inventory-stock-dot ${tone}${totalClass}">${available === null ? "—" : available}</span></td>`;
    }).join("");
    return `<tr class="${variant.id === inventory.selectedVariantId ? "is-selected" : ""}">
      <th scope="row">
        <code class="inventory-sku-pill" title="${escapeHtml(variant.title)}">${escapeHtml(variant.sku)}</code>
      </th>
      ${cells}
    </tr>`;
  }).join("");
  const truncatedLocations = displayVariants.some((variant) => variant.locationsTruncated);

  const notices = [
    inventory.duplicateSkuMatches > 1
      ? `同じSKUの商品が${inventory.duplicateSkuMatches}件あるため、注文の商品名に最も近い商品を表示しています。`
      : "",
    inventory.variantsTruncated
      ? "バリエーション件数が取得上限を超えたため、一部を表示しています。"
      : "",
    truncatedLocations
      ? "一部のバリエーションには、ほかの在庫ロケーションがあります。"
      : "",
  ].filter(Boolean);

  return `<div class="inventory-popover-card">
    <header class="inventory-popover-head">
      <div><p>LIVE INVENTORY</p><h2>${escapeHtml(inventory.productTitle)}</h2></div>
      <button class="inventory-close" type="button" data-close-inventory aria-label="閉じる">×</button>
    </header>
    <div class="inventory-popover-body">
      <div class="inventory-product-visual">
        ${inventoryImage(inventory.productImage, inventory.productTitle, "inventory-product-image")}
      </div>
      <div class="inventory-stock-panel">
        ${variants
          ? `<table class="inventory-stock-table">
              <thead><tr>
                <th scope="col">SKU</th>
                ${locationColumns.map((location) =>
                  `<th scope="col"><span class="inventory-location-label${location.isTotal ? " inventory-total" : ""}">${escapeHtml(locationLabel(location.name))}</span></th>`,
                ).join("")}
              </tr></thead>
              <tbody>${variants}</tbody>
            </table>`
          : '<p class="inventory-empty">SKUが設定されたバリエーションはありません。</p>'}
        ${notices.length > 0 ? `<div class="inventory-notices">${notices.map((notice) => `<p>${escapeHtml(notice)}</p>`).join("")}</div>` : ""}
      </div>
    </div>
  </div>`;
}

async function openInventoryPopover(
  sku: string,
  lineItemTitle: string,
  trigger: HTMLButtonElement,
): Promise<void> {
  const dialog = document.querySelector<HTMLDialogElement>("#inventory-popover");
  if (!dialog) return;
  if (dialog.open) dialog.close();
  dialog.style.removeProperty("--inventory-popover-width");
  dialog.innerHTML = `<div class="inventory-popover-card">
    <header class="inventory-popover-head inventory-loading-head">
      <div class="inventory-loading-mark" aria-hidden="true"></div>
      <div><p>LIVE INVENTORY</p><h2>${escapeHtml(sku)}</h2><span>Shopifyから最新在庫を確認中…</span></div>
      <button class="inventory-close" type="button" data-close-inventory aria-label="閉じる">×</button>
    </header>
    <div class="inventory-loading" role="status"><span></span>商品写真・バリエーション・ロケーション在庫を読み込んでいます</div>
  </div>`;
  dialog.querySelector("[data-close-inventory]")?.addEventListener("click", () => dialog.close());
  dialog.onclick = (event) => {
    if (event.target === dialog) dialog.close();
  };
  showLockedModal(dialog);
  positionInventoryPopover(dialog, trigger);

  try {
    const result = await api<{ inventory: InventoryPopoverData }>("/api/inventory", {
      method: "POST",
      body: JSON.stringify({ sku, lineItemTitle }),
    });
    if (!dialog.open) return;
    dialog.innerHTML = inventoryPopoverContent(result.inventory);
    dialog.querySelector("[data-close-inventory]")?.addEventListener("click", () => dialog.close());
    sizeInventoryPopover(dialog);
    positionInventoryPopover(dialog, trigger);
    scrollSelectedInventoryVariant(dialog);
  } catch (error) {
    if (!dialog.open) return;
    dialog.style.removeProperty("--inventory-popover-width");
    dialog.innerHTML = `<div class="inventory-popover-card">
      <header class="inventory-popover-head inventory-error-head">
        <div class="inventory-error-mark" aria-hidden="true">!</div>
        <div><p>INVENTORY ERROR</p><h2>${escapeHtml(sku)}</h2><span>在庫情報を表示できませんでした</span></div>
        <button class="inventory-close" type="button" data-close-inventory aria-label="閉じる">×</button>
      </header>
      <p class="inventory-error">${escapeHtml(error instanceof Error ? error.message : String(error))}</p>
    </div>`;
    dialog.querySelector("[data-close-inventory]")?.addEventListener("click", () => dialog.close());
    positionInventoryPopover(dialog, trigger);
  }
}

type OrderItemTone = "unshipped" | "shipped" | "cancelled";

interface OrderLineItemRowOptions {
  properties?: Array<{ name: string; value: string; continued?: boolean }>;
  showItemIdentity?: boolean;
  showSkuSuffix?: boolean;
}

function lineItemPropertiesHtml(
  properties: Array<{ name: string; value: string; continued?: boolean }>,
): string {
  return properties.length > 0
    ? `<dl class="line-item-properties">${properties.map((property) =>
      `<div><dt>${escapeHtml(property.name)}${property.continued ? "（続き）" : ""}</dt><dd>${escapeHtml(property.value || "（空欄）")}</dd></div>`,
    ).join("")}</dl>`
    : "";
}

function orderLineItemRowHtml(
  item: LineItem,
  displayQuantity: number,
  tone: OrderItemTone,
  options: OrderLineItemRowOptions = {},
): string {
  const displayProperties: Array<{ name: string; value: string; continued?: boolean }> =
    options.properties ?? item.properties;
  const properties = lineItemPropertiesHtml(displayProperties);
  const showItemIdentity = options.showItemIdentity !== false;
  const skuSuffix = !showItemIdentity || options.showSkuSuffix === false
    ? ""
    : `<div class="item-thumb">${escapeHtml(item.sku.slice(-2) || "—")}</div>`;
  return `<div class="detail-item ${tone}${showItemIdentity ? "" : " receive-pdf-item-continuation"}">
    ${skuSuffix}
    <div class="item-desc">
      ${showItemIdentity
        ? `<code>${escapeHtml(item.sku || "SKUなし")}</code>
          <strong>${escapeHtml(item.title)}</strong>
          <small>${escapeHtml(item.variantTitle ?? "バリエーションなし")}</small>`
        : ""}
      ${properties}
    </div>
    ${showItemIdentity
      ? `<span>× ${displayQuantity}</span><b>${formatMoney(item.unitPrice, item.currencyCode)}</b>`
      : ""}
  </div>`;
}

function orderLineItemRowsHtml(
  segments: Array<{ item: LineItem; quantity: number }>,
  tone: OrderItemTone,
): string {
  return segments.map(({ item, quantity }) =>
    orderLineItemRowHtml(item, quantity, tone)
  ).join("");
}

interface ReceivePdfFragment {
  sectionKey: string;
  sectionLabel: string;
  sectionClassName: string;
  html: string;
}

interface ReceivePdfPageSpec {
  order: DashboardOrder;
  bodyHtml: string;
  orderPageNumber: number;
  orderPageCount: number;
  showInspectionFooter: boolean;
}

function receivePdfItemFragments(
  sectionKey: string,
  sectionLabel: string,
  sectionClassName: string,
  segments: Array<{ item: LineItem; quantity: number }>,
  tone: OrderItemTone,
  emptyMessage: string,
  propertySelector: (item: LineItem) => Array<{ name: string; value: string }> =
    (item) => item.properties,
): ReceivePdfFragment[] {
  if (segments.length === 0) {
    return [{
      sectionKey,
      sectionLabel,
      sectionClassName,
      html: `<p class="detail-items-empty">${escapeHtml(emptyMessage)}</p>`,
    }];
  }

  return segments.flatMap(({ item, quantity }) => {
    const propertyGroups = groupReceivePdfProperties(propertySelector(item));
    const groups = propertyGroups.length > 0 ? propertyGroups : [[]];
    return groups.map((properties, index) => ({
      sectionKey,
      sectionLabel,
      sectionClassName,
      html: orderLineItemRowHtml(item, quantity, tone, {
        properties,
        showItemIdentity: index === 0,
        showSkuSuffix: false,
      }),
    }));
  });
}

function receivePdfNoteFragments(
  sectionKey: string,
  sectionLabel: string,
  value: string,
  emptyMessage: string,
): ReceivePdfFragment[] {
  const chunks = value
    ? splitReceivePdfText(value, RECEIVE_PDF_NOTE_CHARS)
    : [emptyMessage];
  return chunks.map((chunk) => ({
    sectionKey,
    sectionLabel,
    sectionClassName: "receive-pdf-note-section",
    html: `<div class="receive-pdf-note-block"><p>${escapeHtml(chunk)}</p></div>`,
  }));
}

function receivePdfTrailingFragments(
  order: DashboardOrder,
  groupedItems: GroupedOrderLineItems<LineItem>,
): ReceivePdfFragment[] {
  const cancelledQuantity = groupedItems.cancelled.reduce(
    (sum, segment) => sum + segment.quantity,
    0,
  );
  return [
    {
      sectionKey: "shipped-summary",
      sectionLabel: "",
      sectionClassName: "receive-pdf-shipped-summary-section",
      html: `<p class="receive-pdf-shipped-summary">${formatReceivePdfShippedSummary(groupedItems.shipped.length)}</p>`,
    },
    ...(groupedItems.cancelled.length > 0
      ? receivePdfItemFragments(
          "cancelled",
          `取消・返金済みの商品 ${cancelledQuantity}点`,
          "receive-pdf-cancelled",
          groupedItems.cancelled,
          "cancelled",
          "",
        )
      : []),
    ...receivePdfNoteFragments(
      "order-note",
      "Shopify注文メモ",
      order.orderNote,
      "注文メモはありません。",
    ),
    ...receivePdfNoteFragments(
      "memo",
      "作業メモ",
      order.memo,
      "作業メモはありません。",
    ),
  ];
}

function receivePdfCommonPropertyFragments(
  properties: Array<{ name: string; value: string }>,
): ReceivePdfFragment[] {
  return groupReceivePdfProperties(properties).map((propertyGroup) => ({
    sectionKey: "final-item-notes",
    sectionLabel: "備考欄・メモ等",
    sectionClassName: "receive-pdf-common-notes-section",
    html: `<div class="receive-pdf-common-properties">${lineItemPropertiesHtml(propertyGroup)}</div>`,
  }));
}

function receivePdfSizeOrderFinalFragments(
  order: DashboardOrder,
  groupedItems: GroupedOrderLineItems<LineItem>,
  itemSegments: Array<{ item: LineItem; quantity: number }>,
): ReceivePdfFragment[] {
  const finalNoteProperties = itemSegments.flatMap(({ item }) =>
    partitionReceivePdfProperties(item.properties).finalNoteProperties
  );
  const cancelledQuantity = groupedItems.cancelled.reduce(
    (sum, segment) => sum + segment.quantity,
    0,
  );
  return [
    ...receivePdfCommonPropertyFragments(finalNoteProperties),
    {
      sectionKey: "shipped-summary",
      sectionLabel: "",
      sectionClassName: "receive-pdf-shipped-summary-section",
      html: `<p class="receive-pdf-shipped-summary">${formatReceivePdfShippedSummary(groupedItems.shipped.length)}</p>`,
    },
    ...(cancelledQuantity > 0
      ? [{
          sectionKey: "cancelled-summary",
          sectionLabel: "",
          sectionClassName: "receive-pdf-shipped-summary-section",
          html: `<p class="receive-pdf-shipped-summary">取消・返金済みの商品：${cancelledQuantity}点</p>`,
        }]
      : []),
    ...(hasReceivePdfNoteContent(order.customerRequest)
      ? receivePdfNoteFragments(
          "customer-request",
          "お客様ご要望",
          order.customerRequest,
          "",
        )
      : []),
    ...(hasReceivePdfNoteContent(order.includedItems)
      ? receivePdfNoteFragments(
          "included-items",
          "同梱品",
          order.includedItems,
          "",
        )
      : []),
    ...(hasReceivePdfNoteContent(order.orderNote)
      ? receivePdfNoteFragments(
          "order-note",
          "Shopify注文メモ",
          order.orderNote,
          "",
        )
      : []),
    ...(hasReceivePdfNoteContent(order.memo)
      ? receivePdfNoteFragments(
          "memo",
          "作業メモ",
          order.memo,
          "",
        )
      : []),
  ];
}

function receivePdfFragments(order: DashboardOrder): ReceivePdfFragment[] {
  const groupedItems = groupOrderLineItems(order.items, order.shopifyFulfillmentStatus);
  return [
    ...receivePdfItemFragments(
      "unshipped",
      "未出荷の商品",
      "unshipped-section",
      groupedItems.unshipped,
      "unshipped",
      "未出荷の商品はありません。",
    ),
    ...receivePdfTrailingFragments(order, groupedItems),
  ];
}

interface ReceivePdfFragmentGroup {
  fragments: ReceivePdfFragment[];
  showInspectionFooterOnLastPage: boolean;
}

function receivePdfBundledItemFragments(
  segments: Array<{ item: LineItem; quantity: number }>,
): ReceivePdfFragment[] {
  return segments.flatMap(({ item, quantity }) => {
    const variant = item.variantTitle?.trim() ?? "";
    const label = [item.title,
      variant && variant !== "Default Title" && !item.title.includes(variant) ? variant : "",
      item.sku,
    ].filter(Boolean).join(" ／ ");
    return splitReceivePdfText(label, 360, 6).map((text, index) => ({
      sectionKey: "bundled-line-items",
      sectionLabel: "同梱品：",
      sectionClassName: "receive-pdf-bundled-items",
      html: `<div class="receive-pdf-bundled-item"><div>${escapeHtml(text)}</div><span>${index === 0 ? `× ${quantity}` : ""}</span></div>`,
    }));
  });
}

function receivePdfSizeOrderFragmentGroups(order: DashboardOrder): ReceivePdfFragmentGroup[] {
  const groupedItems = groupOrderLineItems(order.items, order.shopifyFulfillmentStatus);
  const targetItemIds = new Set(
    orderListItemsForWorkspace("size", order.sourceName, order.items).map((item) => item.id),
  );
  const { itemSegments, bundledSegments } = partitionReceivePdfItemSegments(groupedItems.unshipped, targetItemIds);
  const bundledFragments = receivePdfBundledItemFragments(bundledSegments);
  const finalFragments = receivePdfSizeOrderFinalFragments(order, groupedItems, itemSegments);
  const customerSectionKeys = new Set([
    "customer-request",
    "included-items",
    "order-note",
    "memo",
  ]);
  const itemGroups = itemSegments.map((segment, index) => ({
    fragments: [...receivePdfItemFragments(
      `unshipped-${segment.item.id}`,
      "未出荷の商品",
      "unshipped-section",
      [segment],
      "unshipped",
      "未出荷の商品はありません。",
      (item) => partitionReceivePdfProperties(item.properties).itemProperties,
    ), ...(index === 0 ? bundledFragments : [])],
    showInspectionFooterOnLastPage: true,
  }));
  if (!allowsDetachedSizeOrderPdfInspectionFooter(order.salesSource, itemSegments.length)) {
    const supplementalFragments = finalFragments.filter(
      (fragment) => !customerSectionKeys.has(fragment.sectionKey),
    );
    const customerFragments = finalFragments.filter(
      (fragment) => customerSectionKeys.has(fragment.sectionKey),
    );
    return [{
      fragments: [
        ...itemGroups.flatMap((group) => group.fragments),
        ...supplementalFragments,
        ...customerFragments,
      ],
      showInspectionFooterOnLastPage: true,
    }];
  }
  return [
    ...itemGroups,
    {
      fragments: finalFragments,
      showInspectionFooterOnLastPage: false,
    },
  ];
}

function receivePdfPageHtml(
  order: DashboardOrder,
  bodyHtml: string,
  showInspectionFooter = false,
  layout: ReceivePdfLayout = "size-order",
): string {
  const displayOrderNumber = order.name.replace(/^#/, "");
  const layoutOptions = receivePdfLayoutOptions(layout);
  const repairLayout = layout === "repair";
  const badgesHtml = repairLayout
    ? `<div class="receive-pdf-badges receive-pdf-repair-badges">
        <h2>${escapeHtml(layoutOptions.instructionTitle)}</h2>
        <div class="receive-pdf-labeled-badge"><small>販売元</small>${salesSourceBadge(order.salesSource)}</div>
        <div class="receive-pdf-labeled-badge"><small>処理状況</small>${workflowBadge(order.csOrderStatus)}</div>
      </div>`
    : `<div class="receive-pdf-badges">
        <div class="receive-pdf-source-stack">${salesSourceBadge(order.salesSource)}${order.isExpress ? '<span class="dialog-express-badge">特急</span>' : ""}</div>
        ${shopifyStatusBadge(order.shopifyFulfillmentStatus)}
      </div>`;

  return `<article class="receive-pdf-page${repairLayout ? " receive-pdf-repair-page" : ""}">
    <header class="receive-pdf-head">
      <div class="receive-pdf-identity">
        <p>ORDER DETAIL</p>
        <h1>${escapeHtml(displayOrderNumber)}</h1>
        <span>${escapeHtml(order.customerName || "ゲスト注文")} 様 · ${formatDate(order.createdAt)}</span>
      </div>
      ${badgesHtml}
      <div class="receive-pdf-meta-stack">
        <dl class="receive-pdf-head-meta">
          <div><dt>お渡し方法</dt><dd>${escapeHtml(order.deliveryMethod || "—")}</dd></div>
          <div><dt>都道府県</dt><dd>${escapeHtml(order.shippingProvince || "—")}</dd></div>
          <div><dt>入荷次第連絡</dt><dd>${escapeHtml(order.arrivalContact || "—")}</dd></div>
          <div><dt>決済状況</dt><dd>${paymentStatusBadge(order.financialStatus)}</dd></div>
        </dl>
        <dl class="receive-pdf-schedule-meta">
          <div><dt>配送希望日</dt><dd>${escapeHtml(order.requestedDeliveryDate || "—")}</dd></div>
          <div><dt>着用予定日</dt><dd>${escapeHtml(order.wearingDate || "—")}</dd></div>
        </dl>
      </div>
    </header>
    <div class="receive-pdf-content-frame">
      <div class="receive-pdf-content">${bodyHtml}</div>
    </div>
    ${layoutOptions.showInspectionFooter ? `<footer class="receive-pdf-inspection ${showInspectionFooter ? "" : "is-placeholder"}">
      <div class="receive-pdf-atelier" aria-label="アトリエ作業記入欄">
        <strong>アトリエ作業</strong>
        <div>
          ${RECEIVE_PDF_ATELIER_TASKS.map((task) =>
            `<span><i aria-hidden="true"></i>${escapeHtml(task)}</span>`
          ).join("")}
        </div>
      </div>
      <div class="receive-pdf-additional-options" aria-label="追加オプション記入欄">
        <strong>追加オプション</strong>
        <div>
          ${RECEIVE_PDF_ADDITIONAL_OPTIONS.map((option) =>
            `<span><i aria-hidden="true"></i>${escapeHtml(option)}</span>`
          ).join("")}
        </div>
      </div>
      <table aria-label="入荷商品の検品記入欄">
        <colgroup>
          <col class="inspection-label-wide">
          <col class="inspection-check">
          <col class="inspection-check">
          <col class="inspection-label">
          <col class="inspection-check">
          <col class="inspection-check">
          <col class="inspection-label">
          <col class="inspection-check">
          <col class="inspection-check">
        </colgroup>
        <tbody>
          <tr>
            <th scope="row">サイズと下げ札</th>
            <td>1回目</td>
            <td>2回目</td>
            <th scope="row">ファスナー</th>
            <td>1回目</td>
            <td>2回目</td>
            <th scope="row">縫ホック</th>
            <td>1回目</td>
            <td>2回目</td>
          </tr>
          <tr>
            <th scope="row">肩紐・ループ</th>
            <td>1回目</td>
            <td>2回目</td>
            <th scope="row">採寸</th>
            <td colspan="5">印</td>
          </tr>
        </tbody>
      </table>
    </footer>` : ""}
  </article>`;
}

function addReceivePdfFragment(
  content: HTMLElement,
  fragment: ReceivePdfFragment,
  continuedSection: boolean,
): HTMLElement {
  let section = content.lastElementChild instanceof HTMLElement
    && content.lastElementChild.dataset.receivePdfSection === fragment.sectionKey
    ? content.lastElementChild
    : null;
  if (!section) {
    section = document.createElement("section");
    section.className = `receive-pdf-section ${fragment.sectionClassName}`.trim();
    section.dataset.receivePdfSection = fragment.sectionKey;
    if (fragment.sectionLabel) {
      const heading = document.createElement("h2");
      heading.textContent = `${fragment.sectionLabel}${continuedSection ? "（続き）" : ""}`;
      section.appendChild(heading);
    }
    content.appendChild(section);
  }
  const fragmentElement = document.createElement("div");
  fragmentElement.className = "receive-pdf-fragment";
  fragmentElement.innerHTML = fragment.html;
  section.appendChild(fragmentElement);
  return fragmentElement;
}

function paginateReceivePdfFragments(
  renderHost: HTMLElement,
  order: DashboardOrder,
  layout: ReceivePdfLayout,
  fragments: ReceivePdfFragment[],
): string[] {
  const pageBodies: string[] = [];
  const emittedSections = new Set<string>();
  let frame!: HTMLElement;
  let content!: HTMLElement;

  const resetPage = (): void => {
    renderHost.innerHTML = receivePdfPageHtml(order, "", false, layout);
    const nextPage = renderHost.querySelector<HTMLElement>(".receive-pdf-page");
    const nextFrame = renderHost.querySelector<HTMLElement>(".receive-pdf-content-frame");
    const nextContent = renderHost.querySelector<HTMLElement>(".receive-pdf-content");
    if (!nextPage || !nextFrame || !nextContent) {
      throw new Error(`${order.name}のPDFページを準備できませんでした。`);
    }
    frame = nextFrame;
    content = nextContent;
  };

  const saveCurrentPage = (): void => {
    if (content.querySelector(".receive-pdf-fragment")) {
      pageBodies.push(content.innerHTML);
    }
  };

  resetPage();
  for (const fragment of fragments) {
    const fragmentElement = addReceivePdfFragment(
      content,
      fragment,
      emittedSections.has(fragment.sectionKey),
    );
    if (content.scrollHeight > frame.clientHeight + 1) {
      const section = fragmentElement.parentElement;
      fragmentElement.remove();
      if (section && !section.querySelector(".receive-pdf-fragment")) section.remove();
      saveCurrentPage();
      resetPage();
      const nextFragmentElement = addReceivePdfFragment(
        content,
        fragment,
        emittedSections.has(fragment.sectionKey),
      );
      if (content.scrollHeight > frame.clientHeight + 1) {
        nextFragmentElement.classList.add("receive-pdf-fragment-condensed");
      }
      if (content.scrollHeight > frame.clientHeight + 1) {
        throw new Error(`${order.name}の長いメモまたは商品情報をB5ページへ分割できませんでした。`);
      }
    }
    emittedSections.add(fragment.sectionKey);
  }
  saveCurrentPage();

  return pageBodies;
}

function paginateReceivePdfOrder(
  renderHost: HTMLElement,
  order: DashboardOrder,
  layout: ReceivePdfLayout,
): ReceivePdfPageSpec[] {
  const fragmentGroups = layout === "size-order"
    ? receivePdfSizeOrderFragmentGroups(order)
    : [{
        fragments: receivePdfFragments(order),
        showInspectionFooterOnLastPage: false,
      }];
  const pageBodyGroups = fragmentGroups.map((group) =>
    paginateReceivePdfFragments(renderHost, order, layout, group.fragments)
  );
  const pageBodies = pageBodyGroups.flat();
  const inspectionFooterFlags = receivePdfInspectionFooterFlags(
    pageBodyGroups.map((pages, index) => ({
      pageCount: pages.length,
      showInspectionFooterOnLastPage:
        fragmentGroups[index]?.showInspectionFooterOnLastPage ?? false,
    })),
  );

  const orderPageCount = pageBodies.length;
  return pageBodies.map((bodyHtml, index) => ({
    order,
    bodyHtml,
    orderPageNumber: index + 1,
    orderPageCount,
    showInspectionFooter: inspectionFooterFlags[index] ?? false,
  }));
}

interface ReceivePdfBuildResult {
  blob: Blob;
  filename: string;
  pageCount: number;
}

function monochromeCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const context = source.getContext("2d");
  if (!context) throw new Error("PDFをモノクロ変換できませんでした。");
  const imageData = context.getImageData(0, 0, source.width, source.height);
  const pixels = imageData.data;
  for (let index = 0; index < pixels.length; index += 4) {
    const red = pixels[index] ?? 255;
    const green = pixels[index + 1] ?? 255;
    const blue = pixels[index + 2] ?? 255;
    const alpha = (pixels[index + 3] ?? 255) / 255;
    const luminance = (red * 0.2126) + (green * 0.7152) + (blue * 0.0722);
    const whiteComposited = 255 - ((255 - luminance) * alpha);
    const monochrome = whiteComposited < 235 ? 0 : 255;
    pixels[index] = monochrome;
    pixels[index + 1] = monochrome;
    pixels[index + 2] = monochrome;
    pixels[index + 3] = 255;
  }
  context.putImageData(imageData, 0, 0);
  return source;
}

interface DailyOrderPrintPageEntry {
  order: DashboardOrder;
  items: LineItem[];
  continued: boolean;
}

function dailyOrderPrintPages(orders: DashboardOrder[]): DailyOrderPrintPageEntry[][] {
  const pages: DailyOrderPrintPageEntry[][] = [];
  let page: DailyOrderPrintPageEntry[] = [];
  let pageRows = 0;
  const flushPage = (): void => {
    if (page.length > 0) pages.push(page);
    page = [];
    pageRows = 0;
  };

  for (const order of orders) {
    const items = orderListItemsForWorkspace("all", order.sourceName, order.items);
    let offset = 0;
    while (offset < items.length) {
      if (pageRows >= DAILY_ORDER_PRINT_ROWS_PER_PAGE) flushPage();
      const availableRows = DAILY_ORDER_PRINT_ROWS_PER_PAGE - pageRows;
      const chunk = items.slice(offset, offset + availableRows);
      page.push({ order, items: chunk, continued: offset > 0 });
      pageRows += chunk.length;
      offset += chunk.length;
      if (pageRows >= DAILY_ORDER_PRINT_ROWS_PER_PAGE) flushPage();
    }
  }
  flushPage();
  return pages;
}

function formatTokyoPdfDateTime(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function dailyOrderPrintRowsHtml(entries: DailyOrderPrintPageEntry[]): string {
  return entries.map((entry) => {
    const { order } = entry;
    const cancellation = summarizeOrderCancellation(order.items, order.shopifyFulfillmentStatus);
    const cancellationText = orderCancellationLabel(cancellation);
    const cancelledQuantityByItemId = new Map(
      cancellation.partiallyCancelled
        ? cancellation.cancelled.map((segment) => [segment.item.id, segment.quantity] as const)
        : [],
    );
    const rowSpan = entry.items.length;
    return entry.items.map((item, index) => {
      const cancelledQuantity = cancelledQuantityByItemId.get(item.id) ?? 0;
      const returnRequired = cancelledQuantity <= 0 && requiresInventoryReturn(
        order.salesSource,
        item.consumedInventoryLocations ?? [],
      );
      const orderCells = index === 0
        ? `<td rowspan="${rowSpan}" class="daily-print-date-cell">${escapeHtml(formatTokyoPdfDateTime(order.createdAt))}</td>
          <td rowspan="${rowSpan}" class="daily-print-order-cell${cancellationText ? " is-cancelled" : ""}">
            <strong>${escapeHtml(order.name.replace(/^#/, ""))}${entry.continued ? "（続き）" : ""}</strong>
            <span>${escapeHtml(order.customerName || "ゲスト注文")} 様</span>
            ${cancellationText ? `<em class="daily-print-cancellation-badge">${escapeHtml(cancellationText)}</em>` : ""}
          </td>
          <td rowspan="${rowSpan}" class="daily-print-source-cell">${salesSourceBadge(order.salesSource)}</td>
          <td rowspan="${rowSpan}">${escapeHtml(order.deliveryMethod || "—")}</td>
          <td rowspan="${rowSpan}" class="daily-print-schedule-cell"><span>配送 ${escapeHtml(order.requestedDeliveryDate || "—")}</span><span>着用 ${escapeHtml(order.wearingDate || "—")}</span></td>`
        : "";
      const provinceCell = index === 0
        ? `<td rowspan="${rowSpan}">${escapeHtml(order.shippingProvince || "—")}</td>`
        : "";
      const rowClasses = [
        "daily-print-item-row",
        index > 0 ? "daily-print-order-continuation" : "",
        index < entry.items.length - 1 ? "daily-print-order-has-next" : "daily-print-order-end",
      ].filter(Boolean).join(" ");
      return `<tr class="${rowClasses}">
        ${orderCells}
        <td class="daily-print-item-cell daily-print-sku-cell"><strong class="${cancelledQuantity > 0 ? "partial-cancelled-sku" : returnRequired ? "return-required-sku" : ""}">${escapeHtml(item.sku.trim() || "SKUなし")}</strong>${dailyOrderPrintInventoryMismatchBadgeHtml(returnRequired)}${cancelledQuantity > 0 ? `<em class="daily-print-sku-cancellation-badge">品取消${cancelledQuantity > 1 ? ` ×${cancelledQuantity}` : ""}</em>` : ""}</td>
        <td class="daily-print-item-cell daily-print-size-cell"><span class="daily-print-cell-text">${escapeHtml(orderListItemSize(item) || "—")}</span></td>
        <td class="daily-print-item-cell daily-print-name-cell"><span class="daily-print-cell-text">${escapeHtml(item.title.trim() || "—")}</span></td>
        <td class="daily-print-item-cell daily-print-consumption-cell"><span class="daily-print-cell-text${returnRequired ? " daily-print-origin-mismatch-consumption" : ""}">${escapeHtml(inventoryConsumptionDisplay(item))}</span></td>
        <td class="daily-print-item-cell daily-print-inventory-cell"><span class="daily-print-cell-text">${escapeHtml(inventoryRemainingDisplay(item))}</span></td>
        ${provinceCell}
      </tr>`;
    }).join("");
  }).join("");
}

function dailyOrderPrintPageHtml(
  entries: DailyOrderPrintPageEntry[],
  pageNumber: number,
  pageCount: number,
  createdAt: Date,
  orderCount: number,
  conditionLabel: string,
): string {
  return `<article class="daily-order-print-page">
    <header class="daily-order-print-head">
      <div><p>DAILY ORDER LIST</p><h1>日々注文リスト</h1></div>
      <dl>
        <div><dt>抽出条件</dt><dd>${escapeHtml(conditionLabel)}</dd></div>
        <div><dt>対象注文</dt><dd>${orderCount}件</dd></div>
        <div><dt>作成日時</dt><dd>${escapeHtml(formatTokyoPdfDateTime(createdAt))}</dd></div>
      </dl>
    </header>
    <table class="daily-order-print-table">
      <colgroup>
        <col class="daily-print-col-date"><col class="daily-print-col-order"><col class="daily-print-col-source">
        <col class="daily-print-col-delivery"><col class="daily-print-col-schedule"><col class="daily-print-col-sku">
        <col class="daily-print-col-size"><col class="daily-print-col-name"><col class="daily-print-col-consumption"><col class="daily-print-col-inventory">
        <col class="daily-print-col-province">
      </colgroup>
      <thead><tr>
        <th>注文日時</th><th>注文・注文者</th><th>販売元</th><th>お渡し方法</th><th>配送／着用日</th>
        <th>対象SKU</th><th>サイズ</th><th>特定名</th><th>消耗在庫</th><th>残り在庫</th><th>都道府県</th>
      </tr></thead>
      <tbody>${dailyOrderPrintRowsHtml(entries)}</tbody>
    </table>
    <footer><span>TWEED CS Order</span><strong>${pageNumber} / ${pageCount}</strong></footer>
  </article>`;
}

function buildDailyOrderHtmlPrintPages(
  orders: DashboardOrder[],
  createdAt: Date,
  conditionLabel: string,
): string[] {
  const pages = dailyOrderPrintPages(orders);
  if (pages.length === 0) throw new Error("印刷する注文がありません。");
  return pages.map((entries, index) =>
    dailyOrderPrintPageHtml(
      entries,
      index + 1,
      pages.length,
      createdAt,
      orders.length,
      conditionLabel,
    )
  );
}

async function buildReceiveHtmlPrintPages(
  orders: DashboardOrder[],
  layout: ReceivePdfLayout,
): Promise<string[]> {
  const renderHost = document.createElement("div");
  renderHost.className = "receive-pdf-render-host";
  renderHost.setAttribute("aria-hidden", "true");
  document.body.appendChild(renderHost);
  try {
    await document.fonts.ready;
    const pages = orders.flatMap((order) =>
      paginateReceivePdfOrder(renderHost, order, layout)
    );
    if (pages.length === 0) throw new Error("印刷する注文がありません。");
    return pages.map((pageSpec) =>
      receivePdfPageHtml(
        pageSpec.order,
        pageSpec.bodyHtml,
        pageSpec.showInspectionFooter,
        layout,
      )
    );
  } finally {
    renderHost.remove();
  }
}

async function buildDailyOrderPrintPdf(
  orders: DashboardOrder[],
  createdAt: Date,
  onPage: (current: number, total: number) => void,
  conditionLabel: string,
): Promise<ReceivePdfBuildResult> {
  const pages = dailyOrderPrintPages(orders);
  if (pages.length === 0) throw new Error("PDFへ出力する注文がありません。");
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  await document.fonts.ready;
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const renderHost = document.createElement("div");
  renderHost.className = "daily-order-print-render-host";
  document.body.appendChild(renderHost);

  try {
    for (let index = 0; index < pages.length; index += 1) {
      const entries = pages[index];
      if (!entries) continue;
      onPage(index + 1, pages.length);
      renderHost.innerHTML = dailyOrderPrintPageHtml(
        entries,
        index + 1,
        pages.length,
        createdAt,
        orders.length,
        conditionLabel,
      );
      const page = renderHost.querySelector<HTMLElement>(".daily-order-print-page");
      if (!page) throw new Error("A4 PDFページを準備できませんでした。");
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      const canvas = await html2canvas(page, {
        backgroundColor: "#ffffff",
        height: DAILY_ORDER_PRINT_RENDER_HEIGHT_PX,
        logging: false,
        scale: 2,
        scrollX: 0,
        scrollY: 0,
        useCORS: false,
        width: DAILY_ORDER_PRINT_RENDER_WIDTH_PX,
        windowHeight: DAILY_ORDER_PRINT_RENDER_HEIGHT_PX,
        windowWidth: DAILY_ORDER_PRINT_RENDER_WIDTH_PX,
      });
      if (index > 0) pdf.addPage("a4", "landscape");
      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        DAILY_ORDER_PRINT_PDF_WIDTH_MM,
        DAILY_ORDER_PRINT_PDF_HEIGHT_MM,
        `daily-order-${index + 1}`,
        "FAST",
      );
      canvas.width = 1;
      canvas.height = 1;
    }
    const blob = pdf.output("blob");
    if (blob.size < 1_000) throw new Error("作成されたPDFデータが空です。");
    return {
      blob,
      filename: formatDailyOrderPrintPdfFilename(createdAt),
      pageCount: pages.length,
    };
  } finally {
    renderHost.remove();
  }
}

async function buildReceivePdf(
  orders: DashboardOrder[],
  createdAt: Date,
  onPage: (current: number, total: number, orderName: string) => void,
  filename = formatReceivePdfFilename(createdAt),
  layout: ReceivePdfLayout = "size-order",
): Promise<ReceivePdfBuildResult> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  await document.fonts.ready;
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [RECEIVE_PDF_WIDTH_MM, RECEIVE_PDF_HEIGHT_MM],
    compress: true,
  });
  const renderHost = document.createElement("div");
  renderHost.className = "receive-pdf-render-host";
  document.body.appendChild(renderHost);

  try {
    const pages = orders.flatMap((order) =>
      paginateReceivePdfOrder(renderHost, order, layout)
    );
    if (pages.length === 0) throw new Error("PDFへ出力する注文がありません。");

    for (let index = 0; index < pages.length; index += 1) {
      const pageSpec = pages[index];
      if (!pageSpec) continue;
      const orderPageLabel = pageSpec.orderPageCount > 1
        ? `${pageSpec.order.name}（${pageSpec.orderPageNumber}/${pageSpec.orderPageCount}）`
        : pageSpec.order.name;
      onPage(index + 1, pages.length, orderPageLabel);
      renderHost.innerHTML = receivePdfPageHtml(
        pageSpec.order,
        pageSpec.bodyHtml,
        pageSpec.showInspectionFooter,
        layout,
      );
      const page = renderHost.querySelector<HTMLElement>(".receive-pdf-page");
      if (!page) throw new Error(`${pageSpec.order.name}のPDFページを準備できませんでした。`);
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      const canvas = await html2canvas(page, {
        backgroundColor: "#ffffff",
        height: RECEIVE_PDF_RENDER_HEIGHT_PX,
        logging: false,
        scale: 3,
        scrollX: 0,
        scrollY: 0,
        useCORS: false,
        width: RECEIVE_PDF_RENDER_WIDTH_PX,
        windowHeight: RECEIVE_PDF_RENDER_HEIGHT_PX,
        windowWidth: RECEIVE_PDF_RENDER_WIDTH_PX,
      });
      const monochrome = monochromeCanvas(canvas);
      if (index > 0) {
        pdf.addPage([RECEIVE_PDF_WIDTH_MM, RECEIVE_PDF_HEIGHT_MM], "portrait");
      }
      pdf.addImage(
        monochrome.toDataURL("image/png"),
        "PNG",
        0,
        0,
        RECEIVE_PDF_WIDTH_MM,
        RECEIVE_PDF_HEIGHT_MM,
        `order-${index + 1}`,
        "FAST",
      );
      canvas.width = 1;
      canvas.height = 1;
      monochrome.width = 1;
      monochrome.height = 1;
    }
    const blob = pdf.output("blob");
    if (blob.size < 1_000) throw new Error("作成されたPDFデータが空です。");
    return {
      blob,
      filename,
      pageCount: pages.length,
    };
  } finally {
    renderHost.remove();
  }
}

function downloadReceivePdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function openAfterAlterationOrder(
  order: DashboardOrder,
  dialog: HTMLDialogElement,
): void {
  state.selectedLegacyId = order.id;
  dialog.innerHTML = `<form method="dialog" class="dialog-card">
    <div class="dialog-head order-dialog-head">
      <div class="order-dialog-identity">
        <p>AFTER ALTERATION DETAIL</p>
        <h2>${escapeHtml(order.name)}</h2>
        <span>${escapeHtml(order.customerName || "-")} · ${escapeHtml(formatCalendarDate(order.createdAt))}</span>
      </div>
      <div class="order-dialog-divider" aria-hidden="true"></div>
      <div class="order-dialog-badges">${salesSourceBadge("ネット")}</div>
      <div class="order-dialog-meta">
        <span><small>依頼工房</small><strong>${escapeHtml(order.repairAtelier || "-")}</strong></span>
        <span><small>依頼日</small><strong>${order.repairRequestDate ? escapeHtml(formatCalendarDate(order.repairRequestDate)) : "-"}</strong></span>
        <span><small>後直し入荷日</small><strong>${order.repairInstockDate ? escapeHtml(formatCalendarDate(order.repairInstockDate)) : "-"}</strong></span>
        <span><small>処理状況</small>${order.csOrderStatus ? workflowBadge(order.csOrderStatus) : "-"}</span>
      </div>
      <button class="dialog-close" value="cancel" aria-label="閉じる">×</button>
    </div>
    <div class="dialog-body">
      <section class="dialog-section memo-section">
        <div><h3>作業メモ</h3></div>
        <textarea id="memo-input" maxlength="5000" placeholder="対応状況、確認事項、引き継ぎなどを記録…">${escapeHtml(order.memo)}</textarea>
      </section>
    </div>
    <div class="dialog-actions">
      <span></span>
      <div>
        <button class="button secondary" value="cancel">キャンセル</button>
        <button id="save-memo" class="button primary" type="button">メモを保存</button>
      </div>
    </div>
  </form>`;
  dialog.addEventListener("close", () => {
    state.selectedLegacyId = null;
  }, { once: true });
  dialog.querySelector<HTMLButtonElement>("#save-memo")?.addEventListener("click", (event) => {
    void saveMemo(order, event.currentTarget as HTMLButtonElement);
  });
  if (!dialog.open) showLockedModal(dialog);
}

function openOrder(orderLookupId: string): void {
  const order = state.data?.orders.find((entry) =>
    entry.legacyId === orderLookupId || entry.id === orderLookupId
  );
  const dialog = document.querySelector<HTMLDialogElement>("#order-dialog");
  if (!order || order.recordType === "ended" || !dialog) return;
  if (order.recordType === "after-alteration") {
    openAfterAlterationOrder(order, dialog);
    return;
  }
  state.selectedLegacyId = order.sizeUnitId || order.legacyId;
  const groupedItems = groupOrderLineItems(order.items, order.shopifyFulfillmentStatus);
  const cancelledQuantity = groupedItems.cancelled.reduce(
    (sum, segment) => sum + segment.quantity,
    0,
  );
  const displayOrderNumber = order.name.replace(/^#/, "");
  const allOrdersReadOnly = state.workspace === "all";

  dialog.innerHTML = `<form method="dialog" class="dialog-card">
    <div class="dialog-head order-dialog-head">
      <div class="order-dialog-identity">
        <p>ORDER DETAIL</p>
        <h2>${escapeHtml(displayOrderNumber)}</h2>
        <span>${escapeHtml(order.customerName || "ゲスト注文")} 様 · ${formatDate(order.createdAt)}</span>
      </div>
      <div class="order-dialog-divider" aria-hidden="true"></div>
      <div class="order-dialog-badges">
        ${order.isExpress ? '<span class="dialog-express-badge">特急</span>' : ""}
        ${salesSourceBadge(order.salesSource)}
        ${shopifyStatusBadge(order.shopifyFulfillmentStatus)}
      </div>
      <div class="order-dialog-meta">
        <span><small>お渡し方法</small><strong>${escapeHtml(order.deliveryMethod || "—")}</strong></span>
        <span><small>都道府県</small><strong>${escapeHtml(order.shippingProvince || "—")}</strong></span>
        <span><small>処理状況</small>${workflowBadge(order.csOrderStatus)}</span>
        <span><small>決済状況</small>${paymentStatusBadge(order.financialStatus)}</span>
        <span><small>入荷次第連絡</small><strong>${escapeHtml(order.arrivalContact || "—")}</strong></span>
      </div>
      <button class="dialog-close" value="cancel" aria-label="閉じる">×</button>
    </div>
    <div class="dialog-body">
      <div class="dialog-schedule-meta">
        <span class="requested-delivery-date${order.requestedDeliveryDate ? " has-value" : ""}"><small>配送希望日</small><strong>${escapeHtml(order.requestedDeliveryDate || "—")}</strong></span>
        <span><small>着用予定日</small><strong>${escapeHtml(order.wearingDate || "—")}</strong></span>
      </div>
      <div class="dialog-summary-meta">
        <span><small>対象商品</small><strong>${order.matchedQuantity}点</strong></span>
        <span><small>注文合計</small><strong>${formatMoney(order.totalAmount, order.currencyCode)}</strong></span>
        <span><small>同期状態</small>${syncBadge(order)}</span>
      </div>
      <section class="dialog-section order-items-section unshipped-section">
        <h3>商品リスト</h3>
        ${groupedItems.unshipped.length > 0
          ? `<div class="detail-items">${orderLineItemRowsHtml(groupedItems.unshipped, "unshipped")}</div>`
          : '<p class="detail-items-empty">未出荷の商品はありません。</p>'}
      </section>
      <section class="dialog-section order-items-section shipped-section">
        <h3>出荷済みの商品</h3>
        ${groupedItems.shipped.length > 0
          ? `<div class="detail-items shipped-items">${orderLineItemRowsHtml(groupedItems.shipped, "shipped")}</div>`
          : '<p class="detail-items-empty">出荷済みの商品はありません。</p>'}
      </section>
      ${groupedItems.cancelled.length > 0
        ? `<details class="cancelled-items-section">
            <summary>
              <span>取消・返金済みの商品</span>
              <strong>${cancelledQuantity}点</strong>
              <em><span class="when-closed">表示する</span><span class="when-open">閉じる</span></em>
            </summary>
            <div class="detail-items cancelled-items">${orderLineItemRowsHtml(groupedItems.cancelled, "cancelled")}</div>
          </details>`
        : ""}
      ${orderDialogImportedFields(order).map((field) => `
        <section class="dialog-section order-note-section imported-order-detail-section">
          <h3>${escapeHtml(field.label)}</h3>
          <p>${escapeHtml(field.text)}</p>
        </section>
      `).join("")}
      <section class="dialog-section order-note-section">
        <h3>Shopify注文メモ</h3>
        <p>${order.orderNote ? escapeHtml(order.orderNote) : "注文メモはありません。"}</p>
      </section>
      <section class="dialog-section memo-section">
        <div><h3>作業メモ</h3>${allOrdersReadOnly ? "<span>日々注文リストでは閲覧のみ</span>" : ""}</div>
        <textarea id="memo-input" maxlength="5000" placeholder="対応状況、確認事項、引き継ぎなどを記録…" ${allOrdersReadOnly ? 'readonly aria-readonly="true"' : ""}>${escapeHtml(order.memo)}</textarea>
        ${order.memoSyncError ? `<p class="inline-error">${escapeHtml(order.memoSyncError)}</p>` : ""}
      </section>
    </div>
    <div class="dialog-actions">
      <a class="text-link" href="shopify://admin/orders/${escapeHtml(order.legacyId)}" target="_blank">注文詳細を開く ↗</a>
      <div>
        ${state.view === "history" && !allOrdersReadOnly ? `<button id="delete-history-order" class="button danger" type="button" ${maintenanceUnlocked() ? "" : 'disabled title="DB保守のパスコード認証後に削除できます"'}>${maintenanceUnlocked() ? "DBデータを削除" : "DB保守認証後に削除"}</button>` : ""}
        <button class="button secondary" value="cancel">キャンセル</button>
        ${allOrdersReadOnly ? "" : '<button id="save-memo" class="button primary" type="button">メモを保存</button>'}
      </div>
    </div>
  </form>`;
  dialog.addEventListener("close", () => {
    state.selectedLegacyId = null;
  }, { once: true });
  dialog.querySelector<HTMLButtonElement>("#save-memo")?.addEventListener("click", (event) => {
    void saveMemo(order, event.currentTarget as HTMLButtonElement);
  });
  dialog.querySelector<HTMLButtonElement>("#delete-history-order")?.addEventListener("click", (event) => {
    void deleteHistoryOrder(order, event.currentTarget as HTMLButtonElement);
  });
  if (!dialog.open) {
    showLockedModal(dialog);
  }
}

async function deleteHistoryOrder(order: DashboardOrder, button: HTMLButtonElement): Promise<void> {
  if (state.view !== "history" || order.recordType !== "order" || !maintenanceUnlocked()) return;
  const confirmed = window.confirm(
    `${order.name}のデータをD1から削除し、識別情報だけの「終了データ」にします。\nShopify上の注文とメタフィールドには一切触れません。よろしいですか？`,
  );
  if (!confirmed) return;
  button.disabled = true;
  button.textContent = "D1削除中…";
  try {
    const result = await api<{ orderName: string }>("/api/maintenance/orders", {
      method: "DELETE",
      headers: maintenanceHeaders(),
      body: JSON.stringify({ orderGid: order.id }),
    });
    state.selectedLegacyId = null;
    document.querySelector<HTMLDialogElement>("#order-dialog")?.close();
    state.historyPage = 1;
    notify(`${result.orderName}を終了データにしました。Shopify注文は変更していません。`);
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "D1データを削除できませんでした", true);
    button.disabled = false;
    button.textContent = "DBデータを削除";
  }
}

async function deleteSelectedHistoryOrders(): Promise<void> {
  if (state.view !== "history" || state.checkedOrderIds.size === 0 || state.isBulkUpdating) return;
  if (!maintenanceUnlocked()) {
    state.maintenanceOpen = true;
    state.maintenanceMessage = "";
    notify("一括削除にはDB保守のパスコード認証が必要です。");
    render();
    return;
  }

  const orderGids = [...state.checkedOrderIds];
  state.isBulkUpdating = true;
  render();
  try {
    const result = await api<{ deletedCount: number }>("/api/maintenance/orders", {
      method: "DELETE",
      headers: maintenanceHeaders(),
      body: JSON.stringify({ orderGids }),
    });
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
    state.historyPage = 1;
    notify(`${result.deletedCount}件を終了データにしました。Shopify注文は変更していません。`);
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "D1データを一括削除できませんでした", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function confirmSelectedDailyUnnecessaryOrders(): Promise<void> {
  if (
    state.workspace !== "all"
    || state.view !== "history"
    || state.filter !== "unnecessary"
    || state.checkedOrderIds.size === 0
    || state.isBulkUpdating
  ) return;
  const orderIds = [...state.checkedOrderIds];
  state.isBulkUpdating = true;
  render();
  try {
    const result = await api<{
      updated: number;
      partialReturned: number;
      cancelledHidden: number;
    }>("/api/orders/daily-cancellation-review", {
      method: "POST",
      body: JSON.stringify({ orderIds }),
    });
    state.checkedOrderIds.clear();
    state.historyPage = 1;
    notify(
      `${result.updated}件を確認済みに変更しました。 一部キャンセル ${result.partialReturned}件は印刷履歴、キャンセル ${result.cancelledHidden}件は非表示済・リストへ移動しました。`,
    );
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "確認済みに変更できませんでした。", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function updateSelectedStatus(status: CsOrderStatus): Promise<void> {
  if (!state.data || state.checkedOrderIds.size === 0 || state.isBulkUpdating) return;
  const orderIds = [...state.checkedOrderIds];
  state.isBulkUpdating = true;
  render();
  try {
    const result = await requestOrderStatusUpdate(orderIds, status);
    state.checkedOrderIds.clear();
    state.repairAtelierSelections.clear();
    const successMessage = status
      ? `${result.updated}件を「${status}」に更新しました`
      : `${result.updated}件の処理状況を初期化しました`;
    if (isShippingRequestStatus(status) && result.chatNotifications) {
      if (result.chatNotifications.failed > 0) {
        notify(
          `${successMessage}。Google Chatは${result.chatNotifications.sent}件送信、${result.chatNotifications.failed}件失敗しました`,
          true,
        );
      } else {
        notify(`${successMessage}。Google Chatへ${result.chatNotifications.sent}件送信しました`);
      }
    } else {
      notify(successMessage);
    }
    await loadData(false);
  } catch (error) {
    if (status === "") {
      state.resetToken = "";
      state.resetExpiresAt = "";
    }
    notify(error instanceof Error ? error.message : "処理状態を更新できませんでした", true);
  } finally {
    state.isBulkUpdating = false;
    render();
  }
}

async function saveMemo(order: DashboardOrder, button: HTMLButtonElement): Promise<void> {
  const memo = document.querySelector<HTMLTextAreaElement>("#memo-input")?.value ?? "";
  button.disabled = true;
  button.textContent = "保存中…";
  try {
    const memoOrderId = order.recordType === "after-alteration" ? order.id : order.legacyId;
    await api<{ order: DashboardOrder }>(`/api/orders/${encodeURIComponent(memoOrderId)}/memo`, {
      method: "PUT",
      body: JSON.stringify({ memo, workspace: state.workspace }),
    });
    notify(
      memo.trim()
        ? order.recordType === "after-alteration"
          || (state.workspace === "repair" && Boolean(order.caseNumberRepairA))
          ? "作業メモをD1へ保存しました"
          : "メモと注文メタフィールドを保存しました"
        : "作業メモを削除しました",
    );
    state.selectedLegacyId = null;
    document.querySelector<HTMLDialogElement>("#order-dialog")?.close();
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "メモを保存できませんでした", true);
    button.disabled = false;
    button.textContent = "メモを保存";
  }
}

function openOrderDetailsRefreshDialog(): void {
  if (state.isOrderDetailsRefreshing || state.isPageReloading) return;
  const dialog = document.querySelector<HTMLDialogElement>("#reset-auth-dialog");
  if (!dialog) return;
  dialog.innerHTML = `<form id="order-details-refresh-form" class="reset-auth-card order-details-refresh-auth-card">
    <p class="reset-auth-kicker">ORDER DETAILS REFRESH</p>
    <h2>注文詳細再更新</h2>
    <p>Shopifyから既存注文の詳細を再取得します。実行するには4桁の暗証番号を入力してください。</p>
    <label>暗証番号
      <input name="passcode" type="password" inputmode="numeric" pattern="[0-9]{4}" maxlength="4" autocomplete="off" required>
    </label>
    <p class="reset-auth-error" role="alert" hidden></p>
    <div class="bulk-confirm-actions">
      <button class="button secondary" type="button" data-order-details-refresh-cancel>キャンセル</button>
      <button class="button order-details-refresh" type="submit">再更新する</button>
    </div>
  </form>`;
  dialog.querySelector<HTMLButtonElement>("[data-order-details-refresh-cancel]")?.addEventListener(
    "click",
    () => dialog.close(),
  );
  dialog.querySelector<HTMLFormElement>("#order-details-refresh-form")?.addEventListener(
    "submit",
    (event) => {
      event.preventDefault();
      void refreshExistingOrderDetails(event.currentTarget as HTMLFormElement, dialog);
    },
  );
  showLockedModal(dialog);
  dialog.querySelector<HTMLInputElement>('input[name="passcode"]')?.focus();
}

async function refreshExistingOrderDetails(
  form: HTMLFormElement,
  dialog: HTMLDialogElement,
): Promise<void> {
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const errorElement = form.querySelector<HTMLParagraphElement>(".reset-auth-error");
  const passcodeInput = form.elements.namedItem("passcode") as HTMLInputElement | null;
  if (!submitButton || !errorElement || !passcodeInput || !form.reportValidity()) return;

  submitButton.disabled = true;
  submitButton.textContent = "認証中…";
  errorElement.hidden = true;
  try {
    const passcode = passcodeInput.value;
    passcodeInput.value = "";
    dialog.close();
    state.isOrderDetailsRefreshing = true;
    render();
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));

    const result = await api<{
      status: "completed" | "completed_with_errors";
      scanned: number;
      updated: number;
      deleted: number;
      syncErrors: number;
      refreshedLegacyInventoryLineItems: number;
    }>("/api/orders/reconcile", {
      method: "POST",
      body: JSON.stringify({ passcode }),
    });
    await loadData(false);
    const warning = result.syncErrors > 0
      ? `・在庫取得警告${result.syncErrors}件`
      : "";
    const successMessage = `Shopifyで既存注文${result.scanned}件を確認し、更新${result.updated}件・削除${result.deleted}件・旧データ再取得対象${result.refreshedLegacyInventoryLineItems}明細を処理しました${warning}。`;
    notify("注文詳細を再更新しました");
    showOperationSuccess(successMessage);
  } catch (error) {
    notify(error instanceof Error ? error.message : "注文詳細を再更新できませんでした。", true);
  } finally {
    state.isOrderDetailsRefreshing = false;
    render();
  }
}

async function reloadCurrentTab(): Promise<void> {
  if (state.isPageReloading || state.isOrderDetailsRefreshing) return;
  const currentTabLabel = filterLabel();
  state.isPageReloading = true;
  render();
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const minimumDelay = new Promise<void>((resolve) => window.setTimeout(resolve, 220));
  const [loaded] = await Promise.all([loadData(false), minimumDelay]);
  state.isPageReloading = false;
  if (!loaded) return;
  render();
  notify(`${currentTabLabel}を再読み込みしました`);
}

async function importOrders(): Promise<void> {
  if (state.isImporting) return;
  clearOperationSuccessTimer();
  state.isImporting = true;
  state.importError = "";
  state.importMessage = "";
  render();
  try {
    const result = await api<{
      imported: number;
      updated: number;
      preserved: number;
      scanned: number;
      posScanned: number;
      posMatched: number;
      posMetafieldPresent: number;
    }>("/api/orders/import", {
      method: "POST",
      body: "{}",
    });
    const successMessage = `全期間から${result.scanned}件を確認し、対象${result.imported}件（更新${result.updated}件・手動登録維持${result.preserved}件）を処理しました。POSは${result.posScanned}件中、メタフィールド値あり${result.posMetafieldPresent}件・条件一致${result.posMatched}件です。`;
    notify("最新の対象注文を再取り込みしました");
    await loadData(false);
    showOperationSuccess(successMessage);
  } catch (error) {
    clearOperationSuccessTimer();
    state.importError = error instanceof Error ? error.message : "注文を取り込めませんでした";
    state.importMessage = "";
    notify(state.importError, true);
  } finally {
    state.isImporting = false;
    render();
  }
}

async function loadComplianceRequests(): Promise<void> {
  const result = await api<{ requests: ComplianceRequestSummary[] }>(
    "/api/maintenance/compliance",
    { headers: maintenanceHeaders() },
  );
  state.complianceRequests = result.requests;
}

async function refreshComplianceRequests(): Promise<void> {
  if (state.maintenanceBusy) return;
  state.maintenanceBusy = true;
  render();
  try {
    await loadComplianceRequests();
    state.maintenanceMessage = "顧客データ開示要求を再読み込みしました。";
  } catch (error) {
    notify(error instanceof Error ? error.message : "開示要求を読み込めませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function exportComplianceRequest(requestId: string): Promise<void> {
  if (state.maintenanceBusy) return;
  state.maintenanceBusy = true;
  render();
  try {
    const result = await api<{ data: { requestReference: string } & Record<string, unknown> }>(
      `/api/maintenance/compliance?id=${encodeURIComponent(requestId)}`,
      { headers: maintenanceHeaders() },
    );
    const blob = new Blob([JSON.stringify(result.data, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const reference = (result.data.requestReference || requestId)
      .replace(/[^A-Za-z0-9._-]+/g, "-")
      .slice(0, 80);
    link.href = objectUrl;
    link.download = `shopify-customer-data-${reference || "request"}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    state.maintenanceMessage = "顧客データJSONを書き出しました。提供後に「提供済みにする」を押してください。";
  } catch (error) {
    notify(error instanceof Error ? error.message : "顧客データを書き出せませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function completeCustomerDataRequest(requestId: string): Promise<void> {
  if (state.maintenanceBusy) return;
  if (!window.confirm("書き出した顧客データをストアオーナーへ提供済みですか？\n完了すると保留中の顧客・注文IDを消去します。")) {
    return;
  }
  state.maintenanceBusy = true;
  render();
  try {
    await api<{ completed: boolean }>("/api/maintenance/compliance", {
      method: "POST",
      headers: maintenanceHeaders(),
      body: JSON.stringify({ id: requestId }),
    });
    await loadComplianceRequests();
    state.maintenanceMessage = "開示要求を提供済みにし、保留していた識別子を消去しました。";
    notify("顧客データ開示要求を完了しました");
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "開示要求を完了できませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function unlockMaintenance(form: HTMLFormElement): Promise<void> {
  if (state.maintenanceBusy) return;
  const passcode = String(new FormData(form).get("passcode") ?? "");
  state.maintenanceBusy = true;
  render();
  try {
    const result = await api<{ token: string; expiresAt: string }>("/api/maintenance/unlock", {
      method: "POST",
      body: JSON.stringify({ passcode }),
    });
    state.maintenanceToken = result.token;
    state.maintenanceExpiresAt = result.expiresAt;
    state.maintenanceMessage = "認証しました。15分間利用できます。";
    try {
      await loadComplianceRequests();
    } catch (error) {
      state.maintenanceMessage += " 顧客データ開示要求の一覧は読み込めませんでした。";
      notify(error instanceof Error ? error.message : "開示要求を読み込めませんでした", true);
    }
    notify("DB保守を認証しました");
  } catch (error) {
    state.maintenanceToken = "";
    state.maintenanceExpiresAt = "";
    state.complianceRequests = [];
    notify(error instanceof Error ? error.message : "認証できませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function searchMaintenanceOrders(query: string): Promise<void> {
  if (state.maintenanceBusy) return;
  state.maintenanceQuery = query.trim();
  state.maintenanceSelectedGid = null;
  state.maintenanceMessage = "";
  state.maintenanceBusy = true;
  render();
  try {
    const result = await api<{ candidates: MaintenanceCandidate[] }>(
      `/api/maintenance/orders?query=${encodeURIComponent(state.maintenanceQuery)}`,
      { headers: maintenanceHeaders() },
    );
    state.maintenanceResults = result.candidates;
    if (result.candidates.length === 1) {
      state.maintenanceSelectedGid = result.candidates[0]?.shopifyOrder.orderGid ?? null;
    }
  } catch (error) {
    notify(error instanceof Error ? error.message : "注文を検索できませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function saveMaintenanceOrder(form: HTMLFormElement): Promise<void> {
  if (state.maintenanceBusy || !state.maintenanceSelectedGid) return;
  const values = new FormData(form);
  const requestBody = {
    orderGid: state.maintenanceSelectedGid,
    customerName: String(values.get("customerName") ?? ""),
    csOrderStatus: String(values.get("csOrderStatus") ?? ""),
    memo: String(values.get("memo") ?? ""),
  };
  state.maintenanceBusy = true;
  render();
  try {
    const result = await api<{
      order: DashboardOrder;
      created: boolean;
      summaryWarning: string | null;
    }>("/api/maintenance/orders", {
      method: "PUT",
      headers: maintenanceHeaders(),
      body: JSON.stringify(requestBody),
    });
    const candidate = state.maintenanceResults.find(
      (entry) => entry.shopifyOrder.orderGid === result.order.id,
    );
    if (candidate) {
      candidate.existing = result.order;
      candidate.manuallyDeleted = false;
    }
    state.maintenanceMessage = result.created
      ? `${result.order.name}を条件無視でD1へ登録しました。`
      : `${result.order.name}のD1登録内容を更新しました。`;
    if (result.summaryWarning) {
      state.maintenanceMessage += ` 明細メタフィールド同期警告: ${result.summaryWarning}`;
    }
    notify(state.maintenanceMessage);
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "注文を保存できませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function deleteMaintenanceOrder(): Promise<void> {
  if (state.maintenanceBusy || !state.maintenanceSelectedGid) return;
  const candidate = state.maintenanceResults.find(
    (entry) => entry.shopifyOrder.orderGid === state.maintenanceSelectedGid,
  );
  if (!candidate?.existing) return;
  if (!window.confirm(`${candidate.existing.name}をD1から削除します。Shopify注文自体は削除されません。よろしいですか？`)) {
    return;
  }
  state.maintenanceBusy = true;
  render();
  try {
    const result = await api<{ orderName: string }>("/api/maintenance/orders", {
      method: "DELETE",
      headers: maintenanceHeaders(),
      body: JSON.stringify({ orderGid: state.maintenanceSelectedGid }),
    });
    candidate.existing = null;
    candidate.manuallyDeleted = true;
    state.maintenanceSelectedGid = null;
    state.maintenanceMessage = `${result.orderName}を終了データにしました。識別情報だけを残し、自動再取込の対象外として記録しました。`;
    if (state.view === "history") state.historyPage = 1;
    notify(state.maintenanceMessage);
    await loadData(false);
  } catch (error) {
    notify(error instanceof Error ? error.message : "D1から削除できませんでした", true);
  } finally {
    state.maintenanceBusy = false;
    render();
  }
}

async function loadData(showToast: boolean, preserveSelections = false): Promise<boolean> {
  try {
    const params = new URLSearchParams({ view: state.view, workspace: state.workspace });
    if (state.view === "history") {
      params.set("page", String(state.historyPage));
      if (state.filter === "received" || state.filter === "shipped" || state.filter === "hidden" || state.filter === "unnecessary" || state.filter === "ended") {
        params.set("filter", state.filter);
      }
    }
    state.data = await api<BootstrapData>(`/api/bootstrap?${params.toString()}`);
    state.inventoryReplenishmentCount = state.data.stats.inventoryReplenishmentCount;
    state.view = state.data.view;
    state.workspace = state.data.workspace;
    state.historyPage = state.data.pagination.page;
    if (preserveSelections) {
      const orderIds = new Set(state.data.orders.map((order) => order.id));
      for (const id of state.checkedOrderIds) if (!orderIds.has(id)) state.checkedOrderIds.delete(id);
      for (const id of state.repairAtelierSelections.keys()) if (!orderIds.has(id)) state.repairAtelierSelections.delete(id);
    } else {
      state.checkedOrderIds.clear();
      state.repairAtelierSelections.clear();
    }
    render();
    if (showToast) notify("注文リストを再読み込みしました");
    return true;
  } catch (error) {
    const root = document.querySelector<HTMLDivElement>("#app");
    if (!root) return false;
    root.innerHTML = `<div class="fatal-state"><div class="brand-mark">T</div><h1>アプリを読み込めませんでした</h1><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p><button class="button primary" id="retry-button">再試行</button></div>`;
    root.querySelector("#retry-button")?.addEventListener("click", () => void loadData(false));
    return false;
  }
}

async function start(): Promise<void> {
  try {
    if (!window.shopify) {
      throw new Error("Shopify App Bridgeを初期化できませんでした。管理画面からアプリを開き直してください。");
    }
    const loaded = await loadData(false);
    if (loaded) openDailyUnnecessaryNotice();
  } catch (error) {
    const root = document.querySelector<HTMLDivElement>("#app");
    if (root) {
      root.innerHTML = `<div class="fatal-state"><div class="brand-mark">T</div><h1>アプリを開始できませんでした</h1><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></div>`;
    }
  }
}

void start();
